/**
 * BudgetTab - Budget tracking and transaction management component
 * This is the largest component in the application
 */
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import customToast from "../../utils/customToast";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Minus,
  Search,
  X,
  Check,
  Edit3,
  Trash2,
  CreditCard,
  PiggyBank,
  TrendingUp,
  TrendingDown,
  Calendar,
  ArrowUpRight,
  ArrowDownLeft,
  ArrowLeftRight,
  RefreshCw,
  Eye,
  EyeOff,
  Filter,
  MoreHorizontal,
  ChevronDown,
  ChevronUp,
  Info,
  HelpCircle,
  Sparkles,
  Clock,
  CheckCircle,
  Building2,
  Wallet,
  Receipt,
  Settings,
  BarChart3,
  AlertCircle,
  ArrowDown,
  ArrowRightLeft,
  Banknote,
  Calculator,
  CalendarDays,
  FileText,
  PieChart,
  Tag,
  Target,
  Bell,
  ShoppingCart,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Progress } from "../ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { formatCurrency, formatCurrencyWhole, formatCurrencyShort, getCurrentCurrency } from "../../utils/formatters";
import { getMonthKey, getDaysInMonth, getFirstDayOfMonth } from "../../utils/dateHelpers";
import { parseCategoryEmoji, getCategoryDisplayName } from "../../utils/categoryEmoji";
import { getAccentColors, cardColorMap, defaultCategories, getThemeColors, fireConfetti } from "../../utils/themes";
import { THEME_COLORS } from "../../utils/constants";
import TransactionRow from "../common/TransactionRow";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { PremiumFeature } from "../premium/PremiumComponents";
import BudgetHero from "../budget/BudgetHero";
import MonthlyBudgetSetup, { BudgetEditPopup } from "../budget/MonthlyBudgetSetup";
import MonthEndReviewList from "../review/MonthEndReviewList";
import { YearReviewRow } from "../review/YearReview"; // w2_18
import FeelingPicker from "../review/FeelingPicker";
import { FEELINGS } from "../../utils/monthEndReview";
import { computeBudgetSpentPercent } from "../../utils/budgetMath";
import { enumerateOccurrences } from "../../utils/recurringMath";
import { usePurple } from "../../utils/purple";
import { usePlan } from "../../utils/plan"; // plan_07
import PlanPath from "../common/PlanPath"; // plan_12
import SiriHowTo, { SiriLink } from "../siri/SiriHowTo"; // siri_08

// Chart palettes - soft "card family" tones per theme. Each hue has a mid and a
// light shade; `text` maps every fill to a darker shade of the same hue so the
// percentages stay readable. Used by the month breakdown and 2026 Stats donuts.
const CHART_PALETTES = {
  // plan_07: Money plan - soft pastels, ink percentages.
  plan: {
    cats: ['#F2AFC8', '#A9C6F0', '#B9A6F0', '#9ED9B6', '#F3DE8E', '#F7CCDC', '#CADBF6', '#D5C9F6', '#C5E9D3', '#F8EAB8'],
    income: { expenses: '#F2AFC8', savings: '#B9A6F0', debt: '#A9C6F0', unallocated: '#9ED9B6' },
    text: {
      '#F2AFC8': '#26252E', '#A9C6F0': '#26252E', '#B9A6F0': '#26252E', '#9ED9B6': '#26252E', '#F3DE8E': '#26252E',
      '#F7CCDC': '#26252E', '#CADBF6': '#26252E', '#D5C9F6': '#26252E', '#C5E9D3': '#26252E', '#F8EAB8': '#26252E',
    },
    centre: '#8B879A',
    toggle: { track: '#ECE8F4', active: '#26252E', inactive: '#6C6879' },
  },
  // Purple (admin-only): shades of lavender and purple only.
  purple: {
    cats: ['#8F70E4', '#AC8DF2', '#C3AEF4', '#D8C9F7', '#E9E0FA', '#9E82E8', '#B79EF0', '#CBB9F5', '#DDD1F8', '#F0EAFC'],
    income: { expenses: '#AC8DF2', savings: '#8F70E4', debt: '#D8C9F7', unallocated: '#E9E0FA' },
    text: {
      '#8F70E4': '#6E4FC6', '#AC8DF2': '#6E4FC6', '#C3AEF4': '#7B5AA6', '#D8C9F7': '#7B5AA6', '#E9E0FA': '#7B5AA6',
      '#9E82E8': '#6E4FC6', '#B79EF0': '#6E4FC6', '#CBB9F5': '#7B5AA6', '#DDD1F8': '#7B5AA6', '#F0EAFC': '#7B5AA6',
    },
    centre: '#6E4FC6',
    toggle: { track: '#F3ECFB', active: '#6E4FC6', inactive: '#9A8FB0' },
  },
  pink: {
    cats: ['#E48AAF', '#F2AE8E', '#A994DE', '#9CC48E', '#8EAEE3', '#F3C0D5', '#F8D2BF', '#D2C6F0', '#C9E0BF', '#C3D5F3'],
    income: { expenses: '#E48AAF', savings: '#9CC48E', debt: '#F2AE8E', unallocated: '#A994DE' },
    text: {
      '#E48AAF': '#B84A7A', '#F3C0D5': '#B84A7A', '#F2AE8E': '#B8683F', '#F8D2BF': '#B8683F',
      '#A994DE': '#6A58A6', '#D2C6F0': '#6A58A6', '#9CC48E': '#56744A', '#C9E0BF': '#56744A',
      '#8EAEE3': '#3E5A8A', '#C3D5F3': '#3E5A8A',
    },
    centre: '#C06A92',
    toggle: { track: '#F4E6EE', active: '#B84A7A', inactive: '#A88A9A' },
  },

  warm: {
    cats: ['#D39B8C', '#DDB27E', '#A3BE92', '#9DB5A6', '#B9A0B6', '#EBCBC1', '#F0D8B8', '#CFDEC3', '#CADBD0', '#DCCCDA'],
    income: { expenses: '#D39B8C', savings: '#A3BE92', debt: '#DDB27E', unallocated: '#B9A0B6' },
    text: {
      '#D39B8C': '#8E4F42', '#EBCBC1': '#8E4F42', '#DDB27E': '#8A5E24', '#F0D8B8': '#8A5E24',
      '#A3BE92': '#3F6A45', '#CFDEC3': '#3F6A45', '#9DB5A6': '#2D5A3D', '#CADBD0': '#2D5A3D',
      '#B9A0B6': '#6E4F6A', '#DCCCDA': '#6E4F6A',
    },
    centre: '#2D5A3D',
    toggle: { track: '#F3E9DC', active: '#2D5A3D', inactive: '#A8998A' },
  },

  blue: {
    cats: ['#8EA6EE', '#AE9BE6', '#86C1E6', '#8FCDB8', '#EBA3B8', '#C6D3F7', '#D6CCF3', '#C2E1F4', '#C6E6DA', '#F5CFDA'],
    income: { expenses: '#8EA6EE', savings: '#8FCDB8', debt: '#EBA3B8', unallocated: '#AE9BE6' },
    text: {
      '#8EA6EE': '#3A4DA8', '#C6D3F7': '#3A4DA8', '#AE9BE6': '#6B4FB0', '#D6CCF3': '#6B4FB0',
      '#86C1E6': '#2E6E96', '#C2E1F4': '#2E6E96', '#8FCDB8': '#2F7560', '#C6E6DA': '#2F7560',
      '#EBA3B8': '#A8456A', '#F5CFDA': '#A8456A',
    },
    centre: '#5B6BC4',
    toggle: { track: '#E9EDFB', active: '#3A4DA8', inactive: '#8F97B8' },
  }
};
const chartTextColor = (pal, fill) => pal.text[fill] || fill;
// One conic-gradient stop, with a thin white gap at the end of each slice
// (skipped when there is only one slice, so a full ring has no notch).
const chartStop = (color, start, pct, gapped) => {
  const end = start + pct;
  if (!gapped) return `${color} ${start}% ${end}%`;
  const gap = Math.min(0.6, pct / 3);
  return `${color} ${start}% ${end - gap}%, #ffffff ${end - gap}% ${end}%`;
};

const BudgetTab = ({ transactions = [], budgetSettings = {}, categoryBudgets = {}, saveUserData, userCards = [], userDebts = [], theme = 'pink', openAddMenu = false, onOpenAddMenuConsumed, noSpendDays = {}, userData = null }) => {
  // plan_07: Money plan (admin-only) restyles this tab.
  const plan = usePlan();
  const [showSiriHelp, setShowSiriHelp] = useState(false); // siri_08
  const { getLimit, showFeaturePaywall, hasAccess } = useSubscription();
  const cardsLimit = getLimit('cards');
  const sinkingFundsLimit = getLimit('sinkingFunds');
  const investmentAccountsLimit = getLimit('investmentAccounts');

  // Get available (non-locked) funds and investments based on subscription
  const allSinkingFunds = budgetSettings.sinking_funds || [];
  const availableSinkingFunds = allSinkingFunds.slice(0, sinkingFundsLimit);
  const allInvestmentAccounts = budgetSettings.investment_accounts || [];
  const availableInvestmentAccounts = allInvestmentAccounts.slice(0, investmentAccountsLimit);

  const [viewMonth, setViewMonth] = useState(new Date());
  const [newTransaction, setNewTransaction] = useState({ amount: '', category: '', description: '', paymentMethod: '', feeling: '' });
  const [newIncome, setNewIncome] = useState({ amount: '', description: '', isInterest: false, interestAccount: '', interestAccountType: '', toCard: '' });
  const [newSavings, setNewSavings] = useState({ amount: '', description: '', accountId: '', accountType: '', fromCard: '' });
  const [newWithdrawal, setNewWithdrawal] = useState({ amount: '', description: '', accountId: '', accountType: '', toCard: '' });
  const [newDebtPayment, setNewDebtPayment] = useState({ amount: '', description: '', debtId: '', paymentMethod: '' });
  const [newTransfer, setNewTransfer] = useState({ amount: '', fromCard: '', toCard: '', description: '' });
  // Date picker for new transactions — defaults to today, resets when the add menu closes
  const [transactionDate, setTransactionDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [localBudgetAmount, setLocalBudgetAmount] = useState('');
  const [budgetInputFocused, setBudgetInputFocused] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showCategoryBudgets, setShowCategoryBudgets] = useState(false);
  const [budgetViewType, setBudgetViewType] = useState('spending'); // 'spending' or 'savings'
  const [editingCategoryBudget, setEditingCategoryBudget] = useState(null);
  const [categoryBudgetValue, setCategoryBudgetValue] = useState('');
  const [editingSavingsBudget, setEditingSavingsBudget] = useState(null);
  const [savingsBudgetValue, setSavingsBudgetValue] = useState('');
  const [editingDebtBudget, setEditingDebtBudget] = useState(null);
  const [debtBudgetValue, setDebtBudgetValue] = useState('');
  const [editingTransaction, setEditingTransaction] = useState(null);
  // Drives the edit-transaction bottom sheet. Kept separate from
  // editingTransaction so that TransactionRow does not switch to its
  // inline-edit mode (it keys off editingTransaction).
  const [editSheetTxId, setEditSheetTxId] = useState(null);
  // Holds a pending edit-transaction save that's waiting on the user's
  // confirmation to move the transaction to a different month.
  const [pendingMonthMoveEdit, setPendingMonthMoveEdit] = useState(null);
  // Controls the "set up your budget for this month?" nudge popup.
  const [editTransactionData, setEditTransactionData] = useState({ 
    amount: '', 
    date: '',
    category: '', 
    description: '', 
    paymentMethod: '',
    // Additional fields for income/savings transactions
    transactionType: '', // 'expense', 'income', 'savings', 'interest'
    accountId: '',
    accountType: '',
    to_card: '',
    from_card: ''
  });
  const [editCategorySearch, setEditCategorySearch] = useState('');
  const [showEditCategoryDropdown, setShowEditCategoryDropdown] = useState(false);
  const [showYearlyStats, setShowYearlyStats] = useState(false);
  const [showBudgetInfo, setShowBudgetInfo] = useState(false);
  const [yearlyStatsView, setYearlyStatsView] = useState('totals'); // 'totals' or 'breakdown'
  const [monthlyChartView, setMonthlyChartView] = useState('income'); // 'income' or 'expenses'
  const [showAllMonthlyCategories, setShowAllMonthlyCategories] = useState(false);
  const [showAllYearlyCategories, setShowAllYearlyCategories] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(() => {
    if (openAddMenu === true) {
      if (onOpenAddMenuConsumed) onOpenAddMenuConsumed();
      return true;
    }
    if (localStorage.getItem('widget_budget_action') === 'addTransaction') {
      localStorage.removeItem('widget_budget_action');
      return true;
    }
    return false;
  });

  // Listen for widget deep link event (fires when Dashboard is already mounted)
  useEffect(() => {
    const handler = () => setShowAddMenu(true);
    window.addEventListener('widget_add_transaction', handler);
    return () => window.removeEventListener('widget_add_transaction', handler);
  }, []);

  const [addFormType, setAddFormType] = useState(null);

  // Set the Add Transaction date based on which month is being viewed:
  //   - Current month -> today
  //   - Past month    -> last day of that month
  //   - Future month  -> first day of that month
  // Runs when the sheet opens or when the user navigates to a different
  // month while it's open.
  useEffect(() => {
    if (!showAddMenu && !addFormType) return;

    const now = new Date();
    const vY = viewMonth.getFullYear();
    const vM = viewMonth.getMonth();
    const cY = now.getFullYear();
    const cM = now.getMonth();

    let target;
    if (vY === cY && vM === cM) {
      target = now; // current month -> today
    } else if (vY < cY || (vY === cY && vM < cM)) {
      // past month -> last day of that month
      target = new Date(vY, vM + 1, 0);
    } else {
      // future month -> first day of that month
      target = new Date(vY, vM, 1);
    }

    setTransactionDate(
      `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, '0')}-${String(target.getDate()).padStart(2, '0')}`
    );
  }, [showAddMenu, addFormType, viewMonth]);
  const [showBudgetSetup, setShowBudgetSetup] = useState(false);
  // Budget editing from the This Month rows. Same popup, same storage keys as
  // MonthlyBudgetSetup - this is a second door onto the same data, not a copy.
  const [budgetPopup, setBudgetPopup] = useState(null);
  // Which slice of MonthlyBudgetSetup is open in a sheet:
  // null | 'amount' | 'balances' | 'copy'
  const [moneySheet, setMoneySheet] = useState(null);
  // The month-start checklist, reachable all month from the row at the foot.
  const [showMonthSetup, setShowMonthSetup] = useState(false);
  const [confirmClearMonth, setConfirmClearMonth] = useState(false);
  // True while a slice sheet was reached from the Set up <month> menu, so the
  // sheet can offer a way back to it instead of only a way out.
  const [setupReturn, setSetupReturn] = useState(false);
  // Picker for categories that have no budget and no spending yet.
  const [pickCategory, setPickCategory] = useState(false);
  const openFromSetup = (which) => { setSetupReturn(true); setShowMonthSetup(false); setMoneySheet(which); };
  const closeSheet = () => { setMoneySheet(null); setSetupReturn(false); };
  const backToSetup = () => { setMoneySheet(null); setSetupReturn(false); setShowMonthSetup(true); };
  const [transactionSearch, setTransactionSearch] = useState('');
  const [showTransactionSearch, setShowTransactionSearch] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [viewingTransaction, setViewingTransaction] = useState(null); // Transaction detail view
  const [viewingYearlyTransactions, setViewingYearlyTransactions] = useState(null); // 'income', 'spent', 'saved', 'debt'
  const [viewingCategoryTransactions, setViewingCategoryTransactions] = useState(null); // category name
  const [viewingCardTransactions, setViewingCardTransactions] = useState(null); // card name
  const [viewingMonthCardTransactions, setViewingMonthCardTransactions] = useState(null); // card name for month view

  // Lock body scroll when a sheet is open (Setup/Stats are inline now, not sheets — excluded)
  useEffect(() => {
    if (addFormType || showAddMenu) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [addFormType, showAddMenu]);

  // Tour: notify the TourController when the Add sheet opens or closes so
  // the coach-mark on the FAB can get out of the way while the user fills it.
  useEffect(() => {
    const open = !!(showAddMenu || addFormType);
    try {
      window.dispatchEvent(new CustomEvent('worthly:budget-sheet-state', { detail: { open } }));
    } catch (e) {}
  }, [showAddMenu, addFormType]);
  // True while the tutorial runs. The Set up <month> pill hides then, so the
  // tutorial's + button step has the corner to itself.
  const [tourRunning, setTourRunning] = useState(
    typeof window !== 'undefined' && !!window.__worthlyTourActive
  );
  useEffect(() => {
    const h = (e) => setTourRunning(!!e?.detail?.active);
    window.addEventListener('worthly:tour-active-change', h);
    return () => window.removeEventListener('worthly:tour-active-change', h);
  }, []);
  const [transactionViewMode, setTransactionViewMode] = useState('posted'); // 'posted' or 'scheduled'
  const [transactionCardFilter, setTransactionCardFilter] = useState('all'); // 'all' or card name
  const [viewingMonthCategoryTransactions, setViewingMonthCategoryTransactions] = useState(null); // category name for month view
  const [viewingMonthSavingsTransactions, setViewingMonthSavingsTransactions] = useState(null); // fund/investment id for month view
  const [pendingRecurring, setPendingRecurring] = useState([]); // Due recurring transactions
  const [showRecurringPrompt, setShowRecurringPrompt] = useState(false);
  // Collapsible sections — closed by default so the tab reads cleaner;
  // Transactions/day-groups stay open since that's the core content.
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const [showMonthlyBreakdown, setShowMonthlyBreakdown] = useState(false);
  const [dayGroupOverrides, setDayGroupOverrides] = useState({}); // dateKey -> true/false, overrides the "only most-recent day open" default
  const [showLeftoverAllocation, setShowLeftoverAllocation] = useState(false);
  const [leftoverAllocations, setLeftoverAllocations] = useState({});
  const [showInterestDetails, setShowInterestDetails] = useState(false);
  const [showBudgetProgress, setShowBudgetProgress] = useState(false); // Your Budgets card - closed until you need it
  const [budgetProgressView, setBudgetProgressView] = useState('spending'); // 'spending', 'savings', 'debts'

  // Helper to get account name from ID
  const getAccountName = (accountId, accountType) => {
    if (accountType === 'fund') {
      const fund = (budgetSettings.sinking_funds || []).find(f => f.id === accountId);
      return fund ? fund.name : 'Unknown Fund';
    } else if (accountType === 'investment') {
      const inv = (budgetSettings.investment_accounts || []).find(i => i.id === accountId);
      return inv ? inv.name : 'Unknown Account';
    }
    return 'Unknown';
  };

  // Get debt name by ID
  const getDebtName = (debtId) => {
    const debt = (userDebts || []).find(d => d.id === debtId);
    return debt ? debt.name : 'Unknown Debt';
  };

  // Get theme-aware accent colors
  const accentColors = getAccentColors(theme);

  // Process user's cards - handle both old string format and new object format
  // Apply subscription limit - free users only see first N cards
  const processedCards = (userCards || []).map((card, idx) => {
    if (typeof card === 'string') {
      return { name: card, color: 'grey', type: 'debit' };
    }
    return { name: card.name || `Card ${idx + 1}`, color: card.color || 'grey', type: card.type || 'debit' };
  });
  const allCards = processedCards.length > 0 ? processedCards : [
    { name: 'Card 1', color: 'grey', type: 'debit' },
    { name: 'Card 2', color: 'grey', type: 'debit' }
  ];
  // Limit cards based on subscription
  const cards = allCards.slice(0, cardsLimit);
  const lockedCardsCount = Math.max(0, allCards.length - cardsLimit);
  // Cash is a synthetic payment method that doesn't count toward the card
  // limit. It's appended to cards-derived arrays so it appears in dropdowns,
  // balance summaries, and lookups — but the actual `cards` array stays
  // untouched (limit logic, premium gating, locked card display).
  const cashEnabled = !!budgetSettings.cash_enabled;
  const cashColor = budgetSettings.cash_color || 'green';
  const paymentMethods = cashEnabled
    ? [...cards, { name: 'Cash', color: cashColor, type: 'cash', isCash: true }]
    : cards;

  // Color mapping from color name to Tailwind classes
  // softer card colours (patch 03): same seven names, softer shades. bg = dot, bgLight = chip, text = name.
  // plan_24: each card's colour as a soft pastel - the dot, and the square behind 💳
  const PLAN_CARD_COLOR = (bg, bgLight) => ({ bg, text: 'text-[#26252E]', bgLight, border: 'border-[#EEEAF6]' });
  const colorMap = plan ? {
    grey: PLAN_CARD_COLOR('bg-[#C9CAD3]', 'bg-[#ECEBF1]'), red: PLAN_CARD_COLOR('bg-[#F0A9A3]', 'bg-[#FBE3E1]'), blue: PLAN_CARD_COLOR('bg-[#A9C6F0]', 'bg-[#DFE9F9]'),
    orange: PLAN_CARD_COLOR('bg-[#F4C3A0]', 'bg-[#FCEADC]'), green: PLAN_CARD_COLOR('bg-[#9ED9B6]', 'bg-[#DFF2E7]'), pink: PLAN_CARD_COLOR('bg-[#F2AFC8]', 'bg-[#FAE4ED]'),
    purple: PLAN_CARD_COLOR('bg-[#B9A6F0]', 'bg-[#ECE6FA]'),
  } : {
    grey: { bg: 'bg-[#9FA2AC]', text: 'text-[#6B6B78]', bgLight: 'bg-[#E4E3E9]', border: 'border-[#E4E3E9]' },
    red: { bg: 'bg-[#E6827D]', text: 'text-[#C4443F]', bgLight: 'bg-[#F6C8C4]', border: 'border-[#F6C8C4]' },
    blue: { bg: 'bg-[#7C9FE6]', text: 'text-[#3F63C4]', bgLight: 'bg-[#C9D8F5]', border: 'border-[#C9D8F5]' },
    orange: { bg: 'bg-[#F0A06C]', text: 'text-[#B8683F]', bgLight: 'bg-[#FAD4B8]', border: 'border-[#FAD4B8]' },
    green: { bg: 'bg-[#6EC08F]', text: 'text-[#3F8A5F]', bgLight: 'bg-[#C8E8D3]', border: 'border-[#C8E8D3]' },
    pink: { bg: 'bg-[#E27FAB]', text: 'text-[#C23A72]', bgLight: 'bg-[#F7CCDE]', border: 'border-[#F7CCDE]' },
    purple: { bg: 'bg-[#A488E0]', text: 'text-[#7A5FB0]', bgLight: 'bg-[#DDD1F2]', border: 'border-[#DDD1F2]' }
  };

  // Helper to get card color classes by card name (or cash)
  const getCardColors = (cardName) => {
    const card = paymentMethods.find(c => c.name === cardName);
    const color = card?.color || 'grey';
    return colorMap[color] || colorMap.grey;
  };

  const viewMonthKey = `${viewMonth.getFullYear()}-${String(viewMonth.getMonth() + 1).padStart(2, '0')}`;
  const currentDate = new Date();
  const currentMonthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
  const isCurrentMonth = viewMonthKey === currentMonthKey;

  // Check for due recurring transactions
  useEffect(() => {
    const recurringTransactions = budgetSettings?.recurring_transactions || [];
    if (recurringTransactions.length === 0 || !isCurrentMonth) return;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dueItems = recurringTransactions.filter(rec => {
      const lastAdded = rec.last_added ? new Date(rec.last_added) : null;
      if (lastAdded) lastAdded.setHours(0, 0, 0, 0);
      
      // All frequencies now use start_date
      const startDate = rec.start_date ? new Date(rec.start_date + 'T00:00:00') : null;
      if (!startDate) return false;
      startDate.setHours(0, 0, 0, 0);
      
      // If today is before start date, not due yet
      if (today < startDate) return false;
      
      // Calculate interval in days
      let intervalDays;
      if (rec.frequency === 'weekly') {
        intervalDays = 7;
      } else if (rec.frequency === 'biweekly') {
        intervalDays = 14;
      } else if (rec.frequency === 'every_n_weeks') {
        intervalDays = (rec.every_n_weeks || 2) * 7;
      } else if (rec.frequency === 'monthly') {
        // For monthly, calculate if today is on or past a monthly occurrence
        const startDay = startDate.getDate();
        const monthsDiff = (today.getFullYear() - startDate.getFullYear()) * 12 + (today.getMonth() - startDate.getMonth());
        
        // Find the next due date from start
        let nextDue = new Date(startDate);
        nextDue.setMonth(nextDue.getMonth() + monthsDiff);
        if (nextDue > today) {
          nextDue.setMonth(nextDue.getMonth() - 1);
        }
        
        // Check if already added for this occurrence
        if (lastAdded && lastAdded >= nextDue) return false;
        
        // Due if today is on or after the next due date
        return today >= nextDue;
      } else {
        return false;
      }
      
      // For weekly frequencies, calculate days since start
      const daysSinceStart = Math.floor((today - startDate) / (1000 * 60 * 60 * 24));
      
      // Find the most recent due date
      const occurrencesSinceStart = Math.floor(daysSinceStart / intervalDays);
      const mostRecentDueDate = new Date(startDate);
      mostRecentDueDate.setDate(mostRecentDueDate.getDate() + (occurrencesSinceStart * intervalDays));
      
      // Check if already added for this occurrence
      if (lastAdded && lastAdded >= mostRecentDueDate) return false;
      
      // Due if today is on or after the most recent due date
      return today >= mostRecentDueDate;
    });
    
    if (dueItems.length > 0) {
      setPendingRecurring(dueItems);
      // Don't auto-show popup - let user click "Review" from the banner
    }
  }, [budgetSettings?.recurring_transactions, isCurrentMonth]);

  const addRecurringTransaction = (rec) => {
    // When "Add Now" is clicked, use TODAY's date so it appears in Posted under today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Use today's date for the transaction, NOT the scheduled date
    const transactionDate = today;
    const monthKey = `${transactionDate.getFullYear()}-${String(transactionDate.getMonth() + 1).padStart(2, '0')}`;
    
    let transaction;
    if (rec.type === 'income') {
      transaction = {
        id: Date.now(),
        amount: parseFloat(rec.amount),
        category: 'Income',
        description: rec.description,
        payment_method: '',
        to_card: rec.payment_card || null,
        month_key: monthKey,
        date: transactionDate.toISOString(),
        completed: false,
        is_income: true,
        is_recurring: true,
        recurring_id: rec.id
      };
    } else if (rec.type === 'savings') {
      transaction = {
        id: Date.now(),
        amount: parseFloat(rec.amount),
        category: 'Savings',
        description: rec.description,
        payment_method: rec.payment_card || 'Savings',
        month_key: monthKey,
        date: transactionDate.toISOString(),
        completed: false,
        is_income: false,
        is_savings: true,
        account_id: rec.account_id,
        account_type: rec.account_type,
        transaction_type: 'deposit',
        from_card: rec.payment_card || null,
        is_recurring: true,
        recurring_id: rec.id
      };
    } else if (rec.type === 'debt') {
      transaction = {
        id: Date.now(),
        amount: parseFloat(rec.amount),
        category: 'Debt Payment',
        description: rec.description,
        payment_method: rec.payment_card || '',
        month_key: monthKey,
        date: transactionDate.toISOString(),
        completed: false,
        is_income: false,
        is_debt_payment: true,
        debt_id: rec.debt_id,
        is_recurring: true,
        recurring_id: rec.id
      };
    } else {
      transaction = {
        id: Date.now(),
        amount: parseFloat(rec.amount),
        category: rec.category || 'Other',
        description: rec.description,
        payment_method: rec.payment_card || '',
        month_key: monthKey,
        date: transactionDate.toISOString(),
        completed: false,
        is_income: false,
        is_recurring: true,
        recurring_id: rec.id
      };
    }
    
    // Update the recurring item's last_added date to today
    const updatedRecurring = (budgetSettings?.recurring_transactions || []).map(r => 
      r.id === rec.id ? { ...r, last_added: transactionDate.toISOString() } : r
    );
    
    saveUserData({ 
      transactions: [transaction, ...transactions],
      recurring_transactions: updatedRecurring
    });
    
    // Remove from pending
    setPendingRecurring(prev => prev.filter(p => p.id !== rec.id));
    if (pendingRecurring.length <= 1) {
      setShowRecurringPrompt(false);
    }
    
  };

  const skipRecurringTransaction = (rec) => {
    // Mark as added this month without actually adding
    const today = new Date();
    const updatedRecurring = (budgetSettings?.recurring_transactions || []).map(r => 
      r.id === rec.id ? { ...r, last_added: today.toISOString() } : r
    );
    saveUserData({ 
      recurring_transactions: updatedRecurring
    });
    
    setPendingRecurring(prev => prev.filter(p => p.id !== rec.id));
    if (pendingRecurring.length <= 1) {
      setShowRecurringPrompt(false);
    }
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const viewMonthTransactions = transactions.filter(t => t.month_key === viewMonthKey);
  const expenseTransactions = viewMonthTransactions.filter(t => !t.is_income && !t.is_savings && !t.is_transfer);
  const incomeTransactions = viewMonthTransactions.filter(t => t.is_income && !t.is_transfer);
  const savingsTransactions = viewMonthTransactions.filter(t => t.is_savings);
  const fromSavingsTransactions = viewMonthTransactions.filter(t => t.is_from_savings);
  const totalSpent = expenseTransactions.reduce((sum, t) => sum + t.amount, 0);
  // Total income for display (includes interest)
  const totalIncome = incomeTransactions.filter(t => !t.is_from_savings).reduce((sum, t) => sum + t.amount, 0);
  // Total income for month (excluding from savings, excluding interest) - for donut chart
  const totalIncomeForMonth = incomeTransactions.filter(t => !t.is_from_savings && !t.is_interest).reduce((sum, t) => sum + t.amount, 0);
  // Income for cash available calculation (excludes interest - it goes directly to savings/investment accounts)
  const incomeForCashAvailable = incomeTransactions.filter(t => !t.is_from_savings && !t.is_interest).reduce((sum, t) => sum + t.amount, 0);
  const totalFromSavings = fromSavingsTransactions.reduce((sum, t) => sum + t.amount, 0);
  // Separate deposits from withdrawals in savings transactions
  const totalSavingsDeposits = savingsTransactions
    .filter(t => t.transaction_type !== 'withdrawal')
    .reduce((sum, t) => sum + t.amount, 0);
  const totalSavingsWithdrawals = savingsTransactions
    .filter(t => t.transaction_type === 'withdrawal')
    .reduce((sum, t) => sum + t.amount, 0);
  const totalSavings = totalSavingsDeposits - totalSavingsWithdrawals - totalFromSavings;
  // "Where your income went" summary (collapsed month breakdown card). Uses exactly the
  // same four parts and rounding as the Income donut inside the card, so they always agree:
  // spent (expenses excluding debt), saved (savings deposits), debt payments, and what's left.
  const monthDebtTotal = viewMonthTransactions
    .filter(t => t.is_debt_payment)
    .reduce((sum, t) => sum + t.amount, 0);
  const monthPct = (value) => totalIncomeForMonth > 0 ? Number(((value / totalIncomeForMonth) * 100).toFixed(0)) : 0;
  const monthSpentPct = monthPct(totalSpent - monthDebtTotal);
  const monthSavedPct = monthPct(totalSavingsDeposits);
  const monthDebtPct = monthPct(monthDebtTotal);
  const monthUnallocatedPct = monthPct(Math.max(0, totalIncomeForMonth - totalSpent - totalSavingsDeposits));
  // Monthly interest earned
  const interestTransactions = viewMonthTransactions.filter(t => t.is_interest);
  const totalInterestEarned = interestTransactions.reduce((sum, t) => sum + t.amount, 0);
  const leftoverFromLastMonth = budgetSettings.leftover_by_month?.[viewMonthKey] || 0;
  const monthlyBudget = budgetSettings.budget_by_month?.[viewMonthKey] || 0;

  // Per-month category budgets
  const monthlyCategoryBudgets = categoryBudgets[viewMonthKey] || {};

  // Per-month savings/debt budgets  
  const monthlySavingsBudgets = (budgetSettings.savings_budgets_by_month || {})[viewMonthKey] || {};
  const monthlyDebtBudgets = (budgetSettings.debt_budgets_by_month || {})[viewMonthKey] || {};
  const moneyAvailable = leftoverFromLastMonth + incomeForCashAvailable + totalFromSavings + totalSavingsWithdrawals - totalSpent - totalSavingsDeposits;
  // Cash Available = Leftover + Income (excluding interest) + Withdrawals from Savings - Spent - Deposits to Savings
  // Note: Credit card spending is included in "Spent" so it reduces cash available (you owe that money)
  // Credit card payments (transfers) are NOT subtracted again to avoid double-counting
  const cashAvailable = (leftoverFromLastMonth + incomeForCashAvailable + totalSavingsWithdrawals) - totalSpent - totalSavingsDeposits;
  const budgetRemaining = monthlyBudget - totalSpent;

  // Calculate yearly stats
  const currentYear = viewMonth.getFullYear();
  const isInYear = (t) => {
    if (t.month_key) return t.month_key.startsWith(`${currentYear}-`);
    if (t.date) return new Date(t.date).getFullYear() === currentYear;
    return false;
  };
  const yearlyExpenseTransactions = transactions.filter(t => isInYear(t) && !t.is_income && !t.is_savings && !t.is_transfer);
  const yearlyIncomeTransactions = transactions.filter(t => isInYear(t) && t.is_income && !t.is_from_savings && !t.is_transfer);
  const yearlySavingsAll = transactions.filter(t => isInYear(t) && t.is_savings && !t.is_from_savings);
  const yearlySavingsDeposits = yearlySavingsAll.filter(t => t.transaction_type !== 'withdrawal');
  const yearlySavingsWithdrawals = yearlySavingsAll.filter(t => t.transaction_type === 'withdrawal');
  const yearlyDebtPayments = transactions.filter(t => isInYear(t) && t.is_debt_payment);
  const yearlyTotalSpent = yearlyExpenseTransactions.reduce((sum, t) => sum + t.amount, 0);
  // Yearly income for display (includes interest)
  const yearlyTotalIncome = yearlyIncomeTransactions.reduce((sum, t) => sum + t.amount, 0);
  const yearlyTotalSavingsDepositSum = yearlySavingsDeposits.reduce((sum, t) => sum + t.amount, 0);
  const yearlyTotalSavingsWithdrawalSum = yearlySavingsWithdrawals.reduce((sum, t) => sum + t.amount, 0);
  const yearlyTotalSavings = yearlyTotalSavingsDepositSum - yearlyTotalSavingsWithdrawalSum;
  const yearlyTotalDebtPayments = yearlyDebtPayments.reduce((sum, t) => sum + t.amount, 0);
  // Yearly interest earned
  const yearlyInterestTransactions = transactions.filter(t => isInYear(t) && t.is_interest);
  const yearlyTotalInterest = yearlyInterestTransactions.reduce((sum, t) => sum + t.amount, 0);

  const yearlySpendingByCategory = {};
  yearlyExpenseTransactions.forEach(t => {
    yearlySpendingByCategory[t.category] = (yearlySpendingByCategory[t.category] || 0) + t.amount;
  });

  // Calculate yearly spending by card (dynamic) — includes cash if enabled
  const yearlyCardTotals = paymentMethods.map(card => ({
    card: card.name,
    color: card.color,
    total: yearlyExpenseTransactions.filter(t => t.payment_method === card.name).reduce((sum, t) => sum + t.amount, 0)
  }));

  // A card's balance at the end of any month, from that month's transactions.
  // Same arithmetic as cardTotals below, just not pinned to the viewed month,
  // so it answers "what does this card hold right now" for a month still in
  // progress as readily as for one that has closed. Nothing waits for a month
  // to end: page to October in mid-September and this returns September's
  // balance as it stands today.
  const cardBalanceAtEndOf = (monthKey, card) => {
    const tx = transactions.filter(t => t.month_key === monthKey);
    const spent = tx
      .filter(t => !t.is_income && !t.is_savings && !t.is_transfer && t.payment_method === card.name)
      .reduce((sum, t) => sum + t.amount, 0);

    if (card.type === 'credit') {
      const paidOff = tx
        .filter(t => t.is_transfer && t.to_card === card.name && t.is_credit_payment)
        .reduce((sum, t) => sum + t.amount, 0);
      return spent - paidOff;
    }

    const startedWith = budgetSettings.leftover_allocations_by_month?.[monthKey]?.[card.name] || 0;
    const incomeIn = tx.filter(t => t.is_income && !t.is_transfer && t.to_card === card.name)
      .reduce((sum, t) => sum + t.amount, 0);
    const transfersIn = tx.filter(t => t.is_transfer && t.to_card === card.name && !t.is_credit_payment)
      .reduce((sum, t) => sum + t.amount, 0);
    const transfersOut = tx.filter(t => t.is_transfer && t.payment_method === card.name)
      .reduce((sum, t) => sum + t.amount, 0);
    const savedFrom = tx.filter(t => t.is_savings && t.transaction_type === 'deposit' && t.from_card === card.name)
      .reduce((sum, t) => sum + t.amount, 0);
    const withdrawnTo = tx.filter(t => t.is_savings && t.transaction_type === 'withdrawal' && t.to_card === card.name)
      .reduce((sum, t) => sum + t.amount, 0);

    return startedWith + incomeIn + transfersIn - spent - transfersOut - savedFrom + withdrawnTo;
  };

  // Suggested carry-in for the viewed month: every debit/cash card's closing
  // balance last month. A suggestion only - nothing is written until the
  // button in Starting balances is tapped.
  const prevCardBalances = (() => {
    const [y, m] = viewMonthKey.split('-').map(Number);
    const key = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
    const out = {};
    paymentMethods.filter(c => c.type !== 'credit').forEach(c => {
      out[c.name] = cardBalanceAtEndOf(key, c);
    });
    return out;
  })();

  // Cards whose starting balance FOLLOWS last month rather than being a frozen
  // figure. Tapping "Use <month>'s closing balances" turns this on for every
  // card; typing over a row turns it off for that card alone.
  const carryFlags = budgetSettings.leftover_carry_by_month?.[viewMonthKey] || {};

  // Keep those cards in step. Storage stays the single source of truth - the
  // Leftover tile, Money, cardTotals and this sheet all read the saved
  // allocations, so the carried figure is re-saved when it drifts rather than
  // computed live in one place and stale in the others.
  //
  // Settles immediately: once written, saved === computed, so this does
  // nothing until last month actually changes again.
  useEffect(() => {
    const following = Object.keys(carryFlags).filter(name => carryFlags[name]);
    if (following.length === 0) return;

    const saved = budgetSettings.leftover_allocations_by_month?.[viewMonthKey] || {};
    const next = { ...saved };
    let drifted = false;

    following.forEach(name => {
      const live = prevCardBalances[name];
      if (typeof live !== 'number') return;
      const rounded = Math.round(live * 100) / 100;
      if (Math.abs((saved[name] || 0) - rounded) >= 0.005) {
        next[name] = rounded;
        drifted = true;
      }
    });
    if (!drifted) return;

    saveUserData({
      budget_settings: {
        ...budgetSettings,
        leftover_allocations_by_month: {
          ...(budgetSettings.leftover_allocations_by_month || {}),
          [viewMonthKey]: next,
        },
        leftover_by_month: {
          ...(budgetSettings.leftover_by_month || {}),
          [viewMonthKey]: Object.values(next).reduce((a, b) => a + b, 0),
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMonthKey, JSON.stringify(carryFlags), JSON.stringify(prevCardBalances)]);

  // Calculate card balances (dynamic) - different logic for debit vs credit (cash treated like debit)
  const cardTotals = paymentMethods.map(card => {
    const isCredit = card.type === 'credit';
    
    // Expenses from this card
    const expenses = expenseTransactions.filter(t => t.payment_method === card.name).reduce((sum, t) => sum + t.amount, 0);
    const expenseCount = expenseTransactions.filter(t => t.payment_method === card.name).length;
    
    if (isCredit) {
      // Credit card balance = Expenses - Transfers received (payments to this card)
      const transfersReceived = viewMonthTransactions
        .filter(t => t.is_transfer && t.to_card === card.name && t.is_credit_payment)
        .reduce((sum, t) => sum + t.amount, 0);
      
      return {
        card: card.name,
        color: card.color,
        type: 'credit',
        total: expenses,
        balance: expenses - transfersReceived, // Amount owed
        count: expenseCount,
        transfersReceived
      };
    } else {
      // Debit/cash balance = Leftover allocation + Income to card + Transfers IN to card - Expenses - Transfers OUT - Savings deposits from card + Withdrawals to card
      const leftoverForCard = budgetSettings.leftover_allocations_by_month?.[viewMonthKey]?.[card.name] || 0;
      
      const incomeToCard = incomeTransactions
        .filter(t => t.to_card === card.name)
        .reduce((sum, t) => sum + t.amount, 0);
      
      const transfersOut = viewMonthTransactions
        .filter(t => t.is_transfer && t.payment_method === card.name)
        .reduce((sum, t) => sum + t.amount, 0);
      
      // Transfers received INTO this card (only count non-credit-payment transfers,
      // since credit-payment transfers are handled by the credit branch above).
      const transfersIn = viewMonthTransactions
        .filter(t => t.is_transfer && t.to_card === card.name && !t.is_credit_payment)
        .reduce((sum, t) => sum + t.amount, 0);
      
      // Savings/Investment deposits that came FROM this card (reduces balance)
      const savingsDepositsFromCard = viewMonthTransactions
        .filter(t => t.is_savings && t.transaction_type === 'deposit' && t.from_card === card.name)
        .reduce((sum, t) => sum + t.amount, 0);
      
      // Withdrawals that went TO this card (increases balance)
      const withdrawalsToCard = viewMonthTransactions
        .filter(t => t.is_savings && t.transaction_type === 'withdrawal' && t.to_card === card.name)
        .reduce((sum, t) => sum + t.amount, 0);
      
      return {
        card: card.name,
        color: card.color,
        type: 'debit',
        total: expenses,
        balance: leftoverForCard + incomeToCard + transfersIn - expenses - transfersOut - savingsDepositsFromCard + withdrawalsToCard,
        count: expenseCount,
        incomeToCard,
        transfersOut,
        leftoverForCard,
        savingsDepositsFromCard,
        withdrawalsToCard
      };
    }
  });

  // Ensure new categories are always available
  const baseCategories = budgetSettings.categories?.length > 0 ? budgetSettings.categories : [];
  const categories = [...new Set(baseCategories)];
  const filteredCategories = categories.filter(cat => cat.toLowerCase().includes(categorySearch.toLowerCase()));
  const editFilteredCategories = categories.filter(cat => cat.toLowerCase().includes(editCategorySearch.toLowerCase()));

  // Calculate spending by category (expenses only)
  const spendingByCategory = {};
  expenseTransactions.forEach(t => {
    spendingByCategory[t.category] = (spendingByCategory[t.category] || 0) + t.amount;
  });

  // Calculate total budgeted across all categories
  const totalCategoryBudgets = Object.entries(monthlyCategoryBudgets)
    .filter(([cat]) => categories.includes(cat))
    .reduce((sum, [, budget]) => sum + (budget || 0), 0);

  // Calculate savings/investment budgets (negative values = planned withdrawals)
  const validSavingsIds = new Set([
    ...(allSinkingFunds || []).map(f => f.id),
    ...(allInvestmentAccounts || []).map(a => a.id)
  ]);
  const validDebtIds = new Set((userDebts || []).map(d => d.id));
  const savingsBudgets = monthlySavingsBudgets;

  // Positive savings budgets (deposits to savings/investments)
  const positiveSavingsBudgets = Object.entries(savingsBudgets)
    .filter(([id, budget]) => validSavingsIds.has(id) && budget > 0)
    .reduce((sum, [, budget]) => sum + budget, 0);

  // Planned withdrawals are negative savings budgets - these add to available budget
  const plannedWithdrawals = Object.entries(savingsBudgets)
    .filter(([id, budget]) => validSavingsIds.has(id) && budget < 0)
    .reduce((sum, [, budget]) => sum + Math.abs(budget), 0);

  // Total debt budgets
  const totalDebtBudgets = Object.entries(monthlyDebtBudgets)
    .filter(([id]) => validDebtIds.has(id))
    .reduce((sum, [, budget]) => sum + (budget || 0), 0);

  // Left to budget = (amount to budget + planned withdrawals) - spending budgets - positive savings budgets - debt budgets
  const leftToBudget = (monthlyBudget + plannedWithdrawals) - totalCategoryBudgets - positiveSavingsBudgets - totalDebtBudgets;

  // Amounts you are counting down to zero should not round away their cents:
  // $6.20 stays $6.20, while a clean $6.00 still shows as $6. Used for the
  // "left"/"over" figures only - the "spent of budget" sublines stay whole.
  // Savings rows show the NET moved this month (deposits minus withdrawals),
  // signed, so a -$400 plan reads -$400 when $200 went in and $600 came out.
  const signedWhole = (n) => (n <= -0.5 ? `-${formatCurrencyWhole(-n)}` : formatCurrencyWhole(Math.max(n, 0)));

  const formatCurrencyExact = (n) =>
    Math.round((n || 0) * 100) % 100 === 0 ? formatCurrencyWhole(n) : formatCurrency(n);

  // Calculate % through the month
  const today = new Date();
  const isViewingCurrentMonth = viewMonth.getMonth() === today.getMonth() && viewMonth.getFullYear() === today.getFullYear();
  const daysInViewMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
  const isViewingFutureMonth = viewMonth.getFullYear() > today.getFullYear() || (viewMonth.getFullYear() === today.getFullYear() && viewMonth.getMonth() > today.getMonth());
  const currentDayOfMonth = isViewingCurrentMonth ? today.getDate() : isViewingFutureMonth ? 0 : daysInViewMonth;
  const monthProgressPercent = Math.round((currentDayOfMonth / daysInViewMonth) * 100);

  // % of budget spent — computed in src/utils/budgetMath.js so the Lock Screen
  // widget draws the exact same number, exclusions and all.
  const { spentExcludingExcluded, budgetForSpentCalc, budgetSpentPercent } = computeBudgetSpentPercent({
    expenseTransactions,
    monthlyCategoryBudgets,
    monthlyDebtBudgets,
    userDebts,
    excludedCategories: budgetSettings.excluded_categories || [],
    monthlyBudget,
  });

  // "Budget left to spend" = how much you can still spend WITHOUT breaking a budget:
  // the sum of what's left in each spending envelope. This moves the moment you
  // change a category's budget, which the previous figure could not — that one
  // was income minus actuals, so it tracked spending rather than allocation and
  // nearly duplicated the Leftover tile.
  //
  // Each category contributes at most its own remainder and never a negative, so
  // one overspent envelope doesn't quietly lend headroom from another. Overspend
  // is surfaced per-row ("$X over") and by the Pace card instead.
  // To net overspend out of the total instead, drop the Math.max(0, ...) wrapper.
  const budgetAvailable = categories.reduce(
    (sum, cat) => sum + Math.max(0, (monthlyCategoryBudgets[cat] || 0) - (spendingByCategory[cat] || 0)),
    0
  );

  // Get categories with budgets set (sorted by: has budget first, then by most spent)
  const categoriesWithActivity = categories
    .map(cat => ({
      name: cat,
      spent: spendingByCategory[cat] || 0,
      budget: monthlyCategoryBudgets[cat] || 0
    }))
    .sort((a, b) => {
      // First, prioritize categories with budgets set
      const aHasBudget = a.budget > 0 ? 1 : 0;
      const bHasBudget = b.budget > 0 ? 1 : 0;
      if (bHasBudget !== aHasBudget) return bHasBudget - aHasBudget;
      // Then sort by most spent
      return b.spent - a.spent;
    });

  // Yearly categories sorted by spent
  const yearlyCategoriesWithSpending = categories
    .map(cat => ({ name: cat, spent: yearlySpendingByCategory[cat] || 0 }))
    .filter(c => c.spent > 0)
    .sort((a, b) => b.spent - a.spent);

  const selectCategory = (cat) => { setNewTransaction(prev => ({ ...prev, category: cat })); setCategorySearch(''); setShowCategoryDropdown(false); };
  const selectEditCategory = (cat) => { setEditTransactionData(prev => ({ ...prev, category: cat })); setEditCategorySearch(''); setShowEditCategoryDropdown(false); };

  // Get the appropriate date for a transaction.
  // If the user picked a date in the form, honour it (using local noon to avoid timezone drift).
  // Otherwise fall back to today (current month) or the 1st of the view month.
  const getTransactionDate = () => {
    if (transactionDate) {
      const [y, m, d] = transactionDate.split('-').map(Number);
      // Use local noon so toISOString never rolls back to the previous day
      return new Date(y, m - 1, d, 12, 0, 0).toISOString();
    }
    const now = new Date();
    const viewYear = viewMonth.getFullYear();
    const viewMonthNum = viewMonth.getMonth();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    if (viewYear === currentYear && viewMonthNum === currentMonth) {
      return now.toISOString();
    } else {
      const firstDay = new Date(viewYear, viewMonthNum, 1);
      return firstDay.toISOString();
    }
  };

  // Derive month_key from the picked date so the transaction lands in the
  // correct month tab, even if the user is viewing a different month.
  const getTransactionMonthKey = () => {
    if (transactionDate) {
      const [y, m] = transactionDate.split('-');
      return `${y}-${m}`;
    }
    return viewMonthKey;
  };

  const addTransaction = (isIncome = false) => {
    if (isIncome) {
      // For income, only amount and description required
      if (newIncome.amount && parseFloat(newIncome.amount) > 0) {
        const transaction = {
          id: Date.now(),
          amount: parseFloat(newIncome.amount),
          category: newIncome.isInterest ? 'Interest' : 'Income',
          description: newIncome.description.trim() || (newIncome.isInterest ? 'Interest' : 'Income'),
          payment_method: 'Income',
          month_key: getTransactionMonthKey(),
          date: getTransactionDate(),
          completed: false,
          is_income: true,
          is_savings: false,
          is_interest: newIncome.isInterest,
          interest_account_id: newIncome.isInterest ? newIncome.interestAccount : null,
          interest_account_type: newIncome.isInterest ? newIncome.interestAccountType : null,
          to_card: newIncome.toCard || null
        };
        saveUserData({ transactions: [transaction, ...transactions] });
        setNewIncome({ amount: '', description: '', isInterest: false, interestAccount: '', interestAccountType: '', toCard: '' });
      } else {
        customToast.error("Missing amount", "Please enter an amount");
      }
    } else {
      // For expenses, all fields required
      if (newTransaction.amount && parseFloat(newTransaction.amount) > 0 && newTransaction.category && newTransaction.paymentMethod) {
        const transaction = {
          id: Date.now(),
          amount: parseFloat(newTransaction.amount),
          category: newTransaction.category,
          description: newTransaction.description.trim(),
          payment_method: newTransaction.paymentMethod,
          month_key: getTransactionMonthKey(),
          date: getTransactionDate(),
          completed: false,
          is_income: false,
          is_savings: false,
          ...(newTransaction.feeling ? { feeling: newTransaction.feeling } : {})
        };
        saveUserData({ transactions: [transaction, ...transactions] });
        setNewTransaction({ amount: '', category: '', description: '', paymentMethod: '', feeling: '' });
      } else {
        customToast.error("Missing fields", "Please fill all required fields");
      }
    }
  };

  const addSavingsTransaction = () => {
    if (newSavings.amount && parseFloat(newSavings.amount) > 0 && newSavings.accountId) {
      const depositAmount = parseFloat(newSavings.amount);
      const transaction = {
        id: Date.now(),
        amount: depositAmount,
        category: 'Savings & Investments',
        description: newSavings.description.trim() || 'Deposit',
        payment_method: newSavings.fromCard || 'Savings',
        month_key: getTransactionMonthKey(),
        date: getTransactionDate(),
        completed: false,
        is_income: false,
        is_savings: true,
        account_id: newSavings.accountId,
        account_type: newSavings.accountType,
        transaction_type: 'deposit',
        from_card: newSavings.fromCard || null
      };
      
      saveUserData({ transactions: [transaction, ...transactions] });
      setNewSavings({ amount: '', description: '', accountId: '', accountType: '', fromCard: '' });
    } else {
      customToast.error("Missing info", "Please select an account and enter an amount");
    }
  };

  const addWithdrawalTransaction = () => {
    if (newWithdrawal.amount && parseFloat(newWithdrawal.amount) > 0 && newWithdrawal.accountId) {
      const transaction = {
        id: Date.now(),
        amount: parseFloat(newWithdrawal.amount),
        category: 'Withdrawal',
        description: newWithdrawal.description.trim() || 'Withdrawal',
        payment_method: newWithdrawal.toCard || 'Savings',
        month_key: getTransactionMonthKey(),
        date: getTransactionDate(),
        completed: false,
        is_income: false,
        is_savings: true,
        account_id: newWithdrawal.accountId,
        account_type: newWithdrawal.accountType,
        transaction_type: 'withdrawal',
        to_card: newWithdrawal.toCard || null
      };
      saveUserData({ transactions: [transaction, ...transactions] });
      setNewWithdrawal({ amount: '', description: '', accountId: '', accountType: '', toCard: '' });
    } else {
      customToast.error("Missing info", "Please select an account and enter an amount");
    }
  };

  const addDebtPaymentTransaction = () => {
    if (newDebtPayment.amount && parseFloat(newDebtPayment.amount) > 0 && newDebtPayment.debtId) {
      const debt = userDebts.find(d => d.id === newDebtPayment.debtId);
      const debtName = debt ? debt.name : 'Debt';
      
      const transaction = {
        id: Date.now(),
        amount: parseFloat(newDebtPayment.amount),
        category: 'Debt Payment',
        description: newDebtPayment.description.trim() || `Payment to ${debtName}`,
        payment_method: newDebtPayment.paymentMethod || 'Debt Payment',
        month_key: getTransactionMonthKey(),
        date: getTransactionDate(),
        completed: false,
        is_income: false,
        is_savings: false,
        is_debt_payment: true,
        debt_id: newDebtPayment.debtId,
        debt_name: debtName
      };
      
      // Calculate new balance after payment to check if paid off
      const currentBalance = debt ? getCurrentDebtBalance(debt) : 0;
      const newBalance = currentBalance - parseFloat(newDebtPayment.amount);
      
      saveUserData({ transactions: [transaction, ...transactions] });
      setNewDebtPayment({ amount: '', description: '', debtId: '', paymentMethod: '' });
      
      // Check if debt is paid off and celebrate!
      if (newBalance <= 0 && debt) {
        confetti({
          particleCount: 150,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#10b981', '#34d399', '#6ee7b7', '#fbbf24', '#f59e0b']
        });
        customToast.celebration(`Congratulations!`, `You've paid off "${debtName}"!`);
      } else {
      }
    } else {
      customToast.error("Missing info", "Please select a debt and enter an amount");
    }
  };

  // Handle card-to-card transfer
  const addTransferTransaction = () => {
    if (newTransfer.amount && parseFloat(newTransfer.amount) > 0 && newTransfer.fromCard && newTransfer.toCard) {
      if (newTransfer.fromCard === newTransfer.toCard) {
        customToast.error("Invalid transfer", "Cannot transfer to the same card");
        return;
      }
      
      const amount = parseFloat(newTransfer.amount);
      const toCard = paymentMethods.find(c => c.name === newTransfer.toCard);
      const isCreditPayment = toCard?.type === 'credit';
      
      // Create transfer-out transaction (from source card)
      const transferOut = {
        id: Date.now(),
        amount: amount,
        category: 'Transfer',
        description: newTransfer.description.trim() || `Transfer to ${newTransfer.toCard}`,
        payment_method: newTransfer.fromCard,
        month_key: getTransactionMonthKey(),
        date: getTransactionDate(),
        completed: false,
        is_income: false,
        is_transfer: true,
        transfer_to: newTransfer.toCard
      };
      
      // Create transfer-in transaction (to destination card)
      const transferIn = {
        id: Date.now() + 1,
        amount: amount,
        category: isCreditPayment ? 'Credit Payment' : 'Transfer',
        description: newTransfer.description.trim() || `Transfer from ${newTransfer.fromCard}`,
        payment_method: 'Transfer',
        month_key: getTransactionMonthKey(),
        date: getTransactionDate(),
        completed: false,
        is_income: true,
        is_transfer: true,
        to_card: newTransfer.toCard,
        transfer_from: newTransfer.fromCard,
        is_credit_payment: isCreditPayment
      };
      
      saveUserData({ transactions: [transferIn, transferOut, ...transactions] });
      setNewTransfer({ amount: '', fromCard: '', toCard: '', description: '' });
      
      if (isCreditPayment) {
      } else {
      }
    } else {
      customToast.error("Missing info", "Please select both cards and enter an amount");
    }
  };

  // Helper to calculate current debt balance from transactions
  const getCurrentDebtBalance = (debt) => {
    // Get the starting balance - prefer starting_balance, fall back to current_by_month for legacy
    let startingBalance;
    if (debt.starting_balance !== undefined) {
      startingBalance = debt.starting_balance;
    } else {
      const monthKeys = Object.keys(debt.current_by_month || {}).sort();
      startingBalance = monthKeys.length > 0 
        ? debt.current_by_month[monthKeys[monthKeys.length - 1]] 
        : (debt.original_amount || 0);
    }
    
    // Sum all debt payments for this debt
    const totalPayments = transactions
      .filter(t => t.is_debt_payment && t.debt_id === debt.id)
      .reduce((sum, t) => sum + t.amount, 0);
    
    return startingBalance - totalPayments;
  };

  const removeTransaction = (id) => saveUserData({ transactions: transactions.filter(t => t.id !== id) });

  const resetMonth = () => {
    // Remove all transactions for the viewed month
    const filteredTransactions = transactions.filter(t => t.month_key !== viewMonthKey);
    
    // Clear all budget_settings fields for the viewed month
    // (matches the nested shape written by MonthlyBudgetSetup.jsx)
    const updatedBudgetByMonth = { ...(budgetSettings.budget_by_month || {}) };
    delete updatedBudgetByMonth[viewMonthKey];
    
    const updatedLeftoverByMonth = { ...(budgetSettings.leftover_by_month || {}) };
    delete updatedLeftoverByMonth[viewMonthKey];
    
    const updatedLeftoverAllocations = { ...(budgetSettings.leftover_allocations_by_month || {}) };
    delete updatedLeftoverAllocations[viewMonthKey];

    // Clearing a month must also stop its cards following last month -
    // otherwise the sync below would put the carried balances straight back.
    const updatedLeftoverCarry = { ...(budgetSettings.leftover_carry_by_month || {}) };
    delete updatedLeftoverCarry[viewMonthKey];
    
    const updatedSavingsBudgets = { ...(budgetSettings.savings_budgets_by_month || {}) };
    delete updatedSavingsBudgets[viewMonthKey];
    
    const updatedDebtBudgets = { ...(budgetSettings.debt_budgets_by_month || {}) };
    delete updatedDebtBudgets[viewMonthKey];
    
    const updatedNotesByMonth = { ...(budgetSettings.notes_by_month || {}) };
    delete updatedNotesByMonth[viewMonthKey];
    
    // Clear category budgets for the viewed month (stored at top level, not nested)
    const updatedCategoryBudgets = { ...categoryBudgets };
    delete updatedCategoryBudgets[viewMonthKey];
    
    saveUserData({
      transactions: filteredTransactions,
      category_budgets_by_month: updatedCategoryBudgets,
      budget_settings: {
        ...budgetSettings,
        budget_by_month: updatedBudgetByMonth,
        leftover_by_month: updatedLeftoverByMonth,
        leftover_allocations_by_month: updatedLeftoverAllocations,
        leftover_carry_by_month: updatedLeftoverCarry,
        savings_budgets_by_month: updatedSavingsBudgets,
        debt_budgets_by_month: updatedDebtBudgets,
        notes_by_month: updatedNotesByMonth
      }
    });
    
  };

  const toggleTransactionCompleted = (id) => {
    const updatedTransactions = transactions.map(t => 
      t.id === id ? { ...t, completed: !t.completed } : t
    );
    saveUserData({ transactions: updatedTransactions });
  };

  const startEditTransaction = (transaction) => {
    // Open the bottom sheet instead of toggling inline edit mode.
    setEditSheetTxId(transaction.id);
    setShowEditCategoryDropdown(false);
    setEditCategorySearch('');
    
    // Determine transaction type
    let transactionType = 'expense';
    if (transaction.is_income) {
      transactionType = transaction.is_interest ? 'interest' : 'income';
    } else if (transaction.is_savings) {
      transactionType = 'savings';
    }
    
    setEditTransactionData({
      amount: transaction.amount.toString(),
      date: transaction.date ? new Date(transaction.date).toISOString().split('T')[0] : '',
      category: transaction.category || '',
      description: transaction.description || '',
      paymentMethod: transaction.payment_method || '',
      transactionType,
      accountId: transaction.account_id || transaction.interest_account_id || '',
      accountType: transaction.account_type || transaction.interest_account_type || '',
      savingsTransactionType: transaction.transaction_type || 'deposit',
      to_card: transaction.to_card || '',
      from_card: transaction.from_card || '',
      feeling: transaction.feeling || ''
    });
  };

  // Helper: build the updated transaction list from current edit state.
  // Returns { updatedList, updated, newMonthKey, oldMonthKey } or null on validation error.
  const buildEditTransactionUpdate = () => {
    const amount = parseFloat(editTransactionData.amount);
    if (!amount || amount <= 0) {
      customToast.error("Invalid amount", "Please enter a valid amount");
      return null;
    }

    if (editTransactionData.transactionType === 'expense' &&
        (!editTransactionData.category || !editTransactionData.paymentMethod)) {
      customToast.error("Missing fields", "Please fill all required fields");
      return null;
    }

    if ((editTransactionData.transactionType === 'interest' || editTransactionData.transactionType === 'savings') &&
        !editTransactionData.accountId) {
      customToast.error("Missing account", "Please select an account");
      return null;
    }

    // Compute new month_key from the picked date (or fall back to the original)
    const original = transactions.find(t => t.id === editSheetTxId);
    if (!original) return null;

    let newMonthKey = original.month_key;
    if (editTransactionData.date) {
      const [y, m] = editTransactionData.date.split('-');
      newMonthKey = `${y}-${m}`;
    }

    let updatedRef = null;
    const updatedList = transactions.map(t => {
      if (t.id === editSheetTxId) {
        const updated = {
          ...t,
          amount: amount,
          date: editTransactionData.date ? new Date(editTransactionData.date + 'T12:00:00').toISOString() : t.date,
          month_key: newMonthKey,
          description: editTransactionData.description.trim()
        };

        if (editTransactionData.transactionType === 'expense') {
          updated.category = editTransactionData.category;
          updated.payment_method = editTransactionData.paymentMethod;
          const selectedCard = paymentMethods.find(c => c.name === editTransactionData.paymentMethod);
          if (selectedCard) updated.card_id = selectedCard.id;
          if (editTransactionData.feeling) updated.feeling = editTransactionData.feeling;
          else delete updated.feeling;
        }
        if (editTransactionData.transactionType === 'income') {
          updated.to_card = editTransactionData.to_card || null;
        }
        if (editTransactionData.transactionType === 'interest') {
          updated.interest_account_id = editTransactionData.accountId || null;
          updated.interest_account_type = editTransactionData.accountType || null;
        }
        if (editTransactionData.transactionType === 'savings') {
          updated.account_id = editTransactionData.accountId || null;
          updated.account_type = editTransactionData.accountType || null;
          if (editTransactionData.savingsTransactionType === 'deposit') {
            updated.from_card = editTransactionData.from_card || null;
            updated.payment_method = editTransactionData.from_card || 'Savings';
          } else if (editTransactionData.savingsTransactionType === 'withdrawal') {
            updated.to_card = editTransactionData.to_card || null;
            updated.payment_method = editTransactionData.to_card || 'Savings';
          }
        }
        updatedRef = updated;
        return updated;
      }
      return t;
    });

    return { updatedList, updated: updatedRef, newMonthKey, oldMonthKey: original.month_key };
  };

  // Helper: actually persist the updated transactions and clean up state.
  const commitEditTransaction = (updatedList) => {
    try {
      const result = saveUserData({ transactions: updatedList });
      if (result && typeof result.catch === 'function') {
        result.catch((err) => {
          console.error('[edit transaction] save failed:', err);
          customToast.error('Save failed', err?.message || 'Could not save transaction');
        });
      }
    } catch (err) {
      console.error('[edit transaction] save threw:', err);
      customToast.error('Save failed', err?.message || 'Could not save transaction');
      return;
    }
    setEditingTransaction(null);
    setEditSheetTxId(null);
    setPendingMonthMoveEdit(null);
    setEditTransactionData({
      amount: '',
      date: '',
      category: '',
      description: '',
      paymentMethod: '',
      transactionType: '',
      accountId: '',
      accountType: '',
      to_card: '',
      from_card: ''
    });
  };

  const saveEditTransaction = () => {
    const result = buildEditTransactionUpdate();
    if (!result) return; // validation error already toasted

    // If the month is changing, ask the user to confirm before committing.
    if (result.newMonthKey !== result.oldMonthKey) {
      setPendingMonthMoveEdit(result);
      return;
    }

    commitEditTransaction(result.updatedList);
  };

  const cancelEditTransaction = () => {
    setEditingTransaction(null);
    setEditSheetTxId(null);
    setEditTransactionData({ 
      amount: '', 
      date: '',
      category: '', 
      description: '', 
      paymentMethod: '',
      transactionType: '',
      accountId: '',
      accountType: '',
      to_card: '',
      from_card: ''
    });
  };

  const updateBudgetSetting = (key, value) => {
    saveUserData({ budget_settings: { ...budgetSettings, [key]: parseFloat(value) || 0 } });
  };

  const updateBudgetForMonth = (monthKey, value) => {
    const newBudgetByMonth = { ...budgetSettings.budget_by_month, [monthKey]: parseFloat(value) || 0 };
    saveUserData({ budget_settings: { ...budgetSettings, budget_by_month: newBudgetByMonth } });
  };

  const updateLeftoverForMonth = (monthKey, value) => {
    const newLeftoverByMonth = { ...budgetSettings.leftover_by_month, [monthKey]: parseFloat(value) || 0 };
    saveUserData({ budget_settings: { ...budgetSettings, leftover_by_month: newLeftoverByMonth } });
  };

  const updateCategoryBudget = (category) => {
    const value = parseFloat(categoryBudgetValue) || 0;
    const monthBudgets = { ...monthlyCategoryBudgets, [category]: value };
    saveUserData({ category_budgets_by_month: { ...categoryBudgets, [viewMonthKey]: monthBudgets } });
    setEditingCategoryBudget(null);
    setCategoryBudgetValue('');
    if (value > 0) {
    }
  };

  const startEditingCategoryBudget = (category) => {
    setEditingCategoryBudget(category);
    setCategoryBudgetValue(monthlyCategoryBudgets[category]?.toString() || '');
  };

  const clearCategoryBudget = (category) => {
    const newMonthBudgets = { ...monthlyCategoryBudgets };
    delete newMonthBudgets[category];
    saveUserData({ category_budgets_by_month: { ...categoryBudgets, [viewMonthKey]: newMonthBudgets } });
  };

  const saveSavingsBudgetForMonth = (itemId, value) => {
    const newMonthBudgets = { ...monthlySavingsBudgets, [itemId]: value };
    const allMonths = { ...(budgetSettings.savings_budgets_by_month || {}), [viewMonthKey]: newMonthBudgets };
    saveUserData({ budget_settings: { ...budgetSettings, savings_budgets_by_month: allMonths } });
  };

  const saveDebtBudgetForMonth = (itemId, value) => {
    const newMonthBudgets = { ...monthlyDebtBudgets, [itemId]: value };
    const allMonths = { ...(budgetSettings.debt_budgets_by_month || {}), [viewMonthKey]: newMonthBudgets };
    saveUserData({ budget_settings: { ...budgetSettings, debt_budgets_by_month: allMonths } });
  };

  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';
  // Purple (admin-only): lavender everywhere instead of many colours.
  const purple = usePurple();
  const chartPal = CHART_PALETTES[plan ? 'plan' : purple ? 'purple' : isPink ? 'pink' : isWarm ? 'warm' : 'blue'];

  // Ink used by the month-setup sheets. Theme-aware like everything else here.
  // Clear month deliberately opts out and stays black in every theme - a
  // destructive action should not wear the colour that safe actions wear.
  const mutedInk = plan ? '#8B879A' : isPink ? '#6B6270' : isWarm ? '#6B6459' : '#5B6072';
  const faintInk = plan ? '#C1BCD0' : isPink ? '#C9C2CC' : isWarm ? '#CFC6B8' : '#C2C6D6';
  const accentInk = plan ? '#7A5DD3' : isPink ? '#7A5FB0' : isWarm ? '#5D6B4D' : '#4361EE';
  // The green the Money figure uses in the Available card, per theme. Every
  // "money you have" figure on this tab reads in it, at one size, so they are
  // recognisably the same kind of number.
  const moneyInk = plan ? '#26252E' : purple ? '#1E1630' : isPink ? '#16A34A' : isWarm ? '#5C7A4A' : '#2F7A80';
  // Money-out figures across the tab. #EF4444 is an alert colour doing a routine
  // job, which is why it read harsh; this is the same red with the alarm taken
  // out. Warm keeps its own dusty rose.
  const negInk = plan ? '#C04B7C' : isWarm ? '#9B6B6B' : '#D65A54';
  // Emoji for a transaction row. Categories store theirs inside the name and not
  // everyone sets one, so the fallback is the same tag the transaction rows use.
  const txEmoji = (t) => {
    if (t.is_income) return '💰';
    if (t.is_transfer) return '🔄';
    if (t.is_savings) return t.transaction_type === 'withdrawal' ? '💸' : '🐷';
    if (t.is_debt_payment) return '🧾';
    const { emoji } = parseCategoryEmoji(t.category || '');
    return emoji || '🏷️';
  };
  // The Available card's two tile washes and its Budget figure colour, so the
  // Your Budgets header can be built as its mirror rather than a lookalike.
  // Blue and purple, the Spent and Leftover tokens from the hero tiles, so this
  // card is not mistaken for Available (pink + green) directly above it.
  const budgetTileBg = plan ? '#F7F5FB' : purple ? '#F6F4F8' : isPink
    ? 'linear-gradient(135deg, #DBE7F5 0%, #C4D6ED 100%)'
    : isWarm
      ? 'linear-gradient(135deg, #EDE3D5 0%, #E2D3BC 100%)'
      : 'linear-gradient(135deg, #E6E6F5 0%, #D6D6ED 100%)';
  const budgetInk = plan ? '#26252E' : purple ? '#1E1630' : isPink ? '#3C6693' : isWarm ? '#7A6A4A' : '#5B4F93';
  const moneyTileBg = plan ? '#F7F5FB' : purple ? '#F6F4F8' : isPink
    ? 'linear-gradient(135deg, #E9E1F5 0%, #D5C7EB 100%)'
    : isWarm
      ? 'linear-gradient(135deg, #F0E3D9 0%, #E5D0C2 100%)'
      : 'linear-gradient(135deg, #E4E1F5 0%, #D2CDEB 100%)';
  const toBudgetInk = plan ? '#7A5DD3' : purple ? '#1E1630' : isPink ? '#7A5FB0' : isWarm ? '#9A5B3C' : '#6B5FB0';
  // Category rows: a calm wash behind each emoji, and the breakdown chart's own
  // "expenses" tone for the bars - #F5C8DC was so close to its track that the
  // bars read as empty space rather than progress.
  const rowTileBg = plan ? '#F5F2FA' : purple ? '#F6F4F8' : isPink ? '#F8F1F5' : isWarm ? '#F3EDE4' : '#F0F2FC';

  // plan_07: Money plan - one slim row shape for Spending, Savings and Debts.
  const planSlimRow = ({ key, emoji, name, right, pct, small, color, onEdit, editLabel }) => (
    <div key={key} className="flex items-center wy-plan-cat" style={{ gap: 12, padding: '12px 0' }}>
      <span className="flex items-center justify-center flex-shrink-0" style={{ width: 36, height: 36, borderRadius: 12, background: '#F5F2FA', fontSize: 16 }}>{emoji}</span>
      <span className="flex-1 min-w-0">
        <span className="flex items-baseline justify-between" style={{ gap: 8 }}>
          <span className="truncate" style={{ fontSize: 14.5, fontWeight: 600, color: '#26252E' }}>{name}</span>
          <span className="flex-shrink-0" style={{ fontSize: 13, fontWeight: 600, color: '#5E5A6B' }}>{right}</span>
        </span>
        <span className="block" style={{ height: 6, borderRadius: 9, background: '#F0EDF6', marginTop: 7, overflow: 'hidden' }}>
          <span className="block" style={{ width: `${Math.min(100, Math.max(0, pct || 0))}%`, height: 6, borderRadius: 9, background: color }} />
        </span>
        <span className="block" style={{ fontSize: 12, color: '#8B879A', marginTop: 5 }}>{small}</span>
      </span>
      <button type="button" aria-label={editLabel} onClick={onEdit} className="w-8 h-8 flex items-center justify-center flex-shrink-0">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#C1BCD0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 20h4L19.5 8.5a2.12 2.12 0 0 0-3-3L5 17z"></path><path d="M14.5 6.5 17.5 9.5"></path></svg>
      </button>
    </div>
  );

  // Warm theme color palette for budget tab
  const warmColors = {
    // Savings/Purple → Muted olive sage
    savings: { text: 'text-[#5D6B4D]', bg: 'bg-[#E8EDE4]', border: 'border-[#D4DBC9]', bgHover: 'hover:bg-[#E8EDE4]', icon: 'text-[#5D6B4D]' },
    // Red/Spent → Muted dusty rose  
    spent: { text: 'text-[#9B6B6B]', bg: 'bg-[#F0E4E4]', border: 'border-[#E0D0D0]', bgHover: 'hover:bg-[#F0E4E4]', icon: 'text-[#9B6B6B]' },
    // Green/Income → Forest green
    income: { text: 'text-[#2D5A3D]', bg: 'bg-[#E4EDE6]', border: 'border-[#C9D9CE]', bgHover: 'hover:bg-[#E4EDE6]', icon: 'text-[#2D5A3D]' },
    // Blue/Investments → Muted teal
    investments: { text: 'text-[#4A6B6B]', bg: 'bg-[#E4EDED]', border: 'border-[#C9D9D9]', bgHover: 'hover:bg-[#E4EDED]', icon: 'text-[#4A6B6B]' },
    // Orange/Debt → Muted terracotta
    debt: { text: 'text-[#8B6B4D]', bg: 'bg-[#F0E8E0]', border: 'border-[#E0D4C4]', bgHover: 'hover:bg-[#F0E8E0]', icon: 'text-[#8B6B4D]' },
  };

  // --- Add Transaction sheet styling (Sept redesign) ---
  // Emoji + copy per transaction type, shared by the picker menu and every form's header.
  const txTypeMeta = {
    income:      { emoji: '💰', label: 'Income',        sub: 'Log earnings' },
    expense:     { emoji: '🛍️', label: 'Expense',       sub: 'Log spending' },
    savings:     { emoji: '📈', label: 'Save & Invest',  sub: 'Funds & accounts' },
    withdrawal:  { emoji: '🏦', label: 'Withdraw',       sub: 'Savings & investments' },
    debtpayment: { emoji: '🧾', label: 'Pay Debt',       sub: 'Credit & loans' },
    transfer:    { emoji: '🔁', label: 'Transfer',       sub: 'Between payment methods' },
  };
  const txTypeChip = (type) => {
    if (plan) return { className: '', style: { background: '#F5F2FA' } };
    if (purple) return { className: '', style: { background: '#F1E9FB' } };
    if (isWarm) {
      const warmMap = { income: warmColors.income, expense: warmColors.spent, savings: warmColors.savings, withdrawal: warmColors.debt, debtpayment: warmColors.spent, transfer: warmColors.investments };
      return { className: (warmMap[type] && warmMap[type].bg) || 'bg-gray-100', style: {} };
    }
    if (isPink) {
      const pinkMap = {
        income:      'linear-gradient(135deg, #FAE3EC 0%, #F5D5E3 100%)',
        expense:     'linear-gradient(135deg, #DBE7F5 0%, #C4D6ED 100%)',
        savings:     'linear-gradient(135deg, #E7F1DB 0%, #D3E5BE 100%)',
        withdrawal:  'linear-gradient(135deg, #F7E7C9 0%, #EEDAA8 100%)',
        debtpayment: 'linear-gradient(135deg, #F6D9D9 0%, #EEC3C3 100%)',
        transfer:    'linear-gradient(135deg, #E9E1F5 0%, #D5C7EB 100%)',
      };
      return { className: '', style: { background: pinkMap[type] } };
    }
    const blueMap = { income: 'bg-[#E8F0E9]', expense: 'bg-blue-100', savings: 'bg-purple-100', withdrawal: 'bg-amber-100', debtpayment: 'bg-red-100', transfer: 'bg-indigo-100' };
    return { className: blueMap[type] || 'bg-gray-100', style: {} };
  };
  // Matching accent colour for each type's amount hero card (pink only — warm/blue
  // amount cards keep the plain grey they already used).
  const txTypeAccent = (type) => {
    if (plan) return '#26252E';
    if (purple) return '#5E35A8';
    if (!isPink) return isWarm ? '#5C4A3D' : '#4B5563';
    const pinkMap = { income: '#C23A72', expense: '#3C6693', savings: '#5C7A4A', withdrawal: '#93691E', debtpayment: '#B23B3B', transfer: '#7A5FB0' };
    return pinkMap[type] || '#4B5563';
  };
  const txTypeAccentSoft = (type) => {
    if (plan) return '#F7F5FB';
    if (purple) return '#F4EEFC';
    if (!isPink) return isWarm ? '#F0ECE6' : '#F3F4F6';
    const pinkMap = {
      income: 'linear-gradient(135deg, #FAE3EC 0%, #F5D5E3 100%)',
      expense: 'linear-gradient(135deg, #DBE7F5 0%, #C4D6ED 100%)',
      savings: 'linear-gradient(135deg, #E7F1DB 0%, #D3E5BE 100%)',
      withdrawal: 'linear-gradient(135deg, #F7E7C9 0%, #EEDAA8 100%)',
      debtpayment: 'linear-gradient(135deg, #F6D9D9 0%, #EEC3C3 100%)',
      transfer: 'linear-gradient(135deg, #E9E1F5 0%, #D5C7EB 100%)',
    };
    return pinkMap[type];
  };
  // The FAB's own colour/gradient, reused for every form's submit button so "Add Expense"
  // / "Add Income" / etc. all match the + button that opened the sheet.
  // Purple (admin-only): the submit buttons are solid lavender.
  const txCtaStyle = plan
    ? { background: '#1F1E24' }
    : purple
    ? { background: '#AC8DF2' }
    : isPink
    ? { background: 'linear-gradient(135deg, #F5A8C1 0%, #E27BA1 100%)' }
    : isWarm
      ? { background: '#2D5A3D' }
      : { background: '#4361EE' };
  const txEyebrowColor = plan ? '#8B879A' : isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE';
  // Maps the Edit Transaction sheet's own type/subtype fields onto the same
  // txType keys the Add Transaction forms use, so the edit sheet picks up the
  // identical icon/colour/emoji per type.
  const editTypeKey = editTransactionData.transactionType === 'expense' ? 'expense'
    : (editTransactionData.transactionType === 'income' || editTransactionData.transactionType === 'interest') ? 'income'
    : editTransactionData.transactionType === 'savings'
      ? (editTransactionData.savingsTransactionType === 'withdrawal' ? 'withdrawal' : 'savings')
      : 'expense';

  // Calculate next due date for a recurring transaction
  const getNextDueDate = (rec) => {
    if (!rec.start_date) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(rec.start_date + 'T00:00:00');
    startDate.setHours(0, 0, 0, 0);
    
    if (today < startDate) return startDate;
    
    if (rec.frequency === 'monthly') {
      const monthsDiff = (today.getFullYear() - startDate.getFullYear()) * 12 + (today.getMonth() - startDate.getMonth());
      let nextDue = new Date(startDate);
      nextDue.setMonth(startDate.getMonth() + monthsDiff);
      if (nextDue <= today) {
        nextDue.setMonth(nextDue.getMonth() + 1);
      }
      return nextDue;
    } else {
      let intervalDays;
      if (rec.frequency === 'weekly') intervalDays = 7;
      else if (rec.frequency === 'biweekly') intervalDays = 14;
      else if (rec.frequency === 'every_n_weeks') intervalDays = (rec.every_n_weeks || 2) * 7;
      else intervalDays = 7;
      
      const daysSinceStart = Math.floor((today - startDate) / (1000 * 60 * 60 * 24));
      const nextOccurrence = Math.ceil(daysSinceStart / intervalDays);
      const nextDue = new Date(startDate);
      nextDue.setDate(startDate.getDate() + (nextOccurrence * intervalDays));
      return nextDue;
    }
  };

  return (
    <div className="max-w-4xl mx-auto relative" onTouchMove={() => { if (editingTransaction || editSheetTxId) document.activeElement?.blur(); }}>
      {/* New Hero Card */}
      {showBudgetInfo && createPortal(
        <div className="fixed inset-0 z-50" style={{ touchAction: 'none' }}>
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowBudgetInfo(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 pb-10 shadow-xl">
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
            <h3 className="font-normal text-gray-800 mb-4" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '20px' }}>About the Budget Tab</h3>
            <div className="space-y-4">
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">🗓️</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Set up your budget</p><p className="text-xs text-gray-500 leading-relaxed">At the start of each month, tap Setup to build your monthly budget across your categories, debts, sinking funds and investment accounts.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">➕</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Add transactions</p><p className="text-xs text-gray-500 leading-relaxed">Log expenses, income, transfers, savings deposits, investment contributions, and debt payments. Entries here automatically update your Sinking Funds, Investments, and Net Worth tabs.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">💳</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Cards & transfers</p><p className="text-xs text-gray-500 leading-relaxed">Track spending across your payment methods and log transfers between them.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">📊</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Budget progress</p><p className="text-xs text-gray-500 leading-relaxed">See your spending progress by category in the budget progress section.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">🔄</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Recurring transactions</p><p className="text-xs text-gray-500 leading-relaxed">Set up recurring transactions in Settings. Toggle to see upcoming planned transactions — if notifications are on, you'll get a reminder 2 days before and a banner on the day to confirm.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">📝</span><div><p className="text-sm font-semibold text-gray-700 mb-0.5">Memos</p><p className="text-xs text-gray-500 leading-relaxed">Use Memos for anything you want to remember about this month's money, like why your income is less than normal, what you're moving to savings, or a bill that's different from usual.</p><p className="text-xs text-gray-500 leading-relaxed mt-2">Memos stay in the month you write them in. When you set up the next month, you can copy them over along with your budgets.</p></div></div>
            </div>
            <button onClick={() => setShowBudgetInfo(false)} className="mt-6 w-full py-3 rounded-2xl theme-bg-500 text-white font-semibold text-sm">Got it</button>
          </div>
        </div>,
        document.body
      )}
      <BudgetHero
        theme={theme}
        viewMonth={viewMonth}
        onPrevMonth={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
        onNextMonth={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
        totalIncome={totalIncome}
        totalSpent={totalSpent}
        totalSavings={totalSavings}
        monthlyBudget={monthlyBudget}
        budgetAvailable={budgetAvailable}
        cashAvailable={cashAvailable}
        cameIn={incomeForCashAvailable /* dark_07: income without interest */}
        carryIn={leftoverFromLastMonth}
        leftToBudget={leftToBudget}
        plannedWithdrawals={plannedWithdrawals}
        transactionCount={viewMonthTransactions.length}
        showYearlyStats={showYearlyStats}
        onToggleYearlyStats={() => setShowYearlyStats(!showYearlyStats)}
        onNotesClick={() => { setShowBudgetSetup(true); setShowYearlyStats(false); }}
        onStatsClick={() => { setShowYearlyStats(true); setShowBudgetSetup(false); }}
        onOverviewClick={() => { setShowBudgetSetup(false); setShowYearlyStats(false); }}
        budgetSpentPercent={budgetSpentPercent}
        spentExcludingExcluded={spentExcludingExcluded}
        budgetForSpentCalc={budgetForSpentCalc}
        onInfoClick={() => setShowBudgetInfo(true)}
        showAddMenu={showAddMenu}
        setShowAddMenu={setShowAddMenu}
        addFormType={addFormType}
        setAddFormType={setAddFormType}
        showBudgetSetup={showBudgetSetup}
        userDebts={userDebts}
        isWarm={isWarm}
        warmColors={warmColors}
        colorMap={colorMap}
        cards={cards}
      />

      <div className="px-4">
{/* Yearly Stats Sheet - Slide-up popup */}
{showYearlyStats && (
  <>
    {/* Tab pills — Totals / Breakdown / Reviews, round segmented control (matches Setup's Income/Budgets/Notes) */}
    <div className="flex justify-center mb-4">
      <div className={plan ? "flex w-full rounded-full p-1 wy-plan-seg" : "inline-flex rounded-full p-1"} style={{ background: chartPal.toggle.track }}>
        {[
          { id: 'totals', label: '\u{1F4C8} Totals' },
          { id: 'breakdown', label: '\u{1F4CA} Breakdown' },
          { id: 'reviews', label: '\u{1F4C5} Reviews' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setYearlyStatsView(t.id)}
            className="px-3.5 py-2 rounded-full text-[14px] whitespace-nowrap transition-all"
            style={yearlyStatsView === t.id
              ? { fontFamily: "'DM Sans', sans-serif", background: '#ffffff', color: chartPal.toggle.active, fontWeight: 600, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }
              : { fontFamily: "'DM Sans', sans-serif", color: chartPal.toggle.inactive, fontWeight: 500 }}
          >
            {plan ? t.label.replace(/^\S+\s/, '') : t.label}
          </button>
        ))}
      </div>
    </div>

    {/* Content */}
    <div className="pb-4">
        {(() => {
          // 2026 Stats -> Totals: each row is coloured by what it is, matching the
          // This Month tiles, so a colour means the same thing across the tab.
          // Order: 0 Income, 1 Spent, 2 Saved/Invested, 3 Debt Payments, 4 Interest.
          // iconBg = pale chip tint, label = solid shade for the label and amount.
          const statPalettes = {
            pink: [
              { iconBg: '#FAE3EC', label: '#C23A72' },
              { iconBg: '#DBE7F5', label: '#3C6693' },
              { iconBg: '#E7F1DB', label: '#5C7A4A' },
              { iconBg: '#FBE6DA', label: '#B8683F' },
              { iconBg: '#E9E1F5', label: '#7A5FB0' },
            ],

            warm: [
              { iconBg: '#F6E3DD', label: '#8E4F42' },
              { iconBg: '#F6E8D6', label: '#8A5E24' },
              { iconBg: '#E5EEDD', label: '#3F6A45' },
              { iconBg: '#EFE5EE', label: '#6E4F6A' },
              { iconBg: '#E1EBE4', label: '#2D5A3D' },
            ],

            blue: [
              { iconBg: '#E4EAFB', label: '#3A4DA8' },
              { iconBg: '#E0F0FA', label: '#2E6E96' },
              { iconBg: '#E0F2EB', label: '#2F7560' },
              { iconBg: '#FBE6EC', label: '#A8456A' },
              { iconBg: '#ECE6FA', label: '#6B4FB0' },
            ]
          };
          const tilePalette = plan ? Array(5).fill({ iconBg: '#F5F2FA', label: '#26252E' }) : statPalettes[isPink ? 'pink' : isWarm ? 'warm' : 'blue'];
          const rankBg = plan ? '#F5F2FA' : isPink ? '#FCE7F3' : isWarm ? '#EDE3D8' : '#E0E7FF';
          const rankText = plan ? '#8B879A' : isPink ? '#B42E76' : isWarm ? '#7C5C3A' : '#4338CA';

          // Helper for stacked stat row
          const StatRow = ({ paletteIdx, icon, label, value, hasTransactions, viewKey }) => plan ? (
            <button
              type="button"
              onClick={() => hasTransactions && setViewingYearlyTransactions(viewingYearlyTransactions === viewKey ? null : viewKey)}
              className="w-full flex items-center text-left wy-plan-statrow"
              style={{ gap: 12, padding: '11px 0', background: 'transparent' }}
            >
              <span className="flex items-center justify-center flex-shrink-0" style={{ width: 36, height: 36, borderRadius: 12, background: '#F5F2FA', fontSize: 16 }}>{icon}</span>
              <span className="flex-1 min-w-0">
                <span className="block" style={{ fontSize: 14.5, fontWeight: 600, color: '#26252E' }}>{label.replace(/^Total /, '').replace(' / ', ' & ').replace(/\b(Invested|Payments|Earned)\b/g, (w) => w.toLowerCase())}</span>
                {hasTransactions && <span className="block" style={{ fontSize: 12, color: '#8B879A' }}>{viewingYearlyTransactions === viewKey ? 'Hide transactions' : 'View transactions'}</span>}
              </span>
              <span className="flex-shrink-0" style={{ fontSize: 14.5, fontWeight: 600, color: '#26252E', textAlign: 'right' }}>{value}</span>
              {hasTransactions && <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#C1BCD0', transform: viewingYearlyTransactions === viewKey ? 'rotate(90deg)' : 'none' }} />}
            </button>
          ) : (
            <div className="flex items-center justify-between py-3" style={{ borderBottom: '1px solid #F3F4F6' }}>
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="rounded-lg flex items-center justify-center flex-shrink-0" style={{ width: 32, height: 32, background: tilePalette[paletteIdx].iconBg, fontSize: 15 }}>{icon}</div>
                <div className="min-w-0">
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tilePalette[paletteIdx].label }}>{label}</div>
                  {hasTransactions && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setViewingYearlyTransactions(viewingYearlyTransactions === viewKey ? null : viewKey); }}
                      className="text-xs hover:opacity-80 transition-opacity"
                      style={{ color: '#9C94A3', marginTop: 2, fontWeight: 500 }}
                    >
                      {viewingYearlyTransactions === viewKey ? 'Hide' : 'View'} Transactions
                    </button>
                  )}
                </div>
              </div>
              <p style={{ fontFamily: "'DM Serif Display', serif", fontSize: 20, fontWeight: 700, letterSpacing: '-0.4px', color: tilePalette[paletteIdx].label, textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>{value}</p>
            </div>
          );

          return (
            <div className="space-y-3">
              {yearlyStatsView === 'reviews' ? (
                <>
                {/* w2_18: your year(s) in review, above the months */}
                {plan && <YearReviewRow userData={userData} saveUserData={saveUserData} />}
                <MonthEndReviewList
                  year={currentYear}
                  transactions={transactions}
                  noSpendDays={noSpendDays}
                  sinkingFunds={allSinkingFunds}
                  categoryBudgets={categoryBudgets}
                  userData={userData}
                  saveUserData={saveUserData}
                  theme={theme}
                />
                </>
              ) : yearlyStatsView === 'totals' ? (
                <>
                  {/* plan_07: Money plan - Your 2026 so far */}
                  {plan && (
                    <div className="bg-white rounded-2xl" style={{ padding: '18px' }} data-testid="plan-year-hero">
                      <div className="flex justify-between" style={{ gap: 14 }}>
                        <div style={{ minWidth: 0 }}>
                          <p className="m-0" style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.4px', color: '#26252E' }}>Your {currentYear} so far</p>
                          <p className="m-0" style={{ fontSize: 13.5, lineHeight: 1.45, color: '#8B879A', marginTop: 4 }}>
                            {yearlyTotalIncome - yearlyTotalSpent >= 0
                              ? `You've kept ${formatCurrencyWhole(yearlyTotalIncome - yearlyTotalSpent)} more than you've spent this year!`
                              : `You've spent ${formatCurrencyWhole(yearlyTotalSpent - yearlyTotalIncome)} more than you've earned this year.`}
                          </p>
                        </div>
                        <span className="flex items-center justify-center flex-shrink-0" style={{ width: 42, height: 42, borderRadius: 999, background: '#DFF2E7', fontSize: 19 }}>{'\ud83d\udcc8'}</span>
                      </div>
                      <div className="grid grid-cols-2" style={{ gap: 10, marginTop: 14 }}>
                        <div style={{ background: '#DFF2E7', borderRadius: 20, padding: 12 }}>
                          <span style={{ display: 'block', fontSize: 12.5, color: '#55525F' }}>Income</span>
                          <span style={{ display: 'block', fontSize: 17.5, fontWeight: 700, letterSpacing: '-0.5px', color: '#2F8756', marginTop: 3 }}>{formatCurrencyWhole(yearlyTotalIncome)}</span>
                        </div>
                        <div style={{ background: '#DFE9F9', borderRadius: 20, padding: 12 }}>
                          <span style={{ display: 'block', fontSize: 12.5, color: '#55525F' }}>Spent</span>
                          <span style={{ display: 'block', fontSize: 17.5, fontWeight: 700, letterSpacing: '-0.5px', color: '#3C6DB5', marginTop: 3 }}>{formatCurrencyWhole(yearlyTotalSpent)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Card 1: This year - stacked rows */}
                  <div className="bg-white rounded-2xl shadow-sm p-4">
                    <p style={{ fontSize: 10, fontWeight: 700, color: '#9A9AA8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>This year</p>
                    <div>
                      <StatRow paletteIdx={0} icon="💰" label="Total Income" value={formatCurrency(yearlyTotalIncome)} hasTransactions={yearlyIncomeTransactions.length > 0} viewKey="income" />
                      <StatRow paletteIdx={1} icon="🛒" label="Total Spent" value={formatCurrency(yearlyTotalSpent)} hasTransactions={yearlyExpenseTransactions.length > 0} viewKey="spent" />
                      <StatRow paletteIdx={2} icon="🐷" label="Saved / Invested" value={formatCurrency(yearlyTotalSavings)} hasTransactions={yearlySavingsAll.length > 0} viewKey="saved" />
                      {yearlyTotalDebtPayments > 0 && (
                        <StatRow paletteIdx={3} icon="💳" label="Debt Payments" value={formatCurrency(yearlyTotalDebtPayments)} hasTransactions={yearlyDebtPayments.length > 0} viewKey="debt" />
                      )}
                      {yearlyTotalInterest > 0 && (
                        <StatRow paletteIdx={4} icon="✨" label="Interest Earned" value={formatCurrency(yearlyTotalInterest)} hasTransactions={yearlyInterestTransactions.length > 0} viewKey="interest" />
                      )}
                      {/* Strip last row's bottom border via inline style override on a sentinel */}
                    </div>
                  </div>

                  {/* Yearly Transactions List - shown right under "This year" so the user sees it without scrolling */}
                  {viewingYearlyTransactions && (
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                      {(() => {
                        const vk = viewingYearlyTransactions;
                        const yTx = (
                          vk === 'income' ? yearlyIncomeTransactions :
                          vk === 'saved' ? yearlySavingsAll :
                          vk === 'spent' ? yearlyExpenseTransactions :
                          vk === 'interest' ? yearlyInterestTransactions :
                          yearlyDebtPayments
                        ).slice().sort((a, b) => new Date(b.date) - new Date(a.date));

                        const heading =
                          vk === 'income' ? 'Income' :
                          vk === 'saved' ? 'Saved / Invested' :
                          vk === 'spent' ? 'Spending' :
                          vk === 'interest' ? 'Interest Earned' :
                          'Debt Payments';

                        return (
                          <>
                            <div className="px-4 pt-3.5 pb-2.5 flex items-baseline gap-2">
                              <span className="text-[15px] font-bold" style={{ color: '#1A1A1F' }}>{heading}</span>
                              <span className="flex-1 min-w-0 text-[11.5px] font-medium truncate" style={{ color: mutedInk }}>
                                {currentYear} · {yTx.length} transaction{yTx.length === 1 ? '' : 's'}
                              </span>
                              <button
                                onClick={() => setViewingYearlyTransactions(null)}
                                aria-label="Close"
                                className="flex-shrink-0"
                                style={{ color: '#B9B2BD' }}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>

                            <div className="max-h-72 overflow-y-auto">
                              {yTx.length === 0 ? (
                                <div className="px-4 pb-4 text-[13px] font-medium" style={{ color: mutedInk }}>
                                  No transactions in {currentYear}.
                                </div>
                              ) : yTx.map((t, i) => {
                                const isPlus =
                                  vk === 'income' || vk === 'interest' ||
                                  (vk === 'saved' && t.transaction_type !== 'withdrawal');
                                const color =
                                  vk === 'income' || vk === 'interest' ? moneyInk :
                                  vk === 'saved' ? (t.transaction_type === 'withdrawal' ? negInk : (isWarm ? '#5D6B4D' : '#8B5CF6')) :
                                  negInk;

                                const meta = [
                                  new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                                  vk === 'interest' && t.interest_account_id
                                    ? getAccountName(t.interest_account_id, t.interest_account_type)
                                    : t.payment_method
                                ].filter(Boolean).join(' · ');

                                return (
                                  <div
                                    key={t.id}
                                    className="flex items-center gap-2.5 px-4 py-2.5"
                                    style={i === 0 ? undefined : { borderTop: '1px solid #F6F3F7' }}
                                  >
                                    {vk === 'spent' && (
                                      <span
                                        className="flex items-center justify-center flex-shrink-0"
                                        style={{ width: '34px', height: '34px', borderRadius: '11px', background: rowTileBg, fontSize: '15px' }}
                                      >{txEmoji(t)}</span>
                                    )}
                                    <span className="flex-1 min-w-0">
                                      <span className="block text-[14px] font-semibold truncate" style={{ color: '#1F2937' }}>
                                        {t.description || t.category}
                                      </span>
                                      <span className="block text-[11.5px] font-medium truncate" style={{ color: mutedInk, marginTop: '1px' }}>
                                        {meta}
                                      </span>
                                    </span>
                                    <span className="text-[14px] font-bold flex-shrink-0" style={{ color }}>
                                      {isPlus ? '+' : '-'}{formatCurrency(t.amount)}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {/* Card 2: Payment methods */}
                  {yearlyCardTotals.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-sm p-4">
                      <p style={{ fontSize: 10, fontWeight: 700, color: '#9A9AA8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>By Payment Method</p>
                      <div>
                        {yearlyCardTotals.map((ct, idx) => {
                          const cardTransactions = yearlyExpenseTransactions.filter(t => t.payment_method === ct.card);
                          const cardColors = colorMap[ct.color] || colorMap.grey;
                          return (
                            <div key={ct.card} className="flex items-center justify-between py-3" style={{ borderBottom: idx < yearlyCardTotals.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <div className={`rounded-lg flex items-center justify-center flex-shrink-0 ${cardColors.bgLight}`} style={plan ? { width: 36, height: 36, fontSize: 16, borderRadius: 12 } : { width: 32, height: 32, fontSize: 15 }}>{ct.card === 'Cash' ? '💵' : '💳'}</div>
                                <div className="min-w-0">
                                  <div className={cardColors.text} style={plan ? { fontSize: 14.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : { fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ct.card}</div>
                                  {cardTransactions.length > 0 && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setViewingCardTransactions(viewingCardTransactions === ct.card ? null : ct.card); }}
                                      className="text-xs hover:opacity-80 transition-opacity"
                                      style={{ color: '#9C94A3', marginTop: 2, fontWeight: 500 }}
                                    >
                                      {viewingCardTransactions === ct.card ? 'Hide' : 'View'} {plan ? 'transactions' : 'Transactions'}
                                    </button>
                                  )}
                                </div>
                              </div>
                              <p className={cardColors.text} style={plan ? { fontSize: 14.5, fontWeight: 600, textAlign: 'right', flexShrink: 0, marginLeft: 12 } : { fontFamily: "'DM Serif Display', serif", fontSize: 20, fontWeight: 700, letterSpacing: '-0.4px', textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>{formatCurrency(ct.total)}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Card Transactions List (preserved) */}
                  {viewingCardTransactions && (
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                      {(() => {
                        const cardName = viewingCardTransactions;
                        const cardTx = transactions.filter(t => isInYear(t)).filter(t => {
                          if (t.payment_method === cardName && !t.is_income && !t.is_savings && !t.is_transfer) return true;
                          if (t.is_income && t.to_card === cardName) return true;
                          if (t.is_transfer && (t.payment_method === cardName || t.to_card === cardName)) return true;
                          if (t.is_savings && t.transaction_type === 'deposit' && t.from_card === cardName) return true;
                          if (t.is_savings && t.transaction_type === 'withdrawal' && t.to_card === cardName) return true;
                          return false;
                        }).sort((a, b) => new Date(b.date) - new Date(a.date));

                        return (
                          <>
                            <div className="px-4 pt-3.5 pb-2.5 flex items-baseline gap-2">
                              <span className="text-[15px] font-bold" style={{ color: '#1A1A1F' }}>{cardName}</span>
                              <span className="flex-1 min-w-0 text-[11.5px] font-medium truncate" style={{ color: mutedInk }}>
                                {currentYear} · {cardTx.length} transaction{cardTx.length === 1 ? '' : 's'}
                              </span>
                              <button
                                onClick={() => setViewingCardTransactions(null)}
                                aria-label="Close"
                                className="flex-shrink-0"
                                style={{ color: '#B9B2BD' }}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>

                            <div className="max-h-72 overflow-y-auto">
                              {cardTx.map((t, i) => {
                                let sign, color;
                                if (t.is_income && t.to_card === cardName) { sign = '+'; color = moneyInk; }
                                else if (t.is_savings && t.transaction_type === 'withdrawal' && t.to_card === cardName) { sign = '+'; color = moneyInk; }
                                else if (t.is_savings && t.transaction_type === 'deposit') { sign = '-'; color = isWarm ? '#5D6B4D' : '#8B5CF6'; }
                                else if (t.is_transfer && t.to_card === cardName) { sign = '+'; color = moneyInk; }
                                else { sign = '-'; color = negInk; }

                                return (
                                  <div
                                    key={t.id}
                                    className="flex items-center gap-2.5 px-4 py-2.5"
                                    style={i === 0 ? undefined : { borderTop: '1px solid #F6F3F7' }}
                                  >
                                    <span
                                      className="flex items-center justify-center flex-shrink-0"
                                      style={{ width: '34px', height: '34px', borderRadius: '11px', background: rowTileBg, fontSize: '15px' }}
                                    >{txEmoji(t)}</span>
                                    <span className="flex-1 min-w-0">
                                      <span className="block text-[14px] font-semibold truncate" style={{ color: '#1F2937' }}>
                                        {t.description || t.category}
                                      </span>
                                      <span className="block text-[11.5px] font-medium truncate" style={{ color: mutedInk, marginTop: '1px' }}>
                                        {new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                      </span>
                                    </span>
                                    <span className="text-[14px] font-bold flex-shrink-0" style={{ color }}>
                                      {sign}{formatCurrency(t.amount)}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </>
              ) : (
                /* % Breakdown view */
                <div className="space-y-3">
                  {/* Income Breakdown */}
                  {(() => {
                    if (yearlyTotalIncome === 0) {
                      return (
                        <div className="bg-white rounded-2xl shadow-sm p-4">
                          <p style={{ fontSize: 10, fontWeight: 700, color: '#9A9AA8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 14 }}>Income Breakdown</p>
                          <div className="text-center py-6 text-gray-400">
                            <PieChart className="w-10 h-10 mx-auto mb-2 opacity-30" />
                            <p className="text-sm">No income recorded this year</p>
                          </div>
                        </div>
                      );
                    }
                    const unallocatedAmount = Math.max(0, yearlyTotalIncome - yearlyTotalSpent - yearlyTotalSavings - yearlyTotalDebtPayments);
                    const expenseColor = chartPal.income.expenses;
                    const savingsColor = chartPal.income.savings;
                    const debtColor = chartPal.income.debt;
                    const unallocatedColor = chartPal.income.unallocated;
                    const segments = [];
                    if (yearlyTotalSpent > 0) segments.push({ value: yearlyTotalSpent, color: expenseColor, label: 'Expenses' });
                    if (yearlyTotalSavings > 0) segments.push({ value: yearlyTotalSavings, color: savingsColor, label: 'Savings & Investments' });
                    if (yearlyTotalDebtPayments > 0) segments.push({ value: yearlyTotalDebtPayments, color: debtColor, label: 'Debt' });
                    if (unallocatedAmount > 0) segments.push({ value: unallocatedAmount, color: unallocatedColor, label: 'Unallocated' });
                    if (segments.length === 0) {
                      return (
                        <div className="bg-white rounded-2xl shadow-sm p-4">
                          <p style={{ fontSize: 10, fontWeight: 700, color: '#9A9AA8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 14 }}>Income Breakdown</p>
                          <div className="text-center py-6 text-gray-400">
                            <PieChart className="w-10 h-10 mx-auto mb-2 opacity-30" />
                            <p className="text-sm">No data to display</p>
                          </div>
                        </div>
                      );
                    }
                    const total = yearlyTotalIncome;
                    let currentPercent = 0;
                    const gradientStops = [];
                    const segmentsWithPercent = segments.map(seg => {
                      const percent = (seg.value / total) * 100;
                      gradientStops.push(chartStop(seg.color, currentPercent, percent, segments.length > 1));
                      currentPercent += percent;
                      return { ...seg, percent };
                    });
                    const conicGradient = `conic-gradient(from 0deg, ${gradientStops.join(', ')})`;
                    return (
                      <div className="bg-white rounded-2xl shadow-sm p-4">
                        <p style={{ fontSize: 10, fontWeight: 700, color: '#9A9AA8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 14 }}>Income Breakdown</p>
                        <div className="flex items-center gap-4">
                          <div className="relative flex-shrink-0" style={{ width: 120, height: 120 }}>
                            <div
                              style={{
                                width: 120, height: 120, borderRadius: '50%',
                                background: conicGradient,
                                mask: 'radial-gradient(circle, transparent 38px, black 39px)',
                                WebkitMask: 'radial-gradient(circle, transparent 38px, black 39px)'
                              }}
                            />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="text-center">
                                <p className="text-base font-bold text-gray-800">{formatCurrencyShort(total)}</p>
                                <p className="text-xs" style={{ color: chartPal.centre }}>Income</p>
                              </div>
                            </div>
                          </div>
                          <div className="flex-1 space-y-2 min-w-0">
                            {segmentsWithPercent.map((seg, i) => (
                              <div key={i}>
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: seg.color }} />
                                    <span className="text-xs font-medium text-gray-700 truncate">{seg.label}</span>
                                  </div>
                                  <span className="text-xs font-bold flex-shrink-0" style={{ color: chartTextColor(chartPal, seg.color) }}>{seg.percent.toFixed(0)}%</span>
                                </div>
                                <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                                  <div className="h-full rounded-full transition-all" style={{ width: `${seg.percent}%`, backgroundColor: seg.color }} />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Expenses Breakdown */}
                  {(() => {
                    if (yearlyTotalSpent === 0) {
                      return (
                        <div className="bg-white rounded-2xl shadow-sm p-4">
                          <p style={{ fontSize: 10, fontWeight: 700, color: '#9A9AA8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 14 }}>Expenses Breakdown</p>
                          <div className="text-center py-6 text-gray-400">
                            <PieChart className="w-10 h-10 mx-auto mb-2 opacity-30" />
                            <p className="text-sm">No expenses recorded this year</p>
                          </div>
                        </div>
                      );
                    }
                    const categoryTotals = {};
                    yearlyExpenseTransactions.forEach(t => {
                      const cat = t.category || 'Other';
                      categoryTotals[cat] = (categoryTotals[cat] || 0) + t.amount;
                    });
                    const sortedCategories = Object.entries(categoryTotals).sort((a, b) => b[1] - a[1]);
                    const total = sortedCategories.reduce((sum, [_, val]) => sum + val, 0);
                    const categoryColors = chartPal.cats;
                    const segments = sortedCategories.map(([cat, val], i) => ({
                      value: val, color: categoryColors[i % categoryColors.length], label: cat
                    }));
                    let currentPercent = 0;
                    const gradientStops = [];
                    const segmentsWithPercent = segments.map(seg => {
                      const percent = (seg.value / total) * 100;
                      gradientStops.push(chartStop(seg.color, currentPercent, percent, segments.length > 1));
                      currentPercent += percent;
                      return { ...seg, percent };
                    });
                    const conicGradient = `conic-gradient(from 0deg, ${gradientStops.join(', ')})`;
                    return (
                      <div className="bg-white rounded-2xl shadow-sm p-4">
                        <p style={{ fontSize: 10, fontWeight: 700, color: '#9A9AA8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 14 }}>Expenses Breakdown</p>
                        <div className="flex items-center gap-4">
                          <div className="relative flex-shrink-0" style={{ width: 120, height: 120 }}>
                            <div
                              style={{
                                width: 120, height: 120, borderRadius: '50%',
                                background: conicGradient,
                                mask: 'radial-gradient(circle, transparent 38px, black 39px)',
                                WebkitMask: 'radial-gradient(circle, transparent 38px, black 39px)'
                              }}
                            />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="text-center">
                                <p className="text-base font-bold text-gray-800">{formatCurrencyShort(total)}</p>
                                <p className="text-xs" style={{ color: chartPal.centre }}>Spent</p>
                              </div>
                            </div>
                          </div>
                          <div className="flex-1 space-y-2 min-w-0">
                            {(showAllYearlyCategories ? segmentsWithPercent : segmentsWithPercent.slice(0, 4)).map((seg, i) => (
                              <div key={i}>
                                <div className="flex items-center justify-between mb-0.5">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: seg.color }} />
                                    <span className="text-xs font-medium text-gray-700 truncate">{seg.label}</span>
                                  </div>
                                  <span className="text-xs font-bold flex-shrink-0" style={{ color: chartTextColor(chartPal, seg.color) }}>{seg.percent.toFixed(0)}%</span>
                                </div>
                                <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                                  <div className="h-full rounded-full transition-all" style={{ width: `${seg.percent}%`, backgroundColor: seg.color }} />
                                </div>
                              </div>
                            ))}
                            {segmentsWithPercent.length > 4 && (
                              <button
                                onClick={() => setShowAllYearlyCategories(!showAllYearlyCategories)}
                                className="text-xs font-medium pt-1 theme-text-500"
                                data-testid="toggle-yearly-categories"
                              >
                                {showAllYearlyCategories ? 'Show less' : `+${segmentsWithPercent.length - 4} more categories`}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Spending by Category (themed card with rows) - not on the Month-end reviews pill */}
              <div className={`bg-white rounded-2xl shadow-sm p-4${yearlyStatsView === 'reviews' ? ' hidden' : ''}`}>
                <p style={{ fontSize: 10, fontWeight: 700, color: '#9A9AA8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>Spending by Category</p>
                {yearlyCategoriesWithSpending.length > 0 ? (
                  <div>
                    {yearlyCategoriesWithSpending.map((cat, idx) => {
                      const categoryTransactions = yearlyExpenseTransactions.filter(t => t.category === cat.name);
                      const { emoji, name } = parseCategoryEmoji(cat.name || '');
                      const displayName = name || cat.name;
                      const isLast = idx === yearlyCategoriesWithSpending.length - 1;
                      const isOpen = viewingCategoryTransactions === cat.name;
                      return (
                        <div key={cat.name}>
                          <div
                            className="flex items-center justify-between py-3 cursor-pointer"
                            style={{ borderBottom: !isLast || isOpen ? '1px solid #F3F4F6' : 'none' }}
                            onClick={() => categoryTransactions.length > 0 && setViewingCategoryTransactions(isOpen ? null : cat.name)}
                          >
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <span className="rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ width: 22, height: 22, background: rankBg, color: rankText }}>
                                {idx + 1}
                              </span>
                              {emoji && <span style={{ fontSize: 17, flexShrink: 0 }}>{emoji}</span>}
                              <span className="text-gray-800 truncate" style={{ fontSize: 14, fontWeight: 500 }}>{displayName}</span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                              <span style={{ fontSize: 14, fontWeight: 700, color: '#1A1A1F', letterSpacing: '-0.3px' }}>{formatCurrency(cat.spent)}</span>
                              {categoryTransactions.length > 0 && (
                                <span style={{ color: '#C4B5C8', fontSize: 18, lineHeight: 1, fontWeight: 300, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>›</span>
                              )}
                            </div>
                          </div>
                          {isOpen && (
                            <div className="ml-9 mb-2 rounded-2xl overflow-hidden" style={{ marginTop: 4, background: rowTileBg }}>
                              <div className="max-h-48 overflow-y-auto">
                                {categoryTransactions
                                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                                  .map((t, i) => (
                                    <div
                                      key={t.id}
                                      className="flex items-center gap-2.5 px-3 py-2.5"
                                      style={i === 0 ? undefined : { borderTop: '1px solid rgba(255,255,255,0.85)' }}
                                    >
                                      <span
                                        className="flex items-center justify-center flex-shrink-0"
                                        style={{ width: '30px', height: '30px', borderRadius: '10px', background: '#FFFFFF', fontSize: '14px' }}
                                      >{txEmoji(t)}</span>
                                      <span className="flex-1 min-w-0">
                                        <span className="block text-[13.5px] font-semibold truncate" style={{ color: '#1F2937' }}>
                                          {t.description || displayName}
                                        </span>
                                        <span className="block text-[11px] font-medium truncate" style={{ color: mutedInk, marginTop: '1px' }}>
                                          {new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                          {t.payment_method ? ` · ${t.payment_method}` : ''}
                                        </span>
                                      </span>
                                      <span className="text-[13.5px] font-bold flex-shrink-0" style={{ color: negInk }}>
                                        -{formatCurrency(t.amount)}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-400 text-center py-4 text-sm">No transactions this year</p>
                )}
              </div>
            </div>
          );
        })()}
      </div>
  </>
)}

{/* Budget Setup Sheet - Slide-up popup */}
{showBudgetSetup && (
  <div className="pb-24">
    <MonthlyBudgetSetup
      only="notes"
      theme={theme}
      onClose={() => setShowBudgetSetup(false)}
      viewMonthKey={viewMonthKey}
      viewMonth={viewMonth}
      budgetSettings={budgetSettings}
      saveUserData={saveUserData}
      cards={paymentMethods}
      colorMap={colorMap}
      categoriesWithActivity={categoriesWithActivity}
      categoryBudgets={categoryBudgets}
      availableSinkingFunds={availableSinkingFunds}
      availableInvestmentAccounts={availableInvestmentAccounts}
      userDebts={userDebts}
    />
  </div>
)}
{!showBudgetSetup && !showYearlyStats && (
<>
  {/* Your Budgets — the month's categories, savings and debts */}
  {!plan && <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mb-2" style={{ color: isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE', marginTop: '4px' }}><span className="wy-star">✦ </span>{'Your budgets'}</p>}
  <div className={plan ? "bg-white rounded-2xl overflow-hidden wy-plan-card" : "bg-white rounded-2xl border theme-border-100 shadow-sm overflow-hidden"}>
    {/* Header — how much of this month's money is assigned, and what is
        still unassigned; tap it to open the card. This replaces both the
        "Budgeting $X" row that used to sit above the card and the
        spent/saved/debt bars that used to sit inside its header; the same
        split is already on screen in the Breakdown section below.

        leftToBudget is income (+ planned withdrawals) minus every budget.
        Money carried over from last month is deliberately NOT in it — that
        sets a card's starting balance, it isn't yours to assign. */}
    {(() => {
      const toBudget = monthlyBudget + plannedWithdrawals;
      const allocated = totalCategoryBudgets + positiveSavingsBudgets + totalDebtBudgets;
      const over = leftToBudget < 0;
      // plan_07: Money plan - heading inside the card, two soft boxes.
      if (plan) {
        return (
          <button
            type="button"
            onClick={() => setShowBudgetProgress(v => !v)}
            className="w-full text-left"
            style={{ padding: showBudgetProgress ? '18px 18px 6px' : '18px', background: 'transparent', display: 'block' }} /* plan_08 */
            aria-expanded={showBudgetProgress}
            data-testid="budgets-readout"
          >
            <span className="flex items-center justify-between" style={{ gap: 12 }}>
              <span style={{ fontSize: 16.5, fontWeight: 700, letterSpacing: '-0.5px', color: '#26252E' }}>Your budgets {'\u{1F3AF}'}</span>
              <span className="flex items-center justify-center flex-shrink-0" style={{ width: 30, height: 30, borderRadius: 999, background: '#F5F2FA' }}>
                <ChevronDown className={`w-4 h-4 transition-transform ${showBudgetProgress ? 'rotate-180' : ''}`} style={{ color: '#8B879A' }} />
              </span>
            </span>
            {/* w2_12: back to the calm grey boxes */}
            <span className="grid grid-cols-2" style={{ gap: 10, marginTop: 12 }}>
              <span style={{ display: 'block', background: '#F4F1FB' /* dark_08 */, borderRadius: 16, padding: '11px 13px' }}>
                <span style={{ display: 'block', fontSize: 12, color: '#8B879A' }}>Budgeted</span>
                <span style={{ display: 'block', fontSize: 16.5, fontWeight: 600, color: '#26252E', marginTop: 1, wordBreak: 'break-word' }}>{formatCurrencyWhole(allocated)}</span>
              </span>
              <span style={{ display: 'block', background: '#F4F1FB' /* dark_08 */, borderRadius: 16, padding: '11px 13px' }}>
                <span style={{ display: 'block', fontSize: 12, color: '#8B879A' }}>{toBudget <= 0 ? 'No income set' : over ? 'Over-budgeted' : 'Left to budget'}</span>
                <span style={{ display: 'block', fontSize: 16.5, fontWeight: 600, color: over ? '#C04B7C' : '#7A5DD3', marginTop: 1, wordBreak: 'break-word' }}>
                  {toBudget <= 0 ? '—' : formatCurrencyWhole(over ? Math.abs(leftToBudget) : leftToBudget)}
                </span>
              </span>
            </span>
          </button>
        );
      }
      return (
        <button
          type="button"
          onClick={() => setShowBudgetProgress(v => !v)}
          className={`w-full flex items-center py-3.5 text-left relative${showBudgetProgress ? ' border-b theme-border-100' : ''}`}
          aria-expanded={showBudgetProgress}
          data-testid="budgets-readout"
        >
          {/* Both columns carry the same 34px right padding and the chevron
              is taken out of the flow, so each column is exactly half the
              card and this divider lands on the same x as Available's.
              Measured: both at 194.5px on a 390pt screen. */}
          <span className="flex-1 min-w-0 flex items-center gap-2" style={{ padding: '0 34px 0 14px' }}>
            <span
              className="rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ width: '36px', height: '36px', background: budgetTileBg, fontSize: '16px' }}
            >📋</span>
            <span className="min-w-0">
              <span className="block text-[12.5px]" style={{ fontWeight: 500, color: '#1F2937', lineHeight: 1.3, whiteSpace: 'nowrap' }}>Budgeted</span>
              <span className="block text-[15px] font-bold" style={{ color: budgetInk, lineHeight: 1.2, letterSpacing: '-0.2px', wordBreak: 'break-word' }}>
                {formatCurrency(allocated)}
              </span>
            </span>
          </span>

          <span style={{ width: '1px', height: '40px', background: '#F0EDF2', flexShrink: 0 }} />

          <span className="flex-1 min-w-0 flex items-center gap-2" style={{ padding: '0 34px 0 14px' }}>
            <span
              className="rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ width: '36px', height: '36px', background: moneyTileBg, fontSize: '16px' }}
            >📥</span>
            <span className="min-w-0">
              <span className="block text-[12.5px]" style={{ fontWeight: 500, color: '#1F2937', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                {toBudget <= 0 ? 'No income set' : over ? 'Over-budgeted' : 'Left to budget'}
              </span>
              <span
                className="block text-[15px] font-bold"
                style={{ color: over ? '#EF4444' : toBudgetInk, lineHeight: 1.2, letterSpacing: '-0.2px', wordBreak: 'break-word' }}
              >
                {toBudget <= 0
                  ? '\u2014'
                  : formatCurrencyExact(over ? Math.abs(leftToBudget) : leftToBudget)}
              </span>
            </span>
          </span>

          <span
            className={`w-7 h-7 rounded-full flex items-center justify-center ${
              isPink ? 'bg-pink-50' : isWarm ? 'bg-gray-100' : 'bg-indigo-50'
            }`}
            style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}
          >
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showBudgetProgress ? 'rotate-180' : ''}`} />
          </span>
        </button>
      );
    })()}

    {showBudgetProgress && (
      <div className={plan ? "px-[18px] pb-2 wy-plan-budgets-body" : "px-4 pb-4"}>
        {/* Spending / Savings / Debts — short pills, with one summary of the
            selected group underneath. The pills carry no figures, so the
            summary can say what the numbers ARE rather than leaving you to
            guess whether they are planned or done.

            Savings counts withdrawals: a month where $2,000 went in and
            $1,000 came out did not save $2,000, and the same is true of the
            plan, so both sides are net. */}
        {(() => {
          const spentInCategories = categories.reduce((sum, c) => sum + (spendingByCategory[c] || 0), 0);
          const savedNet = totalSavingsDeposits - totalSavingsWithdrawals;
          const plannedSavingsNet = positiveSavingsBudgets - plannedWithdrawals;
          const debtPaid = viewMonthTransactions
            .filter(t => t.is_debt_payment)
            .reduce((sum, t) => sum + t.amount, 0);

          const tabs = [
            {
              id: 'spending', label: 'Spending',
              done: spentInCategories, planned: totalCategoryBudgets,
              verb: 'spent', of: `of ${formatCurrencyWhole(totalCategoryBudgets)} budgeted`,
              // Each group's colour is exactly what its own rows use below,
              // so the summary and the bars under it never disagree.
              color: plan ? '#B9A6F0' : purple ? '#C9B8EE' : isPink ? chartPal.income.expenses : chartPal.centre,
            },
            {
              id: 'savings', label: 'Savings',
              done: savedNet, planned: plannedSavingsNet,
              verb: savedNet < 0 ? 'net out of savings' : 'net into savings',
              of: `of ${formatCurrencyWhole(Math.abs(plannedSavingsNet))} planned`,
              color: plan ? '#9ED9B6' : purple ? '#C9B8EE' : isWarm ? '#2D5A3D' : isPink ? '#9CC48E' : '#8b5cf6',
            },
            {
              id: 'debts', label: 'Debts',
              done: debtPaid, planned: totalDebtBudgets,
              verb: 'paid off',
              of: `of ${formatCurrencyWhole(totalDebtBudgets)} planned`,
              color: plan ? '#A9C6F0' : purple ? '#C9B8EE' : isWarm ? '#2D5A3D' : isPink ? '#8EAEE3' : '#f97316',
            },
          ];

          const active = tabs.find(t => t.id === budgetProgressView) || tabs[0];
          const target = Math.abs(active.planned);
          const pct = target > 0 ? Math.min(100, Math.max(0, (Math.abs(active.done) / target) * 100)) : 0;
          // The selected tab's label stays ink in every theme. It used to take
          // the theme accent, which is pink - and a pink "Debts" sitting on a
          // blue summary box fights it.
          const accent = '#1A1A1F';
          const groupColor = active.color;
          // A readable shade of the same hue for the figure, from the palette's
          // own text map; falls back to the colour itself where there is no
          // darker shade defined.
          const figureInk = chartTextColor(chartPal, groupColor);
          // The wash is that colour mixed into white, so the box is always the
          // same hue as its ring in every theme without adding new constants.
          const softTint = (hex, a) => {
            const h = hex.replace('#', '');
            const n = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
            const mix = v => Math.round(255 + (v - 255) * a);
            return `rgb(${mix((n >> 16) & 255)}, ${mix((n >> 8) & 255)}, ${mix(n & 255)})`;
          };
          const wash = plan ? '#F7F5FB' : softTint(groupColor, 0.16);

          return (
            <>
              <div className="flex gap-1 rounded-full mt-3" style={{ background: plan ? '#ECE8F4' : rowTileBg, padding: '4px' }}>
                {tabs.map(t => {
                  const on = budgetProgressView === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setBudgetProgressView(t.id)}
                      aria-pressed={on}
                      className="flex-1 rounded-full transition-colors"
                      style={{
                        padding: '9px 2px',
                        fontSize: '13.5px',
                        fontWeight: on ? 700 : 600,
                        color: on ? accent : mutedInk,
                        background: on ? '#FFFFFF' : 'transparent',
                        boxShadow: on ? '0 1px 3px rgba(31,41,55,0.10)' : 'none',
                      }}
                      data-testid={`budget-view-${t.id}`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>

              <div
                className="rounded-2xl mt-3 mb-3.5"
                style={{ background: wash, padding: '13px 14px' }}
                data-testid="budget-view-summary"
              >
                <div className="flex items-center gap-3">
                  <span
                    style={{ fontSize: '25px', fontWeight: 700, letterSpacing: '-0.6px', lineHeight: 1.05, color: figureInk }}
                  >
                    {formatCurrencyWhole(Math.abs(active.done))}
                  </span>
                  <span className="flex-1 min-w-0 text-[11.5px] font-medium" style={{ color: mutedInk, lineHeight: '15px' }}>
                    {active.verb}
                    <br />
                    {active.of}
                  </span>
                  {/* The ring is the only progress indicator here now. The bar
                      under it said the same thing twice, and with the box
                      carrying the group's colour the ring reads on its own. */}
                  <span
                    className="flex-shrink-0"
                    style={{
                      position: 'relative', width: '40px', height: '40px', borderRadius: '999px',
                      background: `conic-gradient(${groupColor} ${pct}%, #FFFFFF ${pct}% 100%)`,
                    }}
                    aria-hidden="true"
                  >
                    <span style={{ position: 'absolute', inset: '6px', borderRadius: '999px', background: wash, display: 'block' }} />
                  </span>
                </div>
              </div>
            </>
          );
        })()}
        
        {/* Spending Categories Progress - New Design */}
        {budgetProgressView === 'spending' && (
          <div>
            {(() => {
              // Pink takes the breakdown donut's expenses tone, so a category
              // bar and its slice in September Breakdown match. Warm and blue
              // take the palette's centre tone instead: their slice tones are
              // light enough to vanish into the track (1.98:1 and 2.03:1),
              // where centre gives 6.63:1 and 4.14:1 in the same hue family.
              const standardBarColor = plan ? '#B9A6F0' : purple ? '#C9B8EE' : isPink ? chartPal.income.expenses : chartPal.centre;
              const trackColor = chartPal.toggle.track;
              const rows = categoriesWithActivity.filter(c => c.budget > 0 || c.spent > 0);

              return rows
                .map(({ name: cat, spent, budget }, index) => {
                  // Spending in a category the user never budgeted for is an
                  // overspend too - every dollar of it is unplanned.
                  const isUnplannedSpend = budget <= 0 && spent > 0;
                  const isOverBudget = (budget > 0 && spent > budget) || isUnplannedSpend;
                  const progressPercent = budget > 0 ? Math.min((spent / budget) * 100, 100) : (isUnplannedSpend ? 100 : 0);
                  const barColor = isOverBudget ? (plan ? '#F2AFC8' : '#ef4444') : standardBarColor;
                  // Categories are stored with their emoji in the name, but
                  // not everyone sets one. Falling back to the initial keeps
                  // every row the same shape, and unlike a repeated generic
                  // icon it still tells the rows apart.
                  const { emoji, name: catLabel } = parseCategoryEmoji(cat || '');

                  return (
                    <div
                      key={cat}
                      className="flex items-center gap-2.5 py-3"
                      style={index === rows.length - 1 ? undefined : { borderBottom: '1px solid #F5F2F7' }}
                    >
                      <span
                        className="flex items-center justify-center flex-shrink-0"
                        style={{
                          width: '38px', height: '38px', borderRadius: '12px', background: rowTileBg,
                          fontSize: emoji ? '17px' : '15px', fontWeight: emoji ? 400 : 700,
                          color: emoji ? undefined : mutedInk,
                        }}
                      >
                        {emoji || (catLabel || cat || '?').trim().charAt(0).toUpperCase()}
                      </span>

                      <span className="flex-1 min-w-0">
                        <span className="flex items-baseline gap-2">
                          <span className="flex-1 min-w-0 truncate text-[15px] font-semibold" style={{ color: '#1F2937' }}>
                            {catLabel || cat}
                          </span>
                          {isOverBudget ? (
                            <span className="text-[14.5px] font-bold flex-shrink-0" style={{ color: plan ? '#C04B7C' : '#EF4444' }}>
                              {formatCurrencyExact(spent - budget)} {isUnplannedSpend ? 'unplanned' : 'over'}
                            </span>
                          ) : (
                            <span className="text-[14.5px] font-bold flex-shrink-0" style={{ color: '#1F2937' }}>
                              {formatCurrencyExact(budget - spent)} left
                            </span>
                          )}
                        </span>

                        <span className="block rounded-full overflow-hidden" style={{ height: plan ? '6px' : '5px', background: plan ? '#F0EDF6' : trackColor, margin: '7px 0 6px 0' }}>
                          <span
                            className="block rounded-full transition-all"
                            style={{ width: `${Math.min(progressPercent, 100)}%`, height: plan ? '6px' : '5px', background: barColor }}
                          />
                        </span>

                        <span className="block text-[11.5px] font-medium" style={{ color: mutedInk }}>
                          {isUnplannedSpend
                            ? `${formatCurrencyWhole(spent)} spent, no budget set`
                            : `${formatCurrencyWhole(spent)} spent of ${formatCurrencyWhole(budget)}`}
                        </span>
                      </span>

                      <button
                        type="button"
                        aria-label={`Edit budget for ${catLabel || cat}`}
                        className="w-8 h-8 flex items-center justify-center flex-shrink-0 opacity-70 active:opacity-100 transition-opacity self-center"
                        onClick={(e) => { e.stopPropagation(); setBudgetPopup({ label: cat, value: (monthlyCategoryBudgets[cat] || '').toString(), onSave: (val) => { const v = parseFloat(val) || 0; saveUserData({ category_budgets_by_month: { ...categoryBudgets, [viewMonthKey]: { ...monthlyCategoryBudgets, [cat]: v } } }); } }); }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={faintInk} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 20h4L19.5 8.5a2.12 2.12 0 0 0-3-3L5 17z"></path><path d="M14.5 6.5 17.5 9.5"></path></svg>
                      </button>
                    </div>
                  );
                });
            })()}
            {categoriesWithActivity.filter(c => c.budget > 0 || c.spent > 0).length === 0 && (
              <p className="text-sm text-gray-400 italic py-2">Nothing budgeted or spent yet this month</p>
            )}

            {/* The list above only shows categories with a budget or some
                spending, which leaves no way to budget a category that has
                neither - the one thing the Setup tab could still do that
                this card could not. This is that way. */}
            {categories.filter(c => !(monthlyCategoryBudgets[c] > 0) && !(spendingByCategory[c] > 0)).length > 0 && (
              <button
                type="button"
                onClick={() => setPickCategory(true)}
                className="w-full flex items-center gap-2.5 pt-3.5 pb-0.5 text-left"
                data-testid="add-category-budget"
              >
                <span
                  className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-[15px] font-bold"
                  style={{ background: isPink ? '#F3ECF7' : isWarm ? '#F0EAE0' : '#EEF1FF', color: isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE' }}
                >+</span>
                <span className="text-[14px] font-medium" style={{ color: mutedInk }}>
                  {totalCategoryBudgets > 0 ? 'Budget another category' : 'Budget a category'}
                </span>
              </button>
            )}
          </div>
        )}
        
        {/* Savings Progress - New Design */}
        {budgetProgressView === 'savings' && (
          <div className="space-y-4">
            {/* Interest Earned This Month */}
            {totalInterestEarned > 0 && (
              <div className={`rounded-xl p-3 border ${isWarm ? 'bg-[#f0f7f0] border-[#c4dfc4]' : 'bg-[#EEF5E9] border-[#D3E5BE]'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isWarm ? 'bg-[#2D5A3D]/10' : 'bg-[#C8DDB8]'}`}>
                      <TrendingUp className={`w-3.5 h-3.5 ${isWarm ? 'text-[#2D5A3D]' : 'text-[#2F6B44]'}`} />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-600">Interest Earned This Month</p>
                      <p className="text-[10px] text-gray-400">Paid directly to your accounts</p>
                    </div>
                  </div>
                  <span className={`text-sm font-bold ${isWarm ? 'text-[#2D5A3D]' : 'text-[#2F6B44]'}`}>
                    +{formatCurrency(totalInterestEarned)}
                  </span>
                </div>
                {interestTransactions.length > 0 && (
                  <button
                    onClick={() => setShowInterestDetails(!showInterestDetails)}
                    className="text-xs flex items-center gap-1 mt-2 hover:opacity-80 transition-opacity"
                    style={{ color: accentColors.primary }}
                  >
                      {showInterestDetails ? 'Hide' : 'View'} Transactions
                  </button>
                )}
                {showInterestDetails && interestTransactions.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-dashed border-gray-200 space-y-1.5">
                    {interestTransactions.map(t => (
                      <div key={t.id} className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">{t.description || getAccountName(t.interest_account_id, t.interest_account_type)}</span>
                        <span className={`font-medium ${isWarm ? 'text-[#2D5A3D]' : 'text-[#2F6B44]'}`}>+{formatCurrency(t.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* Sinking Funds */}
            {availableSinkingFunds.map((fund, index) => {
              const savingsBudget = monthlySavingsBudgets[fund.id] || 0;
              const isPlannedWithdrawal = savingsBudget < 0;
              const deposited = savingsTransactions
                .filter(t => t.account_id === fund.id && t.account_type === 'fund' && t.transaction_type === 'deposit')
                .reduce((sum, t) => sum + t.amount, 0);
              const withdrawn = savingsTransactions
                .filter(t => t.account_id === fund.id && t.account_type === 'fund' && t.transaction_type === 'withdrawal')
                .reduce((sum, t) => sum + t.amount, 0);
              const netMoved = deposited - withdrawn;
              // Progress uses the net, matching the "net into savings" summary above.
              const netToward = isPlannedWithdrawal ? -netMoved : netMoved;
              const progressPercent = savingsBudget !== 0
                ? Math.min((Math.max(netToward, 0) / Math.abs(savingsBudget)) * 100, 100)
                : 0;
              const displayPercent = Math.round(progressPercent);
              const isComplete = savingsBudget !== 0 ? netToward >= Math.abs(savingsBudget) : true;
              const barColor = purple ? '#C9B8EE' : isWarm ? '#2D5A3D' : isPink ? '#9CC48E' : (isPlannedWithdrawal ? '#f59e0b' : '#8b5cf6');
              if (plan) {
                const planned = Math.abs(savingsBudget);
                const moved = Math.max(netToward, 0);
                return planSlimRow({
                  key: fund.id,
                  emoji: fund.emoji || '\ud83d\udc37',
                  name: fund.name + (isPlannedWithdrawal ? ' (withdraw)' : ''),
                  right: planned === 0 ? signedWhole(netMoved) : isComplete ? 'Done \u2713' : `${formatCurrencyWhole(planned - moved)} to go`,
                  pct: progressPercent,
                  small: planned === 0 ? 'No plan set this month' : `${signedWhole(netMoved)} of ${isPlannedWithdrawal ? '-' : ''}${formatCurrencyWhole(planned)} ${isPlannedWithdrawal ? 'taken out' : 'in'}`,
                  color: '#9ED9B6',
                  onEdit: (e) => { e.stopPropagation(); setBudgetPopup({ label: fund.name, value: (savingsBudget !== 0 ? savingsBudget.toString() : ''), hint: 'Use negative for planned withdrawal e.g. -100', allowNegative: true, onSave: (val) => { const v = parseFloat(val) || 0; saveUserData({ budget_settings: { ...budgetSettings, savings_budgets_by_month: { ...(budgetSettings.savings_budgets_by_month || {}), [viewMonthKey]: { ...monthlySavingsBudgets, [fund.id]: v } } } }); } }); },
                  editLabel: `Edit budget for ${fund.name}`,
                });
              }
              
              return (
                <div key={fund.id} className="py-2">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-base flex-shrink-0">{fund.emoji || '💰'}</span>
                      <span className="text-sm font-medium text-gray-800">
                        {fund.name}
                        {isPlannedWithdrawal && <span className="text-[10px] text-amber-500 ml-1">(withdraw)</span>}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500" style={{ whiteSpace: 'nowrap' }}>
                        {signedWhole(netMoved)} / {isPlannedWithdrawal ? `-${formatCurrencyWhole(Math.abs(savingsBudget))}` : formatCurrencyWhole(Math.abs(savingsBudget))}
                      </span>
                      <span 
                        className="text-sm font-bold"
                        style={{ color: purple ? '#1E1630' : isComplete ? (purple ? '#5E35A8' : isPink ? '#6B9B5E' : '#11B981') : barColor }}
                      >
                        {displayPercent}%
                      </span>
                      <button type="button" aria-label={`Edit budget for ${fund.name}`} className="w-8 h-8 flex items-center justify-center flex-shrink-0 opacity-70 active:opacity-100 transition-opacity"
                        onClick={(e) => { e.stopPropagation(); setBudgetPopup({ label: fund.name, value: (savingsBudget !== 0 ? savingsBudget.toString() : ''), hint: 'Use negative for planned withdrawal e.g. -100', allowNegative: true, onSave: (val) => { const v = parseFloat(val) || 0; saveUserData({ budget_settings: { ...budgetSettings, savings_budgets_by_month: { ...(budgetSettings.savings_budgets_by_month || {}), [viewMonthKey]: { ...monthlySavingsBudgets, [fund.id]: v } } } }); } }); }}
                      ><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={faintInk} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 20h4L19.5 8.5a2.12 2.12 0 0 0-3-3L5 17z"></path><path d="M14.5 6.5 17.5 9.5"></path></svg></button>
                    </div>
                  </div>
                  <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all"
                      style={{ 
                        width: `${progressPercent}%`,
                        backgroundColor: isComplete ? (purple ? '#8F70E4' : isPink ? '#6B9B5E' : '#11B981') : barColor
                      }}
                    />
                  </div>
                </div>
              );
            })}
            
            {/* Investment Accounts */}
            {availableInvestmentAccounts.map((inv, index) => {
              const invBudget = monthlySavingsBudgets[inv.id] || 0;
              const isPlannedWithdrawal = invBudget < 0;
              const deposited = savingsTransactions
                .filter(t => t.account_id === inv.id && t.account_type === 'investment' && t.transaction_type === 'deposit')
                .reduce((sum, t) => sum + t.amount, 0);
              const withdrawn = savingsTransactions
                .filter(t => t.account_id === inv.id && t.account_type === 'investment' && t.transaction_type === 'withdrawal')
                .reduce((sum, t) => sum + t.amount, 0);
              const netMoved = deposited - withdrawn;
              // Progress uses the net, matching the "net into savings" summary above.
              const netToward = isPlannedWithdrawal ? -netMoved : netMoved;
              const progressPercent = invBudget !== 0
                ? Math.min((Math.max(netToward, 0) / Math.abs(invBudget)) * 100, 100)
                : 0;
              const displayPercent = Math.round(progressPercent);
              const isComplete = invBudget !== 0 ? netToward >= Math.abs(invBudget) : true;
              const barColor = purple ? '#C9B8EE' : isWarm ? '#2D5A3D' : isPink ? '#9CC48E' : (isPlannedWithdrawal ? '#f59e0b' : '#3b82f6');
              if (plan) {
                const planned = Math.abs(invBudget);
                const moved = Math.max(netToward, 0);
                return planSlimRow({
                  key: inv.id,
                  emoji: '\ud83d\udcca',
                  name: inv.name + (isPlannedWithdrawal ? ' (withdraw)' : ''),
                  right: planned === 0 ? signedWhole(netMoved) : isComplete ? 'Done \u2713' : `${formatCurrencyWhole(planned - moved)} to go`,
                  pct: progressPercent,
                  small: planned === 0 ? 'No plan set this month' : `${signedWhole(netMoved)} of ${isPlannedWithdrawal ? '-' : ''}${formatCurrencyWhole(planned)} ${isPlannedWithdrawal ? 'taken out' : 'in'}`,
                  color: '#9ED9B6',
                  onEdit: (e) => { e.stopPropagation(); setBudgetPopup({ label: inv.name, value: (invBudget !== 0 ? invBudget.toString() : ''), hint: 'Use negative for planned withdrawal e.g. -100', allowNegative: true, onSave: (val) => { const v = parseFloat(val) || 0; saveUserData({ budget_settings: { ...budgetSettings, savings_budgets_by_month: { ...(budgetSettings.savings_budgets_by_month || {}), [viewMonthKey]: { ...monthlySavingsBudgets, [inv.id]: v } } } }); } }); },
                  editLabel: `Edit budget for ${inv.name}`,
                });
              }
              
              return (
                <div key={inv.id} className="py-2">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-base flex-shrink-0">📊</span>
                      <span className="text-sm font-medium text-gray-800">
                        {inv.name}
                        {isPlannedWithdrawal && <span className="text-[10px] text-amber-500 ml-1">(withdraw)</span>}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500" style={{ whiteSpace: 'nowrap' }}>
                        {signedWhole(netMoved)} / {isPlannedWithdrawal ? `-${formatCurrencyWhole(Math.abs(invBudget))}` : formatCurrencyWhole(Math.abs(invBudget))}
                      </span>
                      <span 
                        className="text-sm font-bold"
                        style={{ color: purple ? '#1E1630' : isComplete ? (purple ? '#5E35A8' : isPink ? '#6B9B5E' : '#11B981') : barColor }}
                      >
                        {displayPercent}%
                      </span>
                      <button type="button" aria-label={`Edit budget for ${inv.name}`} className="w-8 h-8 flex items-center justify-center flex-shrink-0 opacity-70 active:opacity-100 transition-opacity"
                        onClick={(e) => { e.stopPropagation(); setBudgetPopup({ label: inv.name, value: (invBudget !== 0 ? invBudget.toString() : ''), hint: 'Use negative for planned withdrawal e.g. -100', allowNegative: true, onSave: (val) => { const v = parseFloat(val) || 0; saveUserData({ budget_settings: { ...budgetSettings, savings_budgets_by_month: { ...(budgetSettings.savings_budgets_by_month || {}), [viewMonthKey]: { ...monthlySavingsBudgets, [inv.id]: v } } } }); } }); }}
                      ><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={faintInk} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 20h4L19.5 8.5a2.12 2.12 0 0 0-3-3L5 17z"></path><path d="M14.5 6.5 17.5 9.5"></path></svg></button>
                    </div>
                  </div>
                  <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all"
                      style={{ 
                        width: `${progressPercent}%`,
                        backgroundColor: isComplete ? (purple ? '#8F70E4' : isPink ? '#6B9B5E' : '#11B981') : barColor
                      }}
                    />
                  </div>
                </div>
              );
            })}
            
            {availableSinkingFunds.length === 0 && availableInvestmentAccounts.length === 0 && (
              <p className="text-sm text-gray-400 italic py-2">No savings or investment accounts set up</p>
            )}
          </div>
        )}
        
        {/* Debt Payments Progress - New Design */}
        {budgetProgressView === 'debts' && (
          <div className="space-y-4">
            {(userDebts || []).map((debt, index) => {
              const debtBudget = monthlyDebtBudgets[debt.id] || 0;
              const paid = viewMonthTransactions
                .filter(t => t.is_debt_payment && t.debt_id === debt.id)
                .reduce((sum, t) => sum + t.amount, 0);
              const progressPercent = debtBudget > 0 ? Math.min((paid / debtBudget) * 100, 100) : 0;
              const displayPercent = Math.round(progressPercent);
              const isComplete = paid >= debtBudget;
              const barColor = purple ? '#C9B8EE' : isWarm ? '#2D5A3D' : isPink ? '#8EAEE3' : '#f97316';
              if (plan) {
                return planSlimRow({
                  key: debt.id,
                  emoji: '\ud83e\uddfe',
                  name: debt.name,
                  right: debtBudget <= 0 ? formatCurrencyWhole(paid) : isComplete ? 'Done \u2713' : `${formatCurrencyWhole(debtBudget - paid)} to go`,
                  pct: progressPercent,
                  small: debtBudget <= 0 ? `${formatCurrencyWhole(paid)} paid, no plan set` : `${formatCurrencyWhole(paid)} paid of ${formatCurrencyWhole(debtBudget)}`,
                  color: '#A9C6F0',
                  onEdit: (e) => { e.stopPropagation(); setBudgetPopup({ label: debt.name, value: (debtBudget > 0 ? debtBudget.toString() : ''), onSave: (val) => { const v = parseFloat(val) || 0; saveUserData({ budget_settings: { ...budgetSettings, debt_budgets_by_month: { ...(budgetSettings.debt_budgets_by_month || {}), [viewMonthKey]: { ...monthlyDebtBudgets, [debt.id]: v } } } }); } }); },
                  editLabel: `Edit budget for ${debt.name}`,
                });
              }
              
              return (
                <div key={debt.id} className="py-2">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-base flex-shrink-0">🧾</span>
                      <span className="text-sm font-medium text-gray-800">{debt.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">
                        {formatCurrencyWhole(paid)} / {formatCurrencyWhole(debtBudget)}
                      </span>
                      <span 
                        className="text-sm font-bold"
                        style={{ color: purple ? '#1E1630' : isComplete ? (purple ? '#5E35A8' : '#10b981') : barColor }}
                      >
                        {displayPercent}%
                      </span>
                      <button type="button" aria-label={`Edit budget for ${debt.name}`} className="w-8 h-8 flex items-center justify-center flex-shrink-0 opacity-70 active:opacity-100 transition-opacity"
                        onClick={(e) => { e.stopPropagation(); setBudgetPopup({ label: debt.name, value: (debtBudget > 0 ? debtBudget.toString() : ''), onSave: (val) => { const v = parseFloat(val) || 0; saveUserData({ budget_settings: { ...budgetSettings, debt_budgets_by_month: { ...(budgetSettings.debt_budgets_by_month || {}), [viewMonthKey]: { ...monthlyDebtBudgets, [debt.id]: v } } } }); } }); }}
                      ><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={faintInk} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 20h4L19.5 8.5a2.12 2.12 0 0 0-3-3L5 17z"></path><path d="M14.5 6.5 17.5 9.5"></path></svg></button>
                    </div>
                  </div>
                  <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all"
                      style={{ 
                        width: `${progressPercent}%`,
                        backgroundColor: isComplete ? (purple ? '#8F70E4' : '#10b981') : barColor
                      }}
                    />
                  </div>
                </div>
              );
            })}
            {(userDebts || []).length === 0 && (
              <p className="text-sm text-gray-400 italic py-2">No debts set up in Net Worth tab</p>
            )}
          </div>
        )}
        
      </div>
    )}
  </div>
</>
)}




{/* Set up <month> button - takes the + button's place while the month
    has nothing planned. The + comes back once it is set up, and shows
    during the tutorial so new users can add income. */}
{!showBudgetSetup && !showYearlyStats && !addFormType && !showAddMenu && !tourRunning && !(monthlyBudget > 0) && (
  <button
    type="button"
    onClick={() => setShowMonthSetup(true)}
    className="fixed z-30 rounded-full flex items-center gap-1.5 text-white transition-all duration-200 active:scale-95"
    style={{
      bottom: 'calc(env(safe-area-inset-bottom, 0px) + 76px)',
      right: '20px',
      height: '56px',
      padding: '0 22px',
      fontSize: '14.5px',
      fontWeight: plan ? 600 : 700,
      whiteSpace: 'nowrap',
      // plan_09: black, no pink glow
      background: plan ? '#1F1E24' : purple ? '#AC8DF2' : isPink ? '#D4157E' : isWarm ? '#2D5A3D' : '#4361EE',
      boxShadow: plan
        ? '0 6px 18px rgba(31,30,36,0.18)'
        : purple
        ? 'inset 0 1px 0 rgba(255,255,255,0.45), 0 8px 20px -10px rgba(140,100,220,0.5)'
        : isPink
        ? '0 8px 18px -8px rgba(212,21,126,0.6)'
        : isWarm
          ? '0 8px 18px -8px rgba(45,90,61,0.55)'
          : '0 8px 18px -8px rgba(67,97,238,0.55)',
    }}
    data-testid="month-setup-pill"
  >
    <span aria-hidden="true">{'✦'}</span>
    Set up {monthNames[viewMonth.getMonth()]}
  </button>
)}

{/* Add Form Modal */}
{/* Floating Action Button */}
{!showBudgetSetup && !showYearlyStats && !addFormType && (tourRunning || monthlyBudget > 0) && (
  <button
    onClick={() => setShowAddMenu(v => !v)}
    data-tour-target="budgetFab"
    className="fixed z-30 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 active:scale-95"
    style={{
      bottom: 'calc(env(safe-area-inset-bottom, 0px) + 76px)',
      right: '20px',
      background: isPink ? 'linear-gradient(135deg, #F5A8C1 0%, #E27BA1 100%)' : isWarm ? '#2D5A3D' : '#4361EE',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}
  >
    <Plus className={`w-6 h-6 text-white transition-transform duration-200 ${showAddMenu ? 'rotate-45' : ''}`} />
  </button>
)}

{/* Add Transaction Bottom Sheet - Menu + Forms */}
{(showAddMenu || addFormType) && (
  <div className="fixed inset-0 bg-black/40 z-40 flex items-end justify-center" onClick={() => { setAddFormType(null); setShowAddMenu(false); }}>
    <div
      className="w-full bg-white rounded-t-3xl shadow-2xl flex flex-col animate-in slide-in-from-bottom duration-500 wy-plan-addsheet"
      style={{ position: 'relative', height: 'calc(100vh - env(safe-area-inset-top, 0px) - 12px)', marginTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 44px)' }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="flex justify-center pt-3 pb-3 flex-shrink-0 cursor-grab"
        onTouchStart={(e) => { e.currentTarget._startY = e.touches[0].clientY; }}
        onTouchMove={(e) => {
          const dy = e.touches[0].clientY - e.currentTarget._startY;
          if (dy > 0) e.currentTarget.parentElement.style.transform = `translateY(${dy}px)`;
        }}
        onTouchEnd={(e) => {
          const dy = e.changedTouches[0].clientY - e.currentTarget._startY;
          const el = e.currentTarget.parentElement;
          if (dy > 50) { el.style.transform = ''; setAddFormType(null); setShowAddMenu(false); }
          else { el.style.transition = 'transform 0.2s'; el.style.transform = ''; setTimeout(() => { el.style.transition = ''; }, 200); }
        }}
      ><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
      <button
        type="button"
        aria-label="Close sheet"
        onClick={() => { setAddFormType(null); setShowAddMenu(false); }}
        className="absolute w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
        style={{ top: 10, right: 14, zIndex: 5 }}
      >
        <X className="w-4 h-4 text-gray-500" />
      </button>
      <div className="flex-1 overflow-y-auto overscroll-contain px-5 pb-4">

      {/* Menu Grid */}
      {!addFormType && (
        <div>
          <p className="px-1 pt-2 mb-1" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '24px', fontWeight: 400, color: '#1F2937' }}>{plan ? 'Add a transaction' : 'Add Transaction'}</p>
          <p className="text-xs text-gray-400 px-1 mb-5">{tourRunning ? 'Start with your income. You can add the rest once your budget is set up.' : 'What are you logging today?'}</p>

          <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mb-2" style={{ color: txEyebrowColor }}><span className="wy-star">✦ </span>Money in</p>
          <div className="flex flex-col gap-2 mb-5">
            <button
              onClick={() => setAddFormType('income')}
              className="flex items-center gap-3.5 bg-white rounded-2xl px-4 py-3.5 shadow-sm w-full hover:bg-gray-50 transition-colors"
              style={tourRunning ? { boxShadow: `0 0 0 2px ${isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE'}` } : undefined}
            >
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center text-xl flex-shrink-0 ${txTypeChip('income').className}`} style={txTypeChip('income').style}>{txTypeMeta.income.emoji}</div>
              <div className="flex flex-col items-start min-w-0">
                <span className="text-[15px] font-semibold text-gray-800">{txTypeMeta.income.label}</span>
                <span className="text-xs text-gray-400">{txTypeMeta.income.sub}</span>
              </div>
              {tourRunning && (
                <span className="ml-auto rounded-full flex-shrink-0" style={{ background: isPink ? '#EDE6F5' : isWarm ? '#F0E6D8' : '#EEF1FE', color: isPink ? '#6B55A3' : isWarm ? '#2D5A3D' : '#4361EE', padding: '4px 10px', fontSize: '11px', fontWeight: 700 }}>
                  Add income
                </span>
              )}
            </button>
            <button
              onClick={() => setAddFormType('withdrawal')}
              disabled={tourRunning}
              style={tourRunning ? { opacity: 0.35 } : undefined}
              className="flex items-center gap-3.5 bg-white rounded-2xl px-4 py-3.5 shadow-sm w-full hover:bg-gray-50 transition-colors"
            >
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center text-xl flex-shrink-0 ${txTypeChip('withdrawal').className}`} style={txTypeChip('withdrawal').style}>{txTypeMeta.withdrawal.emoji}</div>
              <div className="flex flex-col items-start min-w-0">
                <span className="text-[15px] font-semibold text-gray-800">{txTypeMeta.withdrawal.label}</span>
                <span className="text-xs text-gray-400">{txTypeMeta.withdrawal.sub}</span>
              </div>
            </button>
          </div>

          <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mb-2" style={{ color: txEyebrowColor }}><span className="wy-star">✦ </span>Money out</p>
          <div className="flex flex-col gap-2 mb-5">
            <button
              onClick={() => setAddFormType('expense')}
              disabled={tourRunning}
              style={tourRunning ? { opacity: 0.35 } : undefined}
              className="flex items-center gap-3.5 bg-white rounded-2xl px-4 py-3.5 shadow-sm w-full hover:bg-gray-50 transition-colors"
            >
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center text-xl flex-shrink-0 ${txTypeChip('expense').className}`} style={txTypeChip('expense').style}>{txTypeMeta.expense.emoji}</div>
              <div className="flex flex-col items-start min-w-0">
                <span className="text-[15px] font-semibold text-gray-800">{txTypeMeta.expense.label}</span>
                <span className="text-xs text-gray-400">{txTypeMeta.expense.sub}</span>
              </div>
            </button>
            {userDebts.length > 0 && (
              <button
                onClick={() => setAddFormType('debtpayment')}
                disabled={tourRunning}
                style={tourRunning ? { opacity: 0.35 } : undefined}
                className="flex items-center gap-3.5 bg-white rounded-2xl px-4 py-3.5 shadow-sm w-full hover:bg-gray-50 transition-colors"
              >
                <div className={`w-11 h-11 rounded-2xl flex items-center justify-center text-xl flex-shrink-0 ${txTypeChip('debtpayment').className}`} style={txTypeChip('debtpayment').style}>{txTypeMeta.debtpayment.emoji}</div>
                <div className="flex flex-col items-start min-w-0">
                  <span className="text-[15px] font-semibold text-gray-800">{txTypeMeta.debtpayment.label}</span>
                  <span className="text-xs text-gray-400">{txTypeMeta.debtpayment.sub}</span>
                </div>
              </button>
            )}
          </div>

          <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mb-2" style={{ color: txEyebrowColor }}><span className="wy-star">✦ </span>Move money</p>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => setAddFormType('savings')}
              disabled={tourRunning}
              style={tourRunning ? { opacity: 0.35 } : undefined}
              className="flex items-center gap-3.5 bg-white rounded-2xl px-4 py-3.5 shadow-sm w-full hover:bg-gray-50 transition-colors"
            >
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center text-xl flex-shrink-0 ${txTypeChip('savings').className}`} style={txTypeChip('savings').style}>{txTypeMeta.savings.emoji}</div>
              <div className="flex flex-col items-start min-w-0">
                <span className="text-[15px] font-semibold text-gray-800">{txTypeMeta.savings.label}</span>
                <span className="text-xs text-gray-400">{txTypeMeta.savings.sub}</span>
              </div>
            </button>
            {cards && cards.length >= 2 && (
              <button
                onClick={() => setAddFormType('transfer')}
                disabled={tourRunning}
                style={tourRunning ? { opacity: 0.35 } : undefined}
                className="flex items-center gap-3.5 bg-white rounded-2xl px-4 py-3.5 shadow-sm w-full hover:bg-gray-50 transition-colors"
              >
                <div className={`w-11 h-11 rounded-2xl flex items-center justify-center text-xl flex-shrink-0 ${txTypeChip('transfer').className}`} style={txTypeChip('transfer').style}>{txTypeMeta.transfer.emoji}</div>
                <div className="flex flex-col items-start min-w-0">
                  <span className="text-[15px] font-semibold text-gray-800">{txTypeMeta.transfer.label}</span>
                  <span className="text-xs text-gray-400">{txTypeMeta.transfer.sub}</span>
                </div>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Forms */}
      {addFormType && (
        <div className="pt-2">
      {/* Income Form */}
      {addFormType === 'income' && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-lg flex-shrink-0 ${txTypeChip('income').className}`} style={txTypeChip('income').style}>{newIncome.isInterest ? '🌱' : txTypeMeta.income.emoji}</div>
            <h3 className="m-0" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '21px', fontWeight: 400, color: '#1F2937' }}>{newIncome.isInterest ? 'Add Interest Income' : 'Add Income'}</h3>
          </div>
          <div className="space-y-4">
            {/* Amount hero card */}
            <div className="rounded-3xl px-4 py-5 text-center" style={{ background: txTypeAccentSoft('income') }}>
              <p className="text-[11px] font-bold uppercase tracking-[0.08em] mb-1" style={{ color: txTypeAccent('income'), opacity: 0.75 }}>Amount</p>
              <div className="flex items-center justify-center gap-1">
                <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: '24px', color: txTypeAccent('income'), opacity: 0.65 }}>{getCurrentCurrency().symbol}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={newIncome.amount}
                  onChange={(e) => setNewIncome(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0"
                  autoFocus
                  data-testid="income-amount-input"
                  className="bg-transparent outline-none text-center placeholder:opacity-40"
                  style={{ fontFamily: "'DM Serif Display', serif", fontSize: '44px', color: txTypeAccent('income'), width: '55%', caretColor: txTypeAccent('income') }}
                />
              </div>
            </div>

            {/* Source */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Source</p>
              <input
                type="text"
                value={newIncome.description}
                onChange={(e) => setNewIncome(prev => ({ ...prev, description: e.target.value }))}
                placeholder={newIncome.isInterest ? "e.g. Interest" : "e.g. Salary, Freelance"}
                data-testid="income-description-input"
                className="w-full h-14 bg-white rounded-2xl px-4 shadow-sm outline-none text-[15px] text-gray-800 placeholder:text-gray-300" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, caretColor: '#1F2937' }}
              />
            </div>

            {/* Interest checkbox */}
            <label className="flex items-center gap-2.5 cursor-pointer bg-white rounded-2xl px-4 h-14 shadow-sm">
              <input
                type="checkbox"
                checked={newIncome.isInterest}
                onChange={(e) => setNewIncome(prev => ({ ...prev, isInterest: e.target.checked, interestAccount: '', interestAccountType: '' }))}
                className="w-4 h-4 rounded border-gray-300 focus:ring-2"
                style={{ accentColor: txTypeAccent('income') }}
              />
              <span className="text-sm text-gray-600">This is interest income</span>
            </label>

            {/* Card selector for income (only for non-interest) */}
            {!newIncome.isInterest && cards.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Deposit to <span className="text-gray-300">(optional)</span></p>
                <Select
                  value={newIncome.toCard || ''}
                  onValueChange={(value) => setNewIncome(prev => ({ ...prev, toCard: value }))}
                >
                  <SelectTrigger className="!h-14 bg-white rounded-2xl px-4 shadow-sm w-full">
                    <div className="flex items-center gap-3 min-w-0" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 400, color: '#1F2937' }}>
                      <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">💳</div>
                      <SelectValue placeholder="Select payment method" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethods.map(card => (
                      <SelectItem key={card.name} value={card.name}>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${colorMap[card.color]?.bg || 'bg-gray-400'}`} />
                          <span>{card.name}</span>
                          {card.type === 'credit' && <span className="text-xs text-red-500">(Credit)</span>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Account selector for interest */}
            {newIncome.isInterest && (
              <div>
                <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Apply interest to</p>
                <Select
                  value={newIncome.interestAccount ? `${newIncome.interestAccountType}:${newIncome.interestAccount}` : ''}
                  onValueChange={(value) => {
                    const [type, id] = value.split(':');
                    setNewIncome(prev => ({ ...prev, interestAccountType: type, interestAccount: id }));
                  }}
                >
                  <SelectTrigger className="!h-14 bg-white rounded-2xl px-4 shadow-sm w-full">
                    <div className="flex items-center gap-3 min-w-0" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 400, color: '#1F2937' }}>
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center text-base flex-shrink-0" style={{ background: txTypeAccentSoft('income') }}>🌱</div>
                      <SelectValue placeholder="Select account" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {availableSinkingFunds.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs font-semibold text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Sinking Funds</div>
                        {availableSinkingFunds.map((fund) => (
                          <SelectItem key={`fund:${fund.id}`} value={`fund:${fund.id}`}>
                            {fund.emoji || '💰'} {fund.name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                    {availableInvestmentAccounts.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs font-semibold text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Investment Accounts</div>
                        {availableInvestmentAccounts.map((inv) => (
                          <SelectItem key={`investment:${inv.id}`} value={`investment:${inv.id}`}>
                            📊 {inv.name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Date */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Date</p>
              <label
                htmlFor="worthly-transaction-date"
                className="flex items-center gap-3 bg-white rounded-2xl px-4 h-14 shadow-sm w-full cursor-pointer"
                onClick={(e) => {
                  // Belt-and-suspenders: native <label> click-forwarding works on iOS,
                  // but desktop browsers sometimes only focus. showPicker() forces it open.
                  const input = e.currentTarget.querySelector('input[type="date"]');
                  if (input && typeof input.showPicker === 'function') {
                    try { input.showPicker(); } catch (_) { /* not supported, label fallback handles it */ }
                  }
                }}
              >
                <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">📅</div>
                <input
                  id="worthly-transaction-date"
                  type="date"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                  className="flex-1 bg-transparent text-[14.5px] text-gray-800 focus:outline-none cursor-pointer"
                />
              </label>
            </div>

            <button
              onClick={() => { addTransaction(true); setAddFormType(null); setShowAddMenu(false); }}
              className="w-full flex items-center justify-center gap-1.5 rounded-full py-3.5 text-white font-semibold text-[15px] shadow-md"
              style={txCtaStyle}
              data-testid="add-income-btn"
            >
              <Plus className="w-4 h-4" /> {newIncome.isInterest ? 'Add Interest' : 'Add Income'}
            </button>
          </div>
        </div>
      )}

      {/* Expense Form */}
      {addFormType === 'expense' && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-lg flex-shrink-0 ${txTypeChip('expense').className}`} style={txTypeChip('expense').style}>{txTypeMeta.expense.emoji}</div>
            <h3 className="m-0" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '21px', fontWeight: 400, color: '#1F2937' }}>Add Expense</h3>
          </div>
          <div className="space-y-4">
            {/* Amount hero card */}
            <div className="rounded-3xl px-4 py-5 text-center" style={{ background: txTypeAccentSoft('expense') }}>
              <p className="text-[11px] font-bold uppercase tracking-[0.08em] mb-1" style={{ color: txTypeAccent('expense'), opacity: 0.75 }}>Amount</p>
              <div className="flex items-center justify-center gap-1">
                <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: '24px', color: txTypeAccent('expense'), opacity: 0.65 }}>{getCurrentCurrency().symbol}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={newTransaction.amount}
                  onChange={(e) => setNewTransaction(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0"
                  autoFocus
                  className="bg-transparent outline-none text-center placeholder:opacity-40"
                  style={{ fontFamily: "'DM Serif Display', serif", fontSize: '44px', color: txTypeAccent('expense'), width: '55%', caretColor: txTypeAccent('expense') }}
                />
              </div>
            </div>

            {/* Category */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Category</p>
              <div className="relative">
                <div className="flex items-center gap-3 bg-white rounded-2xl px-4 h-14 shadow-sm w-full">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center text-base flex-shrink-0" style={{ background: txTypeAccentSoft('expense') }}>🏷️</div>
                  <input
                    type="text"
                    value={newTransaction.category || categorySearch}
                    onChange={(e) => { setCategorySearch(e.target.value); setNewTransaction(prev => ({ ...prev, category: '' })); setShowCategoryDropdown(true); }}
                    onFocus={() => setShowCategoryDropdown(true)}
                    onBlur={() => setTimeout(() => setShowCategoryDropdown(false), 200)}
                    placeholder="Choose a category"
                    className="flex-1 min-w-0 bg-transparent outline-none text-[15px] text-gray-800 placeholder:text-gray-300" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, caretColor: '#1F2937' }}
                  />
                </div>
                {showCategoryDropdown && filteredCategories.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-100 rounded-2xl shadow-lg max-h-72 overflow-y-auto">
                    {filteredCategories.map((cat, index) => (
                      <button key={index} type="button" onMouseDown={(e) => { e.preventDefault(); selectCategory(cat); }} className="w-full text-left px-4 py-2 hover:theme-bg-50 text-sm text-gray-700 flex items-center gap-2">
                        <Tag className="w-3 h-3 text-gray-400" />{cat}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Description <span className="text-gray-300">(optional)</span></p>
              <input
                type="text"
                value={newTransaction.description}
                onChange={(e) => setNewTransaction(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What was this for?"
                className="w-full h-14 bg-white rounded-2xl px-4 shadow-sm outline-none text-[15px] text-gray-800 placeholder:text-gray-300" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, caretColor: '#1F2937' }}
              />
            </div>

            {/* How did it feel? (feeds the month-end review) */}
            <FeelingPicker
              value={newTransaction.feeling || ''}
              onChange={(v) => setNewTransaction(prev => ({ ...prev, feeling: v }))}
              theme={theme}
              autoTip={!!userData && !(userData.month_end_review && userData.month_end_review.feel_tip_seen)}
              onTipShown={() => saveUserData({ month_end_review: { ...((userData && userData.month_end_review) || {}), feel_tip_seen: true } })}
            />

            {/* Payment method */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Payment method</p>
              <Select value={newTransaction.paymentMethod} onValueChange={(value) => setNewTransaction(prev => ({ ...prev, paymentMethod: value }))}>
                <SelectTrigger className="!h-14 bg-white rounded-2xl px-4 shadow-sm w-full">
                  <div className="flex items-center gap-3 min-w-0" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 400, color: '#1F2937' }}>
                    <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">💳</div>
                    <SelectValue placeholder="Select payment method" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.map((card) => {
                    const colors = colorMap[card.color] || colorMap.grey;
                    return (
                      <SelectItem key={card.name} value={card.name}>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${colors.bg}`} />
                          {card.name}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Date */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Date</p>
              <label
                htmlFor="worthly-transaction-date"
                className="flex items-center gap-3 bg-white rounded-2xl px-4 h-14 shadow-sm w-full cursor-pointer"
                onClick={(e) => {
                  // Belt-and-suspenders: native <label> click-forwarding works on iOS,
                  // but desktop browsers sometimes only focus. showPicker() forces it open.
                  const input = e.currentTarget.querySelector('input[type="date"]');
                  if (input && typeof input.showPicker === 'function') {
                    try { input.showPicker(); } catch (_) { /* not supported, label fallback handles it */ }
                  }
                }}
              >
                <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">📅</div>
                <input
                  id="worthly-transaction-date"
                  type="date"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                  className="flex-1 bg-transparent text-[14.5px] text-gray-800 focus:outline-none cursor-pointer"
                />
              </label>
            </div>

            <button
              onClick={() => { addTransaction(false); setAddFormType(null); setShowAddMenu(false); }}
              className="w-full flex items-center justify-center gap-1.5 rounded-full py-3.5 text-white font-semibold text-[15px] shadow-md"
              style={txCtaStyle}
            >
              <Plus className="w-4 h-4" /> Add Expense
            </button>
            <SiriLink theme={theme} plan={plan} onClick={() => setShowSiriHelp(true)} />
            <SiriHowTo open={showSiriHelp} onClose={() => setShowSiriHelp(false)} theme={theme} plan={plan} />
          </div>
        </div>
      )}

      {/* Savings Form */}
      {addFormType === 'savings' && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-lg flex-shrink-0 ${txTypeChip('savings').className}`} style={txTypeChip('savings').style}>{txTypeMeta.savings.emoji}</div>
            <h3 className="m-0" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '21px', fontWeight: 400, color: '#1F2937' }}>Save & Invest</h3>
          </div>
          <div className="space-y-4">
            {/* Note */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Note <span className="text-gray-300">(optional)</span></p>
              <input
                type="text"
                value={newSavings.description}
                onChange={(e) => setNewSavings(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What's this deposit for?"
                data-testid="savings-description-input"
                className="w-full h-14 bg-white rounded-2xl px-4 shadow-sm outline-none text-[15px] text-gray-800 placeholder:text-gray-300" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, caretColor: '#1F2937' }}
              />
            </div>

            {/* Account */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Account</p>
              <Select
                value={newSavings.accountId ? `${newSavings.accountType}:${newSavings.accountId}` : ''}
                onValueChange={(value) => {
                  const [type, id] = value.split(':');
                  setNewSavings(prev => ({ ...prev, accountType: type, accountId: id }));
                }}
              >
                <SelectTrigger className="!h-14 bg-white rounded-2xl px-4 shadow-sm w-full">
                  <div className="flex items-center gap-3 min-w-0" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 400, color: '#1F2937' }}>
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center text-base flex-shrink-0" style={{ background: txTypeAccentSoft('savings') }}>{txTypeMeta.savings.emoji}</div>
                    <SelectValue placeholder="Select account to deposit to" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {availableSinkingFunds.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-xs font-semibold text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Sinking Funds</div>
                      {availableSinkingFunds.map((fund) => (
                        <SelectItem key={`fund:${fund.id}`} value={`fund:${fund.id}`}>
                          {fund.emoji || '💰'} {fund.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {availableInvestmentAccounts.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-xs font-semibold text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Investment Accounts</div>
                      {availableInvestmentAccounts.map((inv) => (
                        <SelectItem key={`investment:${inv.id}`} value={`investment:${inv.id}`}>
                          📊 {inv.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Amount hero card */}
            <div className="rounded-3xl px-4 py-5 text-center" style={{ background: txTypeAccentSoft('savings') }}>
              <p className="text-[11px] font-bold uppercase tracking-[0.08em] mb-1" style={{ color: txTypeAccent('savings'), opacity: 0.75 }}>Amount</p>
              <div className="flex items-center justify-center gap-1">
                <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: '24px', color: txTypeAccent('savings'), opacity: 0.65 }}>{getCurrentCurrency().symbol}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={newSavings.amount}
                  onChange={(e) => setNewSavings(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0"
                  autoFocus
                  data-testid="savings-amount-input"
                  className="bg-transparent outline-none text-center placeholder:opacity-40"
                  style={{ fontFamily: "'DM Serif Display', serif", fontSize: '44px', color: txTypeAccent('savings'), width: '55%', caretColor: txTypeAccent('savings') }}
                />
              </div>
            </div>

            {/* From payment method */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>From payment method <span className="text-gray-300">(optional)</span></p>
              <Select
                value={newSavings.fromCard || ''}
                onValueChange={(value) => setNewSavings(prev => ({ ...prev, fromCard: value }))}
              >
                <SelectTrigger className="!h-14 bg-white rounded-2xl px-4 shadow-sm w-full">
                  <div className="flex items-center gap-3 min-w-0" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 400, color: '#1F2937' }}>
                    <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">💳</div>
                    <SelectValue placeholder="Select payment method" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.filter(c => c.type !== 'credit').map(card => (
                    <SelectItem key={card.name} value={card.name}>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${colorMap[card.color]?.bg || 'bg-gray-400'}`} />
                        {card.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Date</p>
              <label
                htmlFor="worthly-transaction-date"
                className="flex items-center gap-3 bg-white rounded-2xl px-4 h-14 shadow-sm w-full cursor-pointer"
                onClick={(e) => {
                  // Belt-and-suspenders: native <label> click-forwarding works on iOS,
                  // but desktop browsers sometimes only focus. showPicker() forces it open.
                  const input = e.currentTarget.querySelector('input[type="date"]');
                  if (input && typeof input.showPicker === 'function') {
                    try { input.showPicker(); } catch (_) { /* not supported, label fallback handles it */ }
                  }
                }}
              >
                <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">📅</div>
                <input
                  id="worthly-transaction-date"
                  type="date"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                  className="flex-1 bg-transparent text-[14.5px] text-gray-800 focus:outline-none cursor-pointer"
                />
              </label>
            </div>

            <button
              onClick={() => { addSavingsTransaction(); setAddFormType(null); setShowAddMenu(false); }}
              className="w-full flex items-center justify-center gap-1.5 rounded-full py-3.5 text-white font-semibold text-[15px] shadow-md disabled:opacity-40"
              style={txCtaStyle}
              data-testid="add-savings-btn"
              disabled={!newSavings.accountId}
            >
              <Plus className="w-4 h-4" /> Add Deposit
            </button>
          </div>
        </div>
      )}

      {/* Withdrawal Form */}
      {addFormType === 'withdrawal' && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-lg flex-shrink-0 ${txTypeChip('withdrawal').className}`} style={txTypeChip('withdrawal').style}>{txTypeMeta.withdrawal.emoji}</div>
            <h3 className="m-0" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '21px', fontWeight: 400, color: '#1F2937' }}>Withdraw from Savings</h3>
          </div>
          <div className="space-y-4">
            {/* Reason */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Reason <span className="text-gray-300">(optional)</span></p>
              <input
                type="text"
                value={newWithdrawal.description}
                onChange={(e) => setNewWithdrawal(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What's this withdrawal for?"
                className="w-full h-14 bg-white rounded-2xl px-4 shadow-sm outline-none text-[15px] text-gray-800 placeholder:text-gray-300" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, caretColor: '#1F2937' }}
              />
            </div>

            {/* Account */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Account</p>
              <Select
                value={newWithdrawal.accountId ? `${newWithdrawal.accountType}:${newWithdrawal.accountId}` : ''}
                onValueChange={(value) => {
                  const [type, id] = value.split(':');
                  setNewWithdrawal(prev => ({ ...prev, accountType: type, accountId: id }));
                }}
              >
                <SelectTrigger className="!h-14 bg-white rounded-2xl px-4 shadow-sm w-full">
                  <div className="flex items-center gap-3 min-w-0" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 400, color: '#1F2937' }}>
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center text-base flex-shrink-0" style={{ background: txTypeAccentSoft('withdrawal') }}>{txTypeMeta.withdrawal.emoji}</div>
                    <SelectValue placeholder="Select account to withdraw from" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {availableSinkingFunds.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-xs font-semibold text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Sinking Funds</div>
                      {availableSinkingFunds.map((fund) => (
                        <SelectItem key={`fund:${fund.id}`} value={`fund:${fund.id}`}>
                          {fund.emoji || '💰'} {fund.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {availableInvestmentAccounts.length > 0 && (
                    <>
                      <div className="px-2 py-1 text-xs font-semibold text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Investment Accounts</div>
                      {availableInvestmentAccounts.map((inv) => (
                        <SelectItem key={`investment:${inv.id}`} value={`investment:${inv.id}`}>
                          📊 {inv.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Amount hero card */}
            <div className="rounded-3xl px-4 py-5 text-center" style={{ background: txTypeAccentSoft('withdrawal') }}>
              <p className="text-[11px] font-bold uppercase tracking-[0.08em] mb-1" style={{ color: txTypeAccent('withdrawal'), opacity: 0.75 }}>Amount</p>
              <div className="flex items-center justify-center gap-1">
                <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: '24px', color: txTypeAccent('withdrawal'), opacity: 0.65 }}>{getCurrentCurrency().symbol}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={newWithdrawal.amount}
                  onChange={(e) => setNewWithdrawal(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0"
                  autoFocus
                  className="bg-transparent outline-none text-center placeholder:opacity-40"
                  style={{ fontFamily: "'DM Serif Display', serif", fontSize: '44px', color: txTypeAccent('withdrawal'), width: '55%', caretColor: txTypeAccent('withdrawal') }}
                />
              </div>
            </div>

            {/* To bank account/card */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>To bank account/card <span className="text-gray-300">(optional)</span></p>
              <Select
                value={newWithdrawal.toCard || ''}
                onValueChange={(value) => setNewWithdrawal(prev => ({ ...prev, toCard: value }))}
              >
                <SelectTrigger className="!h-14 bg-white rounded-2xl px-4 shadow-sm w-full">
                  <div className="flex items-center gap-3 min-w-0" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 400, color: '#1F2937' }}>
                    <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">💳</div>
                    <SelectValue placeholder="Select payment method" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.filter(c => c.type !== 'credit').map(card => (
                    <SelectItem key={card.name} value={card.name}>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${colorMap[card.color]?.bg || 'bg-gray-400'}`} />
                        {card.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Date</p>
              <label
                htmlFor="worthly-transaction-date"
                className="flex items-center gap-3 bg-white rounded-2xl px-4 h-14 shadow-sm w-full cursor-pointer"
                onClick={(e) => {
                  // Belt-and-suspenders: native <label> click-forwarding works on iOS,
                  // but desktop browsers sometimes only focus. showPicker() forces it open.
                  const input = e.currentTarget.querySelector('input[type="date"]');
                  if (input && typeof input.showPicker === 'function') {
                    try { input.showPicker(); } catch (_) { /* not supported, label fallback handles it */ }
                  }
                }}
              >
                <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">📅</div>
                <input
                  id="worthly-transaction-date"
                  type="date"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                  className="flex-1 bg-transparent text-[14.5px] text-gray-800 focus:outline-none cursor-pointer"
                />
              </label>
            </div>

            <button
              onClick={() => { addWithdrawalTransaction(); setAddFormType(null); setShowAddMenu(false); }}
              className="w-full flex items-center justify-center gap-1.5 rounded-full py-3.5 text-white font-semibold text-[15px] shadow-md disabled:opacity-40"
              style={txCtaStyle}
              disabled={!newWithdrawal.accountId}
            >
              Withdraw
            </button>
          </div>
        </div>
      )}

      {/* Debt Payment Form */}
      {addFormType === 'debtpayment' && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-lg flex-shrink-0 ${txTypeChip('debtpayment').className}`} style={txTypeChip('debtpayment').style}>{txTypeMeta.debtpayment.emoji}</div>
            <h3 className="m-0" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '21px', fontWeight: 400, color: '#1F2937' }}>Pay Debt</h3>
          </div>
          <div className="space-y-4">
            {/* Note */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Note <span className="text-gray-300">(optional)</span></p>
              <input
                type="text"
                value={newDebtPayment.description}
                onChange={(e) => setNewDebtPayment(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Add a note"
                className="w-full h-14 bg-white rounded-2xl px-4 shadow-sm outline-none text-[15px] text-gray-800 placeholder:text-gray-300" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, caretColor: '#1F2937' }}
              />
            </div>

            {/* Debt */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Debt</p>
              <Select value={newDebtPayment.debtId} onValueChange={(value) => setNewDebtPayment(prev => ({ ...prev, debtId: value }))}>
                <SelectTrigger className="!h-14 bg-white rounded-2xl px-4 shadow-sm w-full">
                  <div className="flex items-center gap-3 min-w-0" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 400, color: '#1F2937' }}>
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center text-base flex-shrink-0" style={{ background: txTypeAccentSoft('debtpayment') }}>{txTypeMeta.debtpayment.emoji}</div>
                    <SelectValue placeholder="Select debt to pay" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {userDebts.map(debt => {
                    const balance = getCurrentDebtBalance(debt);
                    return (
                      <SelectItem key={debt.id} value={debt.id} disabled={balance <= 0}>
                        <div className="flex items-center justify-between w-full gap-2">
                          <span>{debt.name}</span>
                          <span className={`text-xs ${balance <= 0 ? 'text-green-500' : 'text-gray-500'}`}>
                            {balance <= 0 ? '✓ Paid off' : `${getCurrentCurrency().symbol}${balance.toLocaleString()} left`}
                          </span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Amount hero card */}
            <div className="rounded-3xl px-4 py-5 text-center" style={{ background: txTypeAccentSoft('debtpayment') }}>
              <p className="text-[11px] font-bold uppercase tracking-[0.08em] mb-1" style={{ color: txTypeAccent('debtpayment'), opacity: 0.75 }}>Payment amount</p>
              <div className="flex items-center justify-center gap-1">
                <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: '24px', color: txTypeAccent('debtpayment'), opacity: 0.65 }}>{getCurrentCurrency().symbol}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={newDebtPayment.amount}
                  onChange={(e) => setNewDebtPayment(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0"
                  autoFocus
                  className="bg-transparent outline-none text-center placeholder:opacity-40"
                  style={{ fontFamily: "'DM Serif Display', serif", fontSize: '44px', color: txTypeAccent('debtpayment'), width: '55%', caretColor: txTypeAccent('debtpayment') }}
                />
              </div>
            </div>

            {/* Payment method */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Payment method <span className="text-gray-300">(optional)</span></p>
              <Select value={newDebtPayment.paymentMethod} onValueChange={(value) => setNewDebtPayment(prev => ({ ...prev, paymentMethod: value }))}>
                <SelectTrigger className="!h-14 bg-white rounded-2xl px-4 shadow-sm w-full">
                  <div className="flex items-center gap-3 min-w-0" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 400, color: '#1F2937' }}>
                    <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">💳</div>
                    <SelectValue placeholder="Select payment method" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.map(card => (
                    <SelectItem key={card.name} value={card.name}>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${colorMap[card.color]?.bg || 'bg-gray-400'}`} />
                        <span>{card.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Date</p>
              <label
                htmlFor="worthly-transaction-date"
                className="flex items-center gap-3 bg-white rounded-2xl px-4 h-14 shadow-sm w-full cursor-pointer"
                onClick={(e) => {
                  // Belt-and-suspenders: native <label> click-forwarding works on iOS,
                  // but desktop browsers sometimes only focus. showPicker() forces it open.
                  const input = e.currentTarget.querySelector('input[type="date"]');
                  if (input && typeof input.showPicker === 'function') {
                    try { input.showPicker(); } catch (_) { /* not supported, label fallback handles it */ }
                  }
                }}
              >
                <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">📅</div>
                <input
                  id="worthly-transaction-date"
                  type="date"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                  className="flex-1 bg-transparent text-[14.5px] text-gray-800 focus:outline-none cursor-pointer"
                />
              </label>
            </div>

            <button
              onClick={() => { addDebtPaymentTransaction(); setAddFormType(null); setShowAddMenu(false); }}
              className="w-full flex items-center justify-center gap-1.5 rounded-full py-3.5 text-white font-semibold text-[15px] shadow-md disabled:opacity-40"
              style={txCtaStyle}
              disabled={!newDebtPayment.debtId || !newDebtPayment.amount}
            >
              Make Payment
            </button>
          </div>
        </div>
      )}

      {/* Transfer Form */}
      {addFormType === 'transfer' && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-lg flex-shrink-0 ${txTypeChip('transfer').className}`} style={txTypeChip('transfer').style}>{txTypeMeta.transfer.emoji}</div>
            <h3 className="m-0" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '21px', fontWeight: 400, color: '#1F2937' }}>Transfer</h3>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              {/* From */}
              <div>
                <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>From</p>
                <Select value={newTransfer.fromCard} onValueChange={(value) => setNewTransfer(prev => ({ ...prev, fromCard: value }))}>
                  <SelectTrigger className="!h-14 bg-white rounded-2xl px-4 shadow-sm w-full">
                    <div className="flex items-center gap-3 min-w-0" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 400, color: '#1F2937' }}>
                      <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">💳</div>
                      <SelectValue placeholder="Select payment method" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethods.filter(c => c.type !== 'credit').map(card => (
                      <SelectItem key={card.name} value={card.name}>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${colorMap[card.color]?.bg || 'bg-gray-400'}`} />
                          <span>{card.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-center">
                <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: txTypeAccentSoft('transfer') }}>
                  <ArrowDown className="w-3.5 h-3.5" style={{ color: txTypeAccent('transfer') }} />
                </div>
              </div>

              {/* To */}
              <div>
                <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>To</p>
                <Select value={newTransfer.toCard} onValueChange={(value) => setNewTransfer(prev => ({ ...prev, toCard: value }))}>
                  <SelectTrigger className="!h-14 bg-white rounded-2xl px-4 shadow-sm w-full">
                    <div className="flex items-center gap-3 min-w-0" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 400, color: '#1F2937' }}>
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-base flex-shrink-0 ${txTypeChip('transfer').className}`} style={txTypeChip('transfer').style}>💳</div>
                      <SelectValue placeholder="Select payment method" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethods.map(card => (
                      <SelectItem key={card.name} value={card.name} disabled={card.name === newTransfer.fromCard}>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${colorMap[card.color]?.bg || 'bg-gray-400'}`} />
                          <span>{card.name}</span>
                          {card.type === 'credit' && <span className="text-xs text-red-500">(Credit)</span>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Amount hero card */}
            <div className="rounded-3xl px-4 py-5 text-center" style={{ background: txTypeAccentSoft('transfer') }}>
              <p className="text-[11px] font-bold uppercase tracking-[0.08em] mb-1" style={{ color: txTypeAccent('transfer'), opacity: 0.75 }}>Amount</p>
              <div className="flex items-center justify-center gap-1">
                <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: '24px', color: txTypeAccent('transfer'), opacity: 0.65 }}>{getCurrentCurrency().symbol}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={newTransfer.amount}
                  onChange={(e) => setNewTransfer(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0"
                  autoFocus
                  className="bg-transparent outline-none text-center placeholder:opacity-40"
                  style={{ fontFamily: "'DM Serif Display', serif", fontSize: '44px', color: txTypeAccent('transfer'), width: '55%', caretColor: txTypeAccent('transfer') }}
                />
              </div>
            </div>

            {/* Note */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Note <span className="text-gray-300">(optional)</span></p>
              <input
                type="text"
                value={newTransfer.description}
                onChange={(e) => setNewTransfer(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Add a note"
                className="w-full h-14 bg-white rounded-2xl px-4 shadow-sm outline-none text-[15px] text-gray-800 placeholder:text-gray-300" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, caretColor: '#1F2937' }}
              />
            </div>

            {/* Date */}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Date</p>
              <label
                htmlFor="worthly-transaction-date"
                className="flex items-center gap-3 bg-white rounded-2xl px-4 h-14 shadow-sm w-full cursor-pointer"
                onClick={(e) => {
                  // Belt-and-suspenders: native <label> click-forwarding works on iOS,
                  // but desktop browsers sometimes only focus. showPicker() forces it open.
                  const input = e.currentTarget.querySelector('input[type="date"]');
                  if (input && typeof input.showPicker === 'function') {
                    try { input.showPicker(); } catch (_) { /* not supported, label fallback handles it */ }
                  }
                }}
              >
                <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">📅</div>
                <input
                  id="worthly-transaction-date"
                  type="date"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                  className="flex-1 bg-transparent text-[14.5px] text-gray-800 focus:outline-none cursor-pointer"
                />
              </label>
            </div>

            <button
              onClick={() => { addTransferTransaction(); setAddFormType(null); setShowAddMenu(false); }}
              className="w-full flex items-center justify-center gap-1.5 rounded-full py-3.5 text-white font-semibold text-[15px] shadow-md disabled:opacity-40"
              style={txCtaStyle}
              disabled={!newTransfer.fromCard || !newTransfer.toCard || !newTransfer.amount}
            >
              Transfer
            </button>
          </div>
        </div>
      )}
        </div>
      )}

      </div>
    </div>
  </div>
)}


{!showBudgetSetup && !showYearlyStats && (
<>
{/* Payment Method Totals - Stacked Vertical, collapsible */}
{!plan && <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mb-2 mt-6" style={{ color: isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE' }}><span className="wy-star">✦ </span>Payment methods</p>}
<button
  type="button"
  onClick={() => setShowPaymentMethods(v => !v)}
  className={"w-full flex items-center justify-between bg-white rounded-2xl px-5 py-4 shadow-sm mb-2" + (plan ? " wy-plan-xhead" : "")}
  data-open={showPaymentMethods ? '1' : undefined}
>
  {plan ? (() => {
    const rawTotal = cardTotals.reduce((sum, ct) => sum + (ct.type === 'credit' ? -ct.balance : ct.balance), 0);
    const total = Math.abs(rawTotal) < 0.005 ? 0 : rawTotal; // dark_12
    return (
      <div className="text-left min-w-0">
        <p className="m-0" style={{ fontSize: 16.5, fontWeight: 700, letterSpacing: '-0.5px', color: '#26252E' }}>Payment methods {'\u{1F4B3}'}</p>{/* w2_12 */}
        <p className="m-0 truncate" style={{ fontSize: 12.5, color: '#8B879A', marginTop: 2 }}>
          {cardTotals.length} account{cardTotals.length === 1 ? '' : 's'}{cardTotals.length > 0 ? ' \u00b7 ' + (total < 0 ? '\u2212' : '') + formatCurrency(Math.abs(total)) : ''}
        </p>
      </div>
    );
  })() : (
  <div className="text-left min-w-0">
    <p className="text-[14px] text-gray-800" style={{ fontWeight: 500 }}>{cardTotals.length} account{cardTotals.length === 1 ? '' : 's'}</p>
    {!showPaymentMethods && (
      <p className="text-[13px] text-gray-400 font-medium mt-0.5 truncate">
        {cardTotals.length > 0 ? cardTotals.map(ct => ct.card).join(', ') : 'No accounts yet'}
      </p>
    )}
  </div>
  )}
  <div className="flex items-center gap-2 flex-shrink-0">
    {!plan && !showPaymentMethods && (() => {
      const total = cardTotals.reduce((sum, ct) => sum + (ct.type === 'credit' ? -ct.balance : ct.balance), 0);
      return (
        <span className="text-[15px] font-bold" style={{ color: total < 0 ? negInk : moneyInk }}>
          {total < 0 ? '-' : ''}{formatCurrency(Math.abs(total))}
        </span>
      );
    })()}
    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
      isPink ? 'bg-pink-50' : isWarm ? 'bg-gray-100' : 'bg-indigo-50'
    }`}>
      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showPaymentMethods ? 'rotate-180' : ''}`} />
    </div>
  </div>
</button>
{showPaymentMethods && (
<div className={plan ? "wy-plan-xbody wy-plan-pm mb-2" : "space-y-2 mb-4"}>
  {cardTotals.map((rawCt, idx) => {
    // dark_12: a balance that rounds to zero shows as $0.00, never -$0.00
    const ct = Math.abs(rawCt.balance) < 0.005 ? { ...rawCt, balance: 0 } : rawCt;
    const colors = colorMap[ct.color] || colorMap.grey;
    const isExpanded = viewingMonthCardTransactions === ct.card;
    const isCredit = ct.type === 'credit';
    
    return (
      <div 
        key={ct.card} 
        className="relative px-3 py-3 rounded-2xl bg-white shadow-sm cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setViewingMonthCardTransactions(isExpanded ? null : ct.card)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 text-xl ${colors.bgLight}`}>
              {ct.card === 'Cash' ? '💵' : '💳'}
            </div>
            <div>
              <p className={`text-sm font-semibold ${colors.text}`}>{ct.card}</p>
              <p className="text-[10px] text-gray-500">{ct.card === 'Cash' ? 'Cash' : plan ? (isCredit ? 'Credit card' : 'Debit card') : (isCredit ? 'Credit Card' : 'Debit Card')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold" style={{ color:
              isCredit
                ? (ct.balance > 0 ? negInk : moneyInk)
                : (ct.balance >= 0 ? moneyInk : negInk)
            }}>
              {isCredit && ct.balance > 0 ? '-' : ''}{!isCredit && ct.balance < 0 ? '-' : ''}{formatCurrency(Math.abs(ct.balance))}
            </p>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </div>
        </div>
      </div>
    );
  })}

  {/* Locked cards – blurred, tappable to show paywall */}
  {allCards.slice(cardsLimit).map((card) => {
    const colors = colorMap[card.color] || colorMap.grey;
    const isCredit = card.type === 'credit';
    return (
      <div
        key={card.name}
        className="relative px-3 py-3 rounded-2xl bg-white shadow-sm cursor-pointer select-none overflow-hidden"
        onClick={() => showFeaturePaywall('cards')}
      >
        <div className="flex items-center justify-between opacity-30 blur-[2px] pointer-events-none">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 text-xl ${colors.bgLight}`}>
              💳
            </div>
            <div>
              <p className={`text-sm font-semibold ${colors.text}`}>{card.name}</p>
              <p className="text-[10px] text-gray-500">{isCredit ? 'Credit Card' : 'Debit Card'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold text-gray-400">$0.00</p>
          </div>
        </div>
        <div
          className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-sm rounded-2xl"
          style={{ pointerEvents: 'none' }}
        >
          <div className="rounded-full flex items-center" style={{ background: '#fff', color: (isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE'), padding: '6px 14px', fontSize: '13px', fontWeight: 700, fontFamily: "'DM Sans', sans-serif", boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>{'\uD83D\uDD12'} Premium</div>
        </div>
      </div>
    );
  })}
</div>
)}

{/* Month Card Transactions List */}
{viewingMonthCardTransactions && (
  <div className="mb-4 bg-white rounded-2xl overflow-hidden shadow-sm">
    {(() => {
      const cardName = viewingMonthCardTransactions;

      // Everything that touched this card this month.
      const cardTransactions = viewMonthTransactions.filter(t => {
        if (t.payment_method === cardName && !t.is_income && !t.is_savings && !t.is_transfer) return true;
        if (t.is_income && t.to_card === cardName) return true;
        if (t.is_transfer && t.payment_method === cardName) return true;
        if (t.is_transfer && t.to_card === cardName) return true;
        if (t.is_savings && t.transaction_type === 'deposit' && t.from_card === cardName) return true;
        if (t.is_savings && t.transaction_type === 'withdrawal' && t.to_card === cardName) return true;
        return false;
      }).sort((a, b) => new Date(b.date) - new Date(a.date));

      return (
        <>
          {/* A title, not a toolbar. The grey strip and full-bleed rules were
              what made this read as a spreadsheet. */}
          <div className="px-4 pt-3.5 pb-2.5 flex items-baseline gap-2">
            <span className="text-[15px] font-bold" style={{ color: '#1A1A1F' }}>{cardName}</span>
            <span className="flex-1 min-w-0 text-[11.5px] font-medium truncate" style={{ color: mutedInk }}>
              {monthNames[viewMonth.getMonth()]} · {cardTransactions.length} transaction{cardTransactions.length === 1 ? '' : 's'}
            </span>
            <button
              onClick={() => setViewingMonthCardTransactions(null)}
              aria-label="Close"
              className="flex-shrink-0"
              style={{ color: '#B9B2BD' }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="max-h-72 overflow-y-auto">
            {cardTransactions.length === 0 ? (
              <p className="px-4 pb-4 text-[13px] font-medium" style={{ color: mutedInk }}>
                No transactions with {cardName} in {monthNames[viewMonth.getMonth()]}.
              </p>
            ) : cardTransactions.map((t, i) => {
              let label, amount, color;
              if (t.is_income && t.to_card === cardName) {
                label = t.description || 'Income';
                amount = `+${formatCurrency(t.amount)}`;
                color = moneyInk;
              } else if (t.is_savings && t.transaction_type === 'withdrawal' && t.to_card === cardName) {
                label = t.description || 'Withdrawal from Savings';
                amount = `+${formatCurrency(t.amount)}`;
                color = moneyInk;
              } else if (t.is_savings && t.transaction_type === 'deposit' && t.from_card === cardName) {
                label = t.description || 'Deposit to Savings';
                amount = `-${formatCurrency(t.amount)}`;
                color = isWarm ? '#5D6B4D' : '#8B5CF6';
              } else if (t.is_transfer && t.to_card === cardName) {
                label = t.description || `Transfer from ${t.transfer_from || 'card'}`;
                amount = `+${formatCurrency(t.amount)}`;
                color = moneyInk;
              } else if (t.is_transfer && t.payment_method === cardName) {
                label = t.description || `Transfer to ${t.transfer_to || 'card'}`;
                amount = `-${formatCurrency(t.amount)}`;
                color = negInk;
              } else {
                label = t.description || t.category;
                amount = `-${formatCurrency(t.amount)}`;
                color = negInk;
              }

              const kind = t.is_income ? 'Income'
                : t.is_transfer ? 'Transfer'
                : t.is_savings ? (t.transaction_type === 'withdrawal' ? 'Savings withdrawal' : 'Savings deposit')
                : t.is_debt_payment ? 'Debt payment'
                : (() => { const { name } = parseCategoryEmoji(t.category || ''); return name || t.category; })();

              return (
                <div
                  key={t.id}
                  className="flex items-center gap-2.5 px-4 py-2.5"
                  style={i === 0 ? undefined : { borderTop: '1px solid #F6F3F7' }}
                >
                  <span
                    className="flex items-center justify-center flex-shrink-0"
                    style={{ width: '34px', height: '34px', borderRadius: '11px', background: rowTileBg, fontSize: '15px' }}
                  >{txEmoji(t)}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[14px] font-semibold truncate" style={{ color: '#1F2937' }}>{label}</span>
                    <span className="block text-[11.5px] font-medium truncate" style={{ color: mutedInk, marginTop: '1px' }}>
                      {new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      {kind ? ` · ${kind}` : ''}
                      {t.is_recurring ? ' · Recurring' : ''}
                    </span>
                  </span>
                  <span className="text-[14px] font-bold flex-shrink-0" style={{ color }}>{amount}</span>
                </div>
              );
            })}
          </div>
        </>
      );
    })()}
  </div>
)}

{/* Monthly Income Breakdown Donut Chart - Premium Feature, collapsible */}
{!plan && <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mb-2 mt-6" style={{ color: isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE' }}><span className="wy-star">✦ </span>{monthNames[viewMonth.getMonth()]} breakdown</p>}
<button
  type="button"
  onClick={() => (hasAccess('monthlyDonut') ? setShowMonthlyBreakdown(v => !v) : showFeaturePaywall('monthlyDonut'))}
  className={"w-full flex items-center justify-between bg-white rounded-2xl px-5 py-4 shadow-sm mb-2" + (plan ? " wy-plan-xhead" : "")}
  data-open={showMonthlyBreakdown && hasAccess('monthlyDonut') ? '1' : undefined}
>
  <div className="text-left min-w-0">
    <p className={plan ? "m-0" : "text-[14px] text-gray-800"} style={plan ? { fontSize: 16.5, fontWeight: 700, letterSpacing: '-0.5px', color: '#26252E' } : { fontWeight: 500 }}>Where your income went{plan ? ' \u{1F4B8}' : ''}</p>{/* w2_12 */}
    {!showMonthlyBreakdown && (
      <p className="text-[13px] text-gray-400 font-medium mt-0.5">
        {totalIncomeForMonth > 0 ? (plan ? `${monthSpentPct}% spent · ${monthSavedPct}% saved · ${monthDebtPct}% debt` : `${monthSpentPct}% spent · ${monthSavedPct}% saved · ${monthDebtPct}% debt · ${monthUnallocatedPct}% left`) : 'No income logged yet'}
      </p>
    )}
  </div>
  <div className="flex items-center gap-2 flex-shrink-0">
    {!plan && !showMonthlyBreakdown && totalIncomeForMonth > 0 && (
      <div
        className="w-6 h-6 rounded-full flex-shrink-0"
        style={{ background: `conic-gradient(${chartPal.income.expenses} 0% ${monthSpentPct}%, ${chartPal.income.savings} ${monthSpentPct}% ${monthSpentPct + monthSavedPct}%, ${chartPal.income.debt} ${monthSpentPct + monthSavedPct}% ${monthSpentPct + monthSavedPct + monthDebtPct}%, ${chartPal.income.unallocated} ${monthSpentPct + monthSavedPct + monthDebtPct}% 100%)` }}
      />
    )}
    {hasAccess('monthlyDonut') ? (
    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
      isPink ? 'bg-pink-50' : isWarm ? 'bg-gray-100' : 'bg-indigo-50'
    }`}>
      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showMonthlyBreakdown ? 'rotate-180' : ''}`} />
    </div>
    ) : (
      <div className="rounded-full flex items-center" style={{ background: '#fff', color: (isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE'), padding: '6px 14px', fontSize: '13px', fontWeight: 700, fontFamily: "'DM Sans', sans-serif", boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>{'\uD83D\uDD12'} Premium</div>
    )}
  </div>
</button>
{showMonthlyBreakdown && hasAccess('monthlyDonut') && (
<PremiumFeature feature="monthlyDonut" className="mb-0">
  <div className={"bg-white rounded-2xl p-4 shadow-sm" + (plan ? " wy-plan-xbody" : "")}>
    {/* Header */}
    <div className="flex items-center justify-between mb-4">
      {/* Toggle between Income and Expenses view — built to match the
          Spending / Savings / Debts pills exactly: same wash behind it,
          same 4px inset, same 13.5px type, same white active chip and
          accent, same muted inactive. It was on chartPal.toggle.track,
          which is a deeper tint than the category card's wash, and on
          toggle.inactive, which is lighter than the pills' ink. */}
      <div className="flex rounded-full" style={{ background: plan ? '#ECE8F4' : rowTileBg, padding: '4px' }}>
        {[
          { id: 'income', label: 'Income' },
          { id: 'expenses', label: 'Expenses' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setMonthlyChartView(t.id)}
            className="rounded-full transition-colors"
            style={monthlyChartView === t.id
              ? { padding: '9px 18px', fontSize: '13.5px', fontWeight: 700, color: chartPal.toggle.active, background: '#FFFFFF', boxShadow: '0 1px 3px rgba(31,41,55,0.10)' }
              : { padding: '9px 18px', fontSize: '13.5px', fontWeight: 600, color: mutedInk, background: 'transparent' }}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  
  {totalIncomeForMonth === 0 && totalSpent === 0 ? (
    <div className="text-center py-8">
      <PieChart className="w-12 h-12 mx-auto mb-3 text-gray-300" />
      <p className="text-sm text-gray-400">Add transactions to see your spending breakdown</p>
    </div>
  ) : monthlyChartView === 'income' ? (
      /* Income Breakdown - New Design */
      (() => {
        const monthDebt = viewMonthTransactions
          .filter(t => t.is_debt_payment)
          .reduce((sum, t) => sum + t.amount, 0);
        const monthExpenses = totalSpent - monthDebt;
        const monthSavings = totalSavingsDeposits;
        const monthUnallocated = Math.max(0, totalIncomeForMonth - totalSpent - monthSavings);
        
        const total = totalIncomeForMonth;
        const segments = [];
        
        // Theme-aware colors
        const expenseColor = chartPal.income.expenses;
        const savingsColor = chartPal.income.savings;
        const debtColor = chartPal.income.debt;
        const unallocatedColor = chartPal.income.unallocated;
        
        if (monthExpenses > 0) segments.push({ value: monthExpenses, color: expenseColor, label: 'Expenses' });
        if (monthSavings > 0) segments.push({ value: monthSavings, color: savingsColor, label: 'Savings & Investments' });
        if (monthDebt > 0) segments.push({ value: monthDebt, color: debtColor, label: 'Debt' });
        if (monthUnallocated > 0) segments.push({ value: monthUnallocated, color: unallocatedColor, label: 'Unallocated' });
        
        if (segments.length === 0) {
          return <div className="text-center py-4 text-gray-400 text-sm">No data available</div>;
        }
        
        // Build conic-gradient for donut chart
        let currentPercent = 0;
        const gradientStops = [];
        const segmentsWithPercent = segments.map(seg => {
          const percent = (seg.value / total) * 100;
          gradientStops.push(chartStop(seg.color, currentPercent, percent, segments.length > 1));
          currentPercent += percent;
          return { ...seg, percent };
        });
        const conicGradient = `conic-gradient(from 0deg, ${gradientStops.join(', ')})`;
        
        return (
          <div className="flex items-center gap-6">
            {/* Donut Chart */}
            <div className="relative flex-shrink-0" style={{ width: 130, height: 130 }}>
              <div 
                style={{
                  width: 130,
                  height: 130,
                  borderRadius: '50%',
                  background: conicGradient,
                  mask: 'radial-gradient(circle, transparent 40px, black 41px)',
                  WebkitMask: 'radial-gradient(circle, transparent 40px, black 41px)'
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-lg font-bold text-gray-800">{formatCurrencyShort(total)}</p>
                  <p className="text-xs" style={{ color: chartPal.centre }}>Income</p>
                </div>
              </div>
            </div>
            
            {/* Legend with progress bars */}
            <div className="flex-1 space-y-3">
              {segmentsWithPercent.map((seg, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: seg.color }} />
                      <span className="text-sm font-medium text-gray-700">{seg.label}</span>
                    </div>
                    <span className="text-sm font-bold" style={{ color: chartTextColor(chartPal, seg.color) }}>{seg.percent.toFixed(0)}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all"
                      style={{ width: `${seg.percent}%`, backgroundColor: seg.color }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()
    ) : (
      /* Expenses by Category Breakdown - New Design */
      (() => {
        const categoryTotals = {};
        expenseTransactions.forEach(t => {
          const cat = t.category || 'Other';
          categoryTotals[cat] = (categoryTotals[cat] || 0) + t.amount;
        });
        
        const sortedCategories = Object.entries(categoryTotals)
          .sort((a, b) => b[1] - a[1]);
        
        const total = sortedCategories.reduce((sum, [_, val]) => sum + val, 0);
        
        if (total === 0) {
          return (
            <div className="text-center py-4 text-gray-400 text-sm">
              No expenses this month
            </div>
          );
        }
        
        // Theme-aware category colors
        const categoryColors = chartPal.cats;
        
        const segments = sortedCategories.map(([cat, val], i) => ({
          value: val,
          color: categoryColors[i % categoryColors.length],
          label: cat
        }));
        
        // Build conic-gradient for donut chart
        let currentPercent = 0;
        const gradientStops = [];
        const segmentsWithPercent = segments.map(seg => {
          const percent = (seg.value / total) * 100;
          gradientStops.push(chartStop(seg.color, currentPercent, percent, segments.length > 1));
          currentPercent += percent;
          return { ...seg, percent };
        });
        const conicGradient = `conic-gradient(from 0deg, ${gradientStops.join(', ')})`;
        
        return (
          <div className="flex items-center gap-6">
            {/* Donut Chart */}
            <div className="relative flex-shrink-0" style={{ width: 130, height: 130 }}>
              <div 
                style={{
                  width: 130,
                  height: 130,
                  borderRadius: '50%',
                  background: conicGradient,
                  mask: 'radial-gradient(circle, transparent 40px, black 41px)',
                  WebkitMask: 'radial-gradient(circle, transparent 40px, black 41px)'
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-lg font-bold text-gray-800">{formatCurrencyShort(total)}</p>
                  <p className="text-xs" style={{ color: chartPal.centre }}>Spent</p>
                </div>
              </div>
            </div>
            
            {/* Legend with progress bars */}
            <div className="flex-1 space-y-2.5">
              {(showAllMonthlyCategories ? segmentsWithPercent : segmentsWithPercent.slice(0, 4)).map((seg, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: seg.color }} />
                      <span className="text-xs font-medium text-gray-700">{seg.label}</span>
                    </div>
                    <span className="text-xs font-bold" style={{ color: chartTextColor(chartPal, seg.color) }}>{seg.percent.toFixed(0)}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all"
                      style={{ width: `${seg.percent}%`, backgroundColor: seg.color }}
                    />
                  </div>
                </div>
              ))}
              {segmentsWithPercent.length > 4 && (
                <button 
                  onClick={() => setShowAllMonthlyCategories(!showAllMonthlyCategories)}
                  className="text-xs font-medium pt-1 theme-text-500"
                  data-testid="toggle-monthly-categories"
                >
                  {showAllMonthlyCategories ? 'Show less' : `+${segmentsWithPercent.length - 4} more categories`}
                </button>
              )}
            </div>
          </div>
        );
      })()
    )}
  </div>
</PremiumFeature>
)}

{/* Combined Transactions Section */}
<div className={plan ? "wy-plan-txcard" : undefined}>
<div className="flex items-center justify-between mt-6 mb-2">
  <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1" style={{ color: isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE' }}><span className="wy-star">✦ </span>Transactions</p>
  <button
    onClick={() => {
      setShowTransactionSearch(!showTransactionSearch);
      if (showTransactionSearch) setTransactionSearch('');
    }}
    className={`p-1.5 rounded-lg transition-colors ${showTransactionSearch ? 'theme-bg-100 theme-text-600' : 'text-gray-400 hover:theme-text-500 hover:theme-bg-50'}`}
    data-testid="toggle-transaction-search"
  >
    <Search className="w-4 h-4" />
  </button>
</div>
<div className="flex items-center justify-between mb-3">
  {plan ? (
    <div className="flex" style={{ background: '#ECE8F4', borderRadius: 999, padding: 3, width: '62%' }}>
      {[['posted', 'Posted'], ['scheduled', 'Upcoming']].map(([v, lab]) => (
        <button
          key={v}
          type="button"
          onClick={() => setTransactionViewMode(v)}
          style={{ flex: 1, border: 0, borderRadius: 999, padding: '7px 4px', fontSize: 13, fontWeight: transactionViewMode === v ? 600 : 500, color: transactionViewMode === v ? '#26252E' : '#6C6879', background: transactionViewMode === v ? '#FFFFFF' : 'transparent', boxShadow: transactionViewMode === v ? '0 1px 3px rgba(60,40,120,0.08)' : 'none' }}
        >
          {lab}
        </button>
      ))}
    </div>
  ) : (
  <select
    value={transactionViewMode}
    onChange={(e) => setTransactionViewMode(e.target.value)}
    className="text-sm rounded-full border theme-border-200 px-4 py-2 bg-white theme-text-600 font-semibold"
  >
    <option value="posted">Posted</option>
    <option value="scheduled">Scheduled</option>
  </select>
  )}
  <span className="text-xs text-gray-400 font-medium">
    {plan ? `${viewMonthTransactions.length} this month` : `${viewMonthTransactions.length} transaction${viewMonthTransactions.length !== 1 ? 's' : ''}`}
  </span>
</div>
<div className="mb-10">
  <div className="">
    {showTransactionSearch && (
      <div className="mb-3 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          type="text"
          value={transactionSearch}
          onChange={(e) => setTransactionSearch(e.target.value)}
          placeholder="Search by category, description, amount..."
          className="pl-9 theme-border-200"
          autoFocus
        />
        {transactionSearch && (
          <button
            onClick={() => setTransactionSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    )}
  </div>
  
  {/* Pending Recurring Transactions Banner */}
  {pendingRecurring.length > 0 && isCurrentMonth && (
    <div className="mx-3 my-2 px-3 py-3 rounded-2xl bg-white shadow-sm flex items-center gap-3">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-lg" style={{ backgroundColor: plan ? '#F5F2FA' : isPink ? '#FBE3ED' : isWarm ? '#F0E6D8' : '#EEF1FE' }}>
        🔔
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800" style={{ fontWeight: 500 }}>
          {pendingRecurring.length} recurring transaction{pendingRecurring.length > 1 ? 's' : ''} due
        </p>
        <p className="text-xs text-gray-500">Tap to confirm and add to your transactions</p>
      </div>
      <Button
        size="sm"
        onClick={() => setShowRecurringPrompt(true)}
        className="text-xs px-3 hover:opacity-90 flex-shrink-0 border-none font-semibold"
        style={plan ? { backgroundColor: '#1F1E24', color: '#FFFFFF' } : { backgroundColor: isWarm ? '#2D5A3D' : isPink ? '#DCECC4' : '#4361EE', color: isWarm ? '#fff' : isPink ? '#2F6B44' : '#fff' }}
      >
        Review
      </Button>
    </div>
  )}
  
  {transactionViewMode === 'scheduled' ? (
    /* Scheduled View */
    (() => {
      const recurringTransactions = budgetSettings?.recurring_transactions || [];
      
      if (recurringTransactions.length === 0) {
        return (
          <div className="p-8 text-center text-gray-400">
            <RefreshCw className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No recurring transactions set up</p>
            <p className="text-xs mt-1">Add them in Settings → Recurring Transactions</p>
          </div>
        );
      }
      
      // Calculate scheduled items for viewing month
      const viewMonthStart = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
      const viewMonthEnd = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0);
      
      const scheduledItems = [];
      
      scheduledItems.push(...enumerateOccurrences({
        recurring: recurringTransactions,
        windowStart: viewMonthStart,
        windowEnd: viewMonthEnd,
        transactions: viewMonthTransactions,
        viewMonth,
      }));

      // Filter out items that have been added - only show pending items
      const pendingItems = scheduledItems.filter(s => !s.isAdded);
      
      if (pendingItems.length === 0) {
        return (
          <div className="p-8 text-center text-gray-400">
            <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>All scheduled transactions have been posted this month</p>
          </div>
        );
      }
      
      pendingItems.sort((a, b) => a.scheduledDate - b.scheduledDate);
      const pendingCount = pendingItems.length;
      
      return (
        <div>
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="text-xs text-gray-400 font-medium">{pendingCount} scheduled transaction{pendingCount !== 1 ? 's' : ''}</span>

          </div>
          <div className="max-h-[600px] overflow-y-auto pt-2">
            {pendingItems.map((item, idx) => {
              const isPast = item.scheduledDate <= new Date();
              const isDue = isPast && isCurrentMonth;
              const scheduledDateKey = item.scheduledDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
              
              // Get account name for savings/investment items
              let accountName = null;
              if (item.type === 'savings' && item.account_id) {
                if (item.account_type === 'fund') {
                  const fund = (budgetSettings?.sinking_funds || []).find(f => f.id === item.account_id);
                  accountName = fund ? fund.name : null;
                } else if (item.account_type === 'investment') {
                  const inv = (budgetSettings?.investment_accounts || []).find(i => i.id === item.account_id);
                  accountName = inv ? inv.name : null;
                }
              }
              
              // Get debt name for debt payment items
              let debtName = null;
              if (item.type === 'debt' && item.debt_id) {
                const debt = (userDebts || []).find(d => d.id === item.debt_id);
                debtName = debt ? debt.name : null;
              }
              return (
                <div key={`${item.id}-${idx}`} className="mb-2.5">
                  <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                    <div className="flex items-center justify-between px-4 py-3.5 theme-bg-50 border-b theme-border-100">
                      <p className="text-base font-bold theme-text-600 uppercase tracking-wide">{scheduledDateKey}</p>
                      {isDue && <p className="text-base font-semibold text-blue-400">Due</p>}
                    </div>
                    <div className="px-4 py-2.5 flex items-center gap-3">
                  {(() => {
                    const schedEmoji = item.type === 'income' ? '💸'
                      : item.type === 'savings' ? '🏦'
                      : item.type === 'debt' ? '🧾'
                      : item.type === 'transfer' ? '↔️'
                      : (() => { const { emoji } = parseCategoryEmoji(item.category || ''); return emoji; })();
                    const schedBg = item.type === 'income'
                      ? (isWarm ? warmColors.income.bg : '#dcfce7')
                      : item.type === 'savings'
                        ? (isWarm ? warmColors.savings.bg : '#f3e8ff')
                        : item.type === 'debt'
                          ? (isWarm ? warmColors.spent.bg : '#fee2e2')
                          : item.type === 'transfer'
                            ? (isWarm ? warmColors.investments.bg : '#dbeafe')
                            : (isWarm ? warmColors.spent.bg : '#fce7f3');
                    const payCard = cards?.find(c => c.name === item.payment_card);
                    const schedHexMap = {
                      grey: '#6b7280', red: '#ef4444', blue: '#2b6cee',
                      orange: '#f97316', green: '#10b981', pink: '#e879a0', purple: '#8b5cf6'
                    };
                    const payHex = payCard?.color ? schedHexMap[payCard.color] : null;
                    return (
                      <>
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: schedBg }}>
                          {schedEmoji ? (
                            <span className="text-base leading-none">{schedEmoji}</span>
                          ) : (
                            <Tag className={`w-3.5 h-3.5 ${isWarm ? 'text-[#4A6B6B]' : 'theme-text-500'}`} />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold truncate text-gray-800">
                            {item.type === 'debt' ? `Payment to ${debtName || item.description || 'Debt'}`
                              : item.type === 'savings' ? (item.description || (accountName ? `→ ${accountName}` : 'Deposit'))
                              : item.type === 'income' ? (item.description || 'Income')
                              : (() => { const { name } = parseCategoryEmoji(item.category || ''); return name || item.description || 'Expense'; })()}
                          </p>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            {item.payment_card && (
                              <span className="flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: payHex || '#d1d5db' }} />
                                <span className="text-xs text-gray-400">{item.payment_card}</span>
                              </span>
                            )}
                            {accountName && <span className="text-xs text-gray-400">→ {accountName}</span>}
                            {debtName && item.type !== 'debt' && <span className="text-xs text-gray-400">{debtName}</span>}
                          </div>
                        </div>
                        <span className={`text-sm font-bold ${item.type === 'income' ? (isWarm ? warmColors.income.text : 'text-[#10b981]') : 'text-gray-700'}`}>
                          {item.type === 'income' ? '+' : '-'}{getCurrentCurrency().symbol}{parseFloat(item.amount).toFixed(2)}
                        </span>
                        <button
                          onClick={() => addRecurringTransaction(item)}
                          className="px-2.5 py-1 text-xs font-semibold rounded-lg text-white flex-shrink-0"
                          style={txCtaStyle}
                        >
                          Add
                        </button>
                      </>
                    );
                  })()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    })()
  ) : (
    /* Posted View */
    (() => {
      const isSearching = transactionSearch.length > 0;
      
      const filteredTransactions = isSearching 
        ? viewMonthTransactions.filter(t => {
            const searchLower = transactionSearch.toLowerCase();
            const category = (t.category || '').toLowerCase();
            const description = (t.description || '').toLowerCase();
            const amount = t.amount.toString();
            const paymentMethod = (t.payment_method || '').toLowerCase();
            const fromCard = (t.from_card || '').toLowerCase();
            const toCard = (t.to_card || '').toLowerCase();
            let accountName = '';
            if (t.account_id && t.account_type === 'fund') {
              const fund = allSinkingFunds.find(f => f.id === t.account_id);
              accountName = (fund?.name || '').toLowerCase();
            } else if (t.account_id && t.account_type === 'investment') {
              const inv = allInvestmentAccounts.find(i => i.id === t.account_id);
              accountName = (inv?.name || '').toLowerCase();
            }
            return category.includes(searchLower) ||
                   description.includes(searchLower) ||
                   amount.includes(searchLower) ||
                   paymentMethod.includes(searchLower) ||
                   fromCard.includes(searchLower) ||
                   toCard.includes(searchLower) ||
                   accountName.includes(searchLower);
          })
        : viewMonthTransactions;
      
      if (viewMonthTransactions.length === 0) {
        return (
          <div className="p-8 text-center text-gray-400">
            <Wallet className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No transactions {isCurrentMonth ? 'this month yet' : 'for this month'}</p>
          </div>
        );
      }
      
      if (filteredTransactions.length === 0) {
        return (
          <div className="p-8 text-center text-gray-400">
            <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No transactions matching "{transactionSearch}"</p>
          </div>
        );
      }

      // Group transactions by date
      const groupedByDate = {};
      filteredTransactions.forEach(t => {
        const dateKey = new Date(t.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        if (!groupedByDate[dateKey]) groupedByDate[dateKey] = [];
        groupedByDate[dateKey].push(t);
      });
      const dateGroups = Object.entries(groupedByDate).sort((a, b) => 
        new Date(b[1][0].date) - new Date(a[1][0].date)
      );
      
      return (
        <div className="max-h-[600px] overflow-y-auto pt-2">
          {isSearching ? (
          // Flat list with dates when searching
          <div className="divide-y divide-theme-50">
            {filteredTransactions.map(transaction => (
              <TransactionRow 
                key={transaction.id}
                transaction={transaction}
                theme={theme}
                showDate={true}
                budgetSettings={budgetSettings}
                editingTransaction={editingTransaction}
                editTransactionData={editTransactionData}
                setEditTransactionData={setEditTransactionData}
                editCategorySearch={editCategorySearch}
                setEditCategorySearch={setEditCategorySearch}
                showEditCategoryDropdown={showEditCategoryDropdown}
                setShowEditCategoryDropdown={setShowEditCategoryDropdown}
                editFilteredCategories={editFilteredCategories}
                selectEditCategory={selectEditCategory}
                saveEditTransaction={saveEditTransaction}
                cancelEditTransaction={cancelEditTransaction}
                startEditTransaction={startEditTransaction}
                removeTransaction={removeTransaction}
                toggleTransactionCompleted={toggleTransactionCompleted}
                setViewingTransaction={setViewingTransaction}
                getCardColors={getCardColors}
                getAccountName={getAccountName}
                getDebtName={getDebtName}
                formatCurrency={formatCurrency}
                colorMap={colorMap}
                cards={paymentMethods}
                debts={userDebts}
                theme={theme}
              />
            ))}
          </div>
        ) : (
          // Grouped by date when not searching — only the most recent
          // day is open by default; older days collapse to a summary row
          <>
            {(dateGroups).map(([dateKey, groupTxns], groupIdx) => {
              const dayNet = groupTxns.reduce((sum, t) => {
                if (t.is_income || (t.is_savings && t.transaction_type === 'withdrawal')) return sum + t.amount;
                return sum - t.amount;
              }, 0);
              const isOpen = dayGroupOverrides[dateKey] !== undefined
                ? dayGroupOverrides[dateKey]
                : groupIdx === 0;
              return (
                <div key={dateKey} className="mb-2.5">
                  <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                    <button
                      type="button"
                      onClick={() => setDayGroupOverrides(prev => ({ ...prev, [dateKey]: !isOpen }))}
                      className="w-full flex items-center justify-between px-4 py-2.5 bg-white border-b border-gray-100"
                    >
                      <p className="text-sm font-bold theme-text-600 uppercase tracking-wide">{dateKey}</p>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isOpen && (
                    <div className="divide-y divide-gray-50">
                      {groupTxns.map(transaction => (
                        <TransactionRow 
                          key={transaction.id}
                          transaction={transaction}
                          theme={theme}
                          showDate={false}
                          budgetSettings={budgetSettings}
                          editingTransaction={editingTransaction}
                          editTransactionData={editTransactionData}
                          setEditTransactionData={setEditTransactionData}
                          editCategorySearch={editCategorySearch}
                          setEditCategorySearch={setEditCategorySearch}
                          showEditCategoryDropdown={showEditCategoryDropdown}
                          setShowEditCategoryDropdown={setShowEditCategoryDropdown}
                          editFilteredCategories={editFilteredCategories}
                          selectEditCategory={selectEditCategory}
                          saveEditTransaction={saveEditTransaction}
                          cancelEditTransaction={cancelEditTransaction}
                          startEditTransaction={startEditTransaction}
                          removeTransaction={removeTransaction}
                          toggleTransactionCompleted={toggleTransactionCompleted}
                          setViewingTransaction={setViewingTransaction}
                          getCardColors={getCardColors}
                          getAccountName={getAccountName}
                          getDebtName={getDebtName}
                          formatCurrency={formatCurrency}
                          colorMap={colorMap}
                          cards={paymentMethods}
                          debts={userDebts}
                          theme={theme}
                        />
                      ))}
                    </div>
                    )}
                  </div>
                </div>
              );
            })}

          </>
        )}
        </div>
      );
    })()
  )}
</div>
</div>
</>
)}

{/* Recurring Transactions Review Modal */}
{showRecurringPrompt && pendingRecurring.length > 0 && (
  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowRecurringPrompt(false)}>
    <div 
      className={`bg-white rounded-2xl shadow-xl max-w-sm w-full max-h-[70vh] overflow-hidden${plan ? ' wy-plan-recurring' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header — pale pink wash, matches the theme */}
      <div className="p-4" style={{ backgroundColor: plan ? '#FFFFFF' : isPink ? '#FCEEF3' : isWarm ? '#F0E6D8' : '#EEF1FE' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-lg flex-shrink-0" style={{ backgroundColor: plan ? '#F5F2FA' : isPink ? '#FBE3ED' : isWarm ? '#F0E6D8' : '#EEF1FE' }}>
              🔔
            </div>
            <div>
              {plan
                ? <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, letterSpacing: '-0.4px', color: '#26252E' }}>Recurring transactions due</h3>
                : <h3 className="text-[13px] font-bold uppercase tracking-[0.08em]" style={{ color: isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE' }}>Recurring Transactions Due</h3>}
              <p className="text-xs text-gray-500 mt-0.5">{pendingRecurring.length} item{pendingRecurring.length > 1 ? 's' : ''} to review</p>
            </div>
          </div>
          <button onClick={() => setShowRecurringPrompt(false)} className="w-7 h-7 rounded-full text-gray-500 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(255,255,255,0.7)' }}>
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      
      {/* List of pending recurring transactions */}
      <div className="p-3 space-y-2 max-h-[45vh] overflow-y-auto">
        {pendingRecurring.map(rec => {
          const cardInfo = rec.payment_card ? paymentMethods.find(c => c.name === rec.payment_card) : null;
          return (
            <div key={rec.id} className="rounded-xl p-3 bg-white">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0" style={{
                    backgroundColor: plan ? '#F5F2FA' : rec.type === 'income' ? '#dcfce7' : rec.type === 'savings' ? '#f3e8ff' : rec.type === 'debt' ? '#fee2e2' : (isPink ? '#fce7f3' : isWarm ? '#F0E6D8' : '#EEF1FE')
                  }}>
                    {rec.type === 'income' ? '💸' : rec.type === 'savings' ? '🏦' : rec.type === 'debt' ? '🧾' : (() => { const { emoji } = parseCategoryEmoji(rec.category || ''); return emoji || '📉'; })()}
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 text-sm">{rec.description}</p>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <span className="capitalize">{rec.type} • {rec.frequency}</span>
                      {rec.payment_card && (
                        <>
                          <span>•</span>
                          <div className="flex items-center gap-1">
                            <div className={`w-2 h-2 rounded-full ${colorMap[cardInfo?.color]?.bg || 'bg-gray-400'}`} />
                            <span>{rec.payment_card}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <span className={`font-semibold ${rec.type === 'income' ? 'text-emerald-600' : 'text-gray-800'}`}>
                  {rec.type === 'income' ? '+' : ''}{formatCurrency(rec.amount)}
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => addRecurringTransaction(rec)}
                  className="flex-1 text-xs h-8 border-none hover:opacity-90 font-semibold"
                  style={plan ? { backgroundColor: '#1F1E24', color: '#FFFFFF' } : { backgroundColor: isWarm ? '#2D5A3D' : isPink ? '#DCECC4' : '#4361EE', color: isWarm ? '#fff' : isPink ? '#2F6B44' : '#fff' }}
                >
                  <Check className="w-3 h-3 mr-1" /> Add Now
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => skipRecurringTransaction(rec)}
                  className="flex-1 text-xs h-8 border-gray-200"
                >
                  Skip This Month
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Footer — outline pill, matches Skip This Month's secondary style */}
      <div className="px-4 pb-4 pt-1">
        <Button
          variant="outline"
          onClick={() => setShowRecurringPrompt(false)}
          className="w-full border-gray-200 text-gray-700 hover:bg-gray-50"
        >
          Close
        </Button>
      </div>
    </div>
  </div>
)}

{/* Transaction Detail Modal */}
{viewingTransaction && (
  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setViewingTransaction(null)}>
    <div 
      className="bg-white rounded-3xl shadow-xl max-w-sm w-full max-h-[70vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      {(() => {
        const vtTypeKey = viewingTransaction.is_transfer ? 'transfer'
          : viewingTransaction.is_income ? 'income'
          : viewingTransaction.is_savings ? (viewingTransaction.transaction_type === 'withdrawal' ? 'withdrawal' : 'savings')
          : viewingTransaction.is_debt_payment ? 'debtpayment'
          : 'expense';
        const vtEmoji = viewingTransaction.is_income && viewingTransaction.is_interest ? '🌱' : txTypeMeta[vtTypeKey].emoji;
        const vtCategoryEmoji = (() => { const { emoji } = parseCategoryEmoji(viewingTransaction.category || ''); return emoji || '🏷️'; })();
        const vtChip = txTypeChip(vtTypeKey);
        const vtAccent = txTypeAccent(vtTypeKey);
        const vtType = viewingTransaction.is_transfer ? 'Transfer'
          : viewingTransaction.is_income ? (viewingTransaction.is_interest ? 'Interest' : 'Income')
          : viewingTransaction.is_savings ? (viewingTransaction.transaction_type === 'withdrawal' ? 'Withdrawal' : 'Deposit')
          : viewingTransaction.is_debt_payment ? 'Debt Payment' : 'Expense';
        const vtCardHexMap = { grey: '#6b7280', red: '#ef4444', blue: '#2b6cee', orange: '#f97316', green: '#10b981', pink: '#e879a0', purple: '#8b5cf6' };
        const getCardDot = (cardName) => { const card = cards?.find(c => c.name === cardName); return vtCardHexMap[card?.color] || '#d1d5db'; };
        return (
          <>
            <div className="flex items-center gap-3 p-4 border-b border-gray-100">
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center text-xl flex-shrink-0 ${vtChip.className}`} style={vtChip.style}>{vtEmoji}</div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>{vtType}</p>
                <p className="mt-0.5" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '26px', color: vtAccent }}>{viewingTransaction.is_income ? '+' : '-'}{formatCurrency(viewingTransaction.amount)}</p>
              </div>
              <button onClick={() => setViewingTransaction(null)} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors flex-shrink-0">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="divide-y divide-gray-50">
              {!viewingTransaction.is_income && !viewingTransaction.is_savings && !viewingTransaction.is_transfer && !viewingTransaction.is_debt_payment && viewingTransaction.category && (
                <div className="flex items-center gap-3 px-4 py-3.5">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-base flex-shrink-0 ${vtChip.className}`} style={vtChip.style}>{vtCategoryEmoji}</div>
                  <div><p className="text-xs font-medium text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Category</p><p className="text-[15px] font-semibold text-gray-800 mt-0.5" style={{ fontFamily: "'DM Sans', sans-serif" }}>{getCategoryDisplayName(viewingTransaction.category)}</p></div>
                </div>
              )}
              {(viewingTransaction.is_savings || viewingTransaction.is_interest) && (viewingTransaction.account_id || viewingTransaction.interest_account_id) && (() => {
                const accId = viewingTransaction.is_interest ? viewingTransaction.interest_account_id : viewingTransaction.account_id;
                const accType = viewingTransaction.is_interest ? viewingTransaction.interest_account_type : viewingTransaction.account_type;
                const fund = accType === 'fund' ? (budgetSettings.sinking_funds || []).find(f => f.id === accId) : null;
                const accEmoji = fund?.emoji || (viewingTransaction.is_interest ? '🌱' : '📊');
                return (
                  <div className="flex items-center gap-3 px-4 py-3.5">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-base flex-shrink-0 ${vtChip.className}`} style={vtChip.style}>{accEmoji}</div>
                    <div><p className="text-xs font-medium text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Account</p><p className="text-[15px] font-semibold text-gray-800 mt-0.5" style={{ fontFamily: "'DM Sans', sans-serif" }}>{getAccountName(accId, accType)}</p></div>
                  </div>
                );
              })()}
              {viewingTransaction.is_debt_payment && viewingTransaction.debt_id && (
                <div className="flex items-center gap-3 px-4 py-3.5">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-base flex-shrink-0 ${vtChip.className}`} style={vtChip.style}>🧾</div>
                  <div><p className="text-xs font-medium text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Debt</p><p className="text-[15px] font-semibold text-gray-800 mt-0.5" style={{ fontFamily: "'DM Sans', sans-serif" }}>{getDebtName(viewingTransaction.debt_id)}</p></div>
                </div>
              )}
              {viewingTransaction.description && (
                <div className="flex items-center gap-3 px-4 py-3.5">
                  <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">📝</div>
                  <div><p className="text-xs font-medium text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Note</p><p className="text-[15px] font-semibold text-gray-800 mt-0.5" style={{ fontFamily: "'DM Sans', sans-serif" }}>{viewingTransaction.description}</p></div>
                </div>
              )}
              {!viewingTransaction.is_income && !viewingTransaction.is_savings && !viewingTransaction.is_transfer && viewingTransaction.payment_method && (
                <div className="flex items-center gap-3 px-4 py-3.5">
                  <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">{viewingTransaction.payment_method === 'Cash' ? '💵' : '💳'}</div>
                  <div><p className="text-xs font-medium text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Payment method</p><p className="text-[15px] font-semibold text-gray-800 mt-0.5 flex items-center gap-1.5" style={{ fontFamily: "'DM Sans', sans-serif" }}><span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: getCardDot(viewingTransaction.payment_method) }}></span>{viewingTransaction.payment_method}</p></div>
                </div>
              )}
              {!viewingTransaction.is_income && !viewingTransaction.is_savings && viewingTransaction.feeling && (() => {
                const fl = FEELINGS.find(f => f.id === viewingTransaction.feeling);
                if (!fl) return null;
                return (
                  <div className="flex items-center gap-3 px-4 py-3.5">
                    <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">{fl.emoji}</div>
                    <div><p className="text-xs font-medium text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>How it felt</p><p className="text-[15px] font-semibold text-gray-800 mt-0.5" style={{ fontFamily: "'DM Sans', sans-serif" }}>{fl.label}</p></div>
                  </div>
                );
              })()}
              {viewingTransaction.is_income && viewingTransaction.to_card && (
                <div className="flex items-center gap-3 px-4 py-3.5">
                  <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">{viewingTransaction.to_card === 'Cash' ? '💵' : '💳'}</div>
                  <div><p className="text-xs font-medium text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>To</p><p className="text-[15px] font-semibold text-gray-800 mt-0.5 flex items-center gap-1.5" style={{ fontFamily: "'DM Sans', sans-serif" }}><span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: getCardDot(viewingTransaction.to_card) }}></span>{viewingTransaction.to_card}</p></div>
                </div>
              )}
              {viewingTransaction.is_savings && viewingTransaction.transaction_type === 'deposit' && viewingTransaction.from_card && (
                <div className="flex items-center gap-3 px-4 py-3.5">
                  <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">{viewingTransaction.from_card === 'Cash' ? '💵' : '💳'}</div>
                  <div><p className="text-xs font-medium text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>From</p><p className="text-[15px] font-semibold text-gray-800 mt-0.5 flex items-center gap-1.5" style={{ fontFamily: "'DM Sans', sans-serif" }}><span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: getCardDot(viewingTransaction.from_card) }}></span>{viewingTransaction.from_card}</p></div>
                </div>
              )}
              {viewingTransaction.is_savings && viewingTransaction.transaction_type === 'withdrawal' && viewingTransaction.to_card && (
                <div className="flex items-center gap-3 px-4 py-3.5">
                  <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">{viewingTransaction.to_card === 'Cash' ? '💵' : '💳'}</div>
                  <div><p className="text-xs font-medium text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>To</p><p className="text-[15px] font-semibold text-gray-800 mt-0.5 flex items-center gap-1.5" style={{ fontFamily: "'DM Sans', sans-serif" }}><span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: getCardDot(viewingTransaction.to_card) }}></span>{viewingTransaction.to_card}</p></div>
                </div>
              )}
              {viewingTransaction.is_transfer && viewingTransaction.transfer_from && (
                <div className="flex items-center gap-3 px-4 py-3.5">
                  <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">{viewingTransaction.transfer_from === 'Cash' ? '💵' : '💳'}</div>
                  <div><p className="text-xs font-medium text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>From</p><p className="text-[15px] font-semibold text-gray-800 mt-0.5 flex items-center gap-1.5" style={{ fontFamily: "'DM Sans', sans-serif" }}><span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: getCardDot(viewingTransaction.transfer_from) }}></span>{viewingTransaction.transfer_from}</p></div>
                </div>
              )}
              {viewingTransaction.is_transfer && viewingTransaction.transfer_to && (
                <div className="flex items-center gap-3 px-4 py-3.5">
                  <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">{viewingTransaction.transfer_to === 'Cash' ? '💵' : '💳'}</div>
                  <div><p className="text-xs font-medium text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>To</p><p className="text-[15px] font-semibold text-gray-800 mt-0.5 flex items-center gap-1.5" style={{ fontFamily: "'DM Sans', sans-serif" }}><span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: getCardDot(viewingTransaction.transfer_to) }}></span>{viewingTransaction.transfer_to}</p></div>
                </div>
              )}
              <div className="flex items-center gap-3 px-4 py-3.5">
                <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">📅</div>
                <div><p className="text-xs font-medium text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Date</p><p className="text-[15px] font-semibold text-gray-800 mt-0.5" style={{ fontFamily: "'DM Sans', sans-serif" }}>{new Date(viewingTransaction.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</p></div>
              </div>
            </div>
            <div className="flex gap-2.5 p-4 border-t border-gray-100">
              <button
                onClick={() => { startEditTransaction(viewingTransaction); setViewingTransaction(null); }}
                className="flex-1 flex items-center justify-center gap-1.5 rounded-full py-3 text-white font-semibold text-[15px] shadow-md"
                style={{ ...txCtaStyle, fontFamily: "'DM Sans', sans-serif" }}
              >
                <Edit3 className="w-3.5 h-3.5" /> Edit
              </button>
              <button
                onClick={() => { removeTransaction(viewingTransaction.id); setViewingTransaction(null); }}
                className="flex-1 flex items-center justify-center gap-1.5 rounded-full py-3 font-semibold text-[15px] border border-red-200 text-red-500"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
          </>
        );
      })()}
    </div>
  </div>
)}

{!showBudgetSetup && !showYearlyStats && (
<>
  <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mb-2 mt-6" style={{ color: isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE' }}><span className="wy-star">✦ </span>Budget setup</p>
  {/* Month setup — everything a month needs once, at the foot of the tab
      where it is out of the way of the numbers but never more than one
      tap away. Clear month lives inside it. */}
  <button
    type="button"
    onClick={() => setShowMonthSetup(true)}
    className={"w-full bg-white rounded-2xl px-4 py-3.5 mb-2 flex items-center gap-3 text-left shadow-sm" + (plan ? " wy-plan-setuprow" : "")}
    data-testid="month-setup-row"
  >
    <span className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: plan ? '#F5F2FA' : isPink ? '#F4F1F8' : isWarm ? '#F0EAE0' : '#EEF1FF' }}>
      <Settings className="w-4 h-4" style={{ color: '#6B6270' }} />
    </span>
    <span className="flex-1 min-w-0">
      {/* w2_11: in Worthly 2.0 the title matches the other section titles */}
      <span className={plan ? 'block' : 'block text-[14px] font-semibold text-gray-800'} style={plan ? { fontSize: 16.5, fontWeight: 700, letterSpacing: '-0.5px', color: '#26252E' } : undefined}>{monthNames[viewMonth.getMonth()]} Setup</span>
      <span className={plan ? 'block' : 'block text-[11.5px] mt-0.5'} style={plan ? { fontSize: 13, color: '#8B879A', marginTop: 2 } : { color: '#6B6270' }}>Income, starting balances, clear month</span>
    </span>
    <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#C9C2CC' }} />
  </button>
</>
)}
</div>

      {/* Budget setup nudge — fires when FAB tapped on an unconfigured month */}
      {/* ── Month setup ────────────────────────────────────────────────────
          The three jobs a month needs once, in one place, reachable all month
          from the row at the foot of this tab. Each row opens a slice of
          MonthlyBudgetSetup rather than a second implementation of it. */}
      {showMonthSetup && createPortal(
        <div className="fixed inset-0 z-[100] flex items-end justify-center" onClick={() => setShowMonthSetup(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full bg-white rounded-t-3xl shadow-2xl flex flex-col wy-plan-sheet"
            style={{ height: '88vh', paddingBottom: 'max(1.75rem, env(safe-area-inset-bottom, 0px))' }}
            onClick={(e) => e.stopPropagation()}
            data-testid="month-setup-sheet"
          >
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-gray-200" />
            </div>

            <div className="px-5 pt-2 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <p style={{ fontFamily: "'DM Serif Display', serif", fontSize: '23px', lineHeight: 1.15, color: '#1A1A1F' }}>
                  Set up {monthNames[viewMonth.getMonth()]}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowMonthSetup(false)}
                aria-label="Close"
                className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {/* Tasks, the Next line and the "change this later" line scroll
                together as one block from the top. The slack falls BELOW them
                instead of between them, so the two lines sit under the third
                task where they are read, not stranded at the foot of the
                sheet. Clear <month> stays pinned below. */}
            <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="px-5 mt-3">
              {(() => {
                const [pmY, pmM] = viewMonthKey.split('-').map(Number);
                const prevKey = pmM === 1 ? `${pmY - 1}-12` : `${pmY}-${String(pmM - 1).padStart(2, '0')}`;
                const prevName = monthNames[pmM === 1 ? 11 : pmM - 2];
                const prevCats = categoryBudgets[prevKey] || {};
                const prevSav = (budgetSettings.savings_budgets_by_month || {})[prevKey] || {};
                const prevDebt = (budgetSettings.debt_budgets_by_month || {})[prevKey] || {};
                const prevEntries = [
                  ...Object.values(prevCats),
                  ...Object.values(prevSav),
                  ...Object.values(prevDebt),
                ].filter(v => typeof v === 'number' && v !== 0);
                const prevTotal = prevEntries.reduce((s, v) => s + Math.max(v, 0), 0);
                const carriedIn = budgetSettings.leftover_by_month?.[viewMonthKey] || 0;
                const carriedRows = Object.keys(budgetSettings.leftover_allocations_by_month?.[viewMonthKey] || {}).length;

                const task = (emoji, tint, title, sub, onClick, last) => (
                  <button
                    type="button"
                    onClick={onClick}
                    className={`w-full flex items-center gap-3 py-3.5 text-left${last ? '' : ' border-b border-gray-50'}`}
                  >
                    <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-[16px]" style={{ background: tint }}>
                      {emoji}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[14.5px] font-semibold text-gray-800">{title}</span>
                      <span className="block text-[11.5px] mt-0.5" style={{ color: mutedInk }}>{sub}</span>
                    </span>
                    <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: faintInk }} />
                  </button>
                );

                const tintA = plan ? '#F5F2FA' : isPink ? '#F7E2EC' : isWarm ? '#F0EAE0' : '#EEF1FF';
                const tintB = plan ? '#F5F2FA' : isPink ? '#F1EDF7' : isWarm ? '#EFEAE2' : '#EDEFFB';

                // plan_12: Money plan - the month's setup as a path.
                const memoText = String((budgetSettings.notes_by_month || {})[viewMonthKey] || '').trim();
                const setupSteps = [
                  { emoji: '\u{1F4B0}', label: 'Income', done: monthlyBudget > 0, text: 'Add your income', go: () => openFromSetup('amount') },
                  { emoji: '\u{1F4CA}', label: 'Budgets', done: totalCategoryBudgets > 0, text: prevEntries.length > 0 ? `Copy your budgets from ${prevName}` : 'Budget your categories', go: () => (prevEntries.length > 0 ? openFromSetup('copy') : setShowMonthSetup(false)) },
                  { emoji: '\u{1F3E6}', label: 'Balances', done: carriedIn !== 0, text: 'Add your starting balances', go: () => openFromSetup('balances') },
                  { emoji: '\u{1F4DD}', label: 'Memos', done: !!memoText, text: `Write a memo for ${monthNames[viewMonth.getMonth()]}`, go: () => { setShowMonthSetup(false); setShowBudgetSetup(true); setShowYearlyStats(false); } },
                ];
                const setupNow = setupSteps.findIndex((s) => !s.done);
                const setupPath = plan ? (
                  <div style={{ background: '#F4F1FB', borderRadius: 26, padding: '6px 14px 14px', marginBottom: 10 }} data-testid="plan-setup-path">
                    <PlanPath
                      steps={setupSteps.map((s, i) => ({ emoji: s.emoji, label: s.label, done: s.done, now: i === setupNow, bg: '#FFFFFF' }))}
                      progress={setupNow < 0 ? 1 : setupNow / (setupSteps.length - 1)}
                    />
                    <div className="flex items-center" style={{ gap: 12, background: '#FFFFFF', borderRadius: 20, padding: '14px 16px', marginTop: 14 }}>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 12.5, color: '#9A8FBF', fontWeight: 500 }}>{setupNow < 0 ? 'All set' : 'Next step'}</span>
                        <span style={{ display: 'block', fontSize: 15, fontWeight: 600, color: '#2B2C31', marginTop: 3, lineHeight: 1.3 }}>
                          {setupNow < 0 ? `${monthNames[viewMonth.getMonth()]} is ready to go! \u{1F389}` : setupSteps[setupNow].text}
                        </span>
                      </span>
                      {setupNow >= 0 && (
                        <button type="button" onClick={setupSteps[setupNow].go} style={{ flexShrink: 0, background: '#1F1E24', color: '#FFFFFF', border: 0, borderRadius: 999, padding: '10px 16px', fontSize: 13.5, fontWeight: 600 }}>Start</button>
                      )}
                    </div>
                  </div>
                ) : null;

                return (
                  <>
                    {setupPath}
                    {task('💰', tintA,
                      `Your income for ${monthNames[viewMonth.getMonth()]}`,
                      monthlyBudget > 0 ? formatCurrency(monthlyBudget) : 'Nothing set yet',
                      () => openFromSetup('amount'))}
                    {task('📊', tintA,
                      `Copy budgets from ${prevName}`,
                      prevEntries.length > 0
                        ? `${prevEntries.length} item${prevEntries.length === 1 ? '' : 's'} · ${formatCurrency(prevTotal)}`
                        : `Nothing budgeted in ${prevName}`,
                      () => openFromSetup('copy'))}
                    {task('🏦', tintB,
                      'Starting balances',
                      carriedIn !== 0
                        ? `${carriedRows} account${carriedRows === 1 ? '' : 's'} · ${formatCurrency(carriedIn)}`
                        : `What each card holds as ${monthNames[viewMonth.getMonth()]} begins`,
                      () => openFromSetup('balances'), true)}
                  </>
                );
              })()}
            </div>

            {/* Setting the month up and setting your budgets are two different
                things, and nothing said so. Without this, you can finish all
                three tasks with income, balances and no envelopes at all - and
                the rings and the pace card never work. Shown only while there
                are no category budgets, so it disappears the moment it stops
                being true. */}
            {totalCategoryBudgets <= 0 && (
              <div className="mx-5 mt-3 rounded-2xl flex items-start gap-2.5 px-3.5 py-3" style={{ background: plan ? '#F7F5FB' : isPink ? '#FBEFF5' : isWarm ? '#F7F2EA' : '#F0F2FC' }}>
                <span className="text-[15px] leading-none flex-shrink-0" style={{ marginTop: '1px' }}>📋</span>
                <p className="text-[12px] leading-[17px] font-medium m-0" style={{ color: '#4B4552' }}>
                  Next: set your budget for each category not copied over in{' '}
                  <span className="font-bold">Your Budgets</span>. You can edit this throughout the month in the same spot too.
                </p>
              </div>
            )}

            {/* Only worth saying while the prompt is still at the top of the
                tab. Once income is in, this sheet IS the row at the foot, so
                pointing at the foot from inside it says nothing. */}
            {monthlyBudget <= 0 && (
              <p className="px-5 mt-3 flex-shrink-0 text-[11.5px] leading-4 font-medium" style={{ color: mutedInk }}>
                You can change any of this later under{' '}
                <span className="font-bold" style={{ color: '#4B4552' }}>{monthNames[viewMonth.getMonth()]} Setup</span>, at the foot of this tab.
              </p>
            )}

            </div>

            {/* No "Not now". Setting a month up is not optional - the ✕ closes
                the sheet, it does not dismiss the job. */}
            <div className="px-5 mt-3.5 pt-3 pb-5 flex items-center border-t border-gray-100 flex-shrink-0">
              <button
                type="button"
                onClick={() => setConfirmClearMonth(true)}
                className="flex items-center gap-1.5 text-[13px] font-semibold"
                style={{ color: '#E11D48' }}
                data-testid="clear-month-btn"
              >
                <Trash2 className="w-[15px] h-[15px]" style={{ color: '#E11D48' }} />
                Clear {monthNames[viewMonth.getMonth()]}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Clear-month confirmation. Same wording and shape as the one in
          MonthlyBudgetSetup, so the action reads identically wherever it is
          reached from. It calls the same resetMonth(). */}
      {confirmClearMonth && createPortal(
        <div className="fixed inset-0 bg-black/50 z-[110] flex items-center justify-center p-4" onClick={() => setConfirmClearMonth(false)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: '#FDE7EC' }}>
                <Trash2 className="w-6 h-6" style={{ color: '#E11D48' }} />
              </div>
            </div>
            <h3 className="text-lg font-bold text-gray-800 text-center mb-2">Reset {monthNames[viewMonth.getMonth()]}?</h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              This clears {monthNames[viewMonth.getMonth()]} {viewMonth.getFullYear()} completely: your income, every budget, the starting balances, this month's notes and reminders, and all of its transactions. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmClearMonth(false)}
                className="flex-1 py-3 rounded-full border border-gray-200 bg-white text-sm font-bold text-gray-500"
              >Cancel</button>
              <button
                onClick={() => { resetMonth(); setConfirmClearMonth(false); setShowMonthSetup(false); }}
                className="flex-1 py-3 rounded-full text-sm font-bold text-white active:scale-[0.98] transition-transform"
                style={{ background: '#E11D48' }}
                data-testid="clear-month-confirm"
              >Yes, Reset</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Copy from last month — headless. MonthlyBudgetSetup opens its own copy
          sheet on mount and unmounts this host when that sheet closes. */}
      {moneySheet === 'copy' && (
        <MonthlyBudgetSetup
          only="copy"
          theme={theme}
          onClose={closeSheet}
          onBack={setupReturn ? backToSetup : null}
          viewMonthKey={viewMonthKey}
          viewMonth={viewMonth}
          budgetSettings={budgetSettings}
          saveUserData={saveUserData}
          cards={paymentMethods}
          colorMap={colorMap}
          categoriesWithActivity={categoriesWithActivity}
          categoryBudgets={categoryBudgets}
          availableSinkingFunds={availableSinkingFunds}
          availableInvestmentAccounts={availableInvestmentAccounts}
          userDebts={userDebts}
        />
      )}

      {/* Income / Starting balances — one slice of MonthlyBudgetSetup's own
          Income section, so amount-to-budget and the per-card carry-in keep a
          single implementation and a single save path. */}
      {(moneySheet === 'amount' || moneySheet === 'balances') && createPortal(
        <div className="fixed inset-0 z-[100] flex items-end justify-center" onClick={() => setMoneySheet(null)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full bg-white rounded-t-3xl shadow-2xl flex flex-col wy-plan-sheet"
            style={{ height: '88vh', paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom, 0px) + 300px)' }}
            onClick={(e) => e.stopPropagation()}
            data-testid="money-to-budget-sheet"
          >
            <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-1 rounded-full bg-gray-200" />
            </div>

            <div className="px-5 pt-2 pb-2 flex items-start gap-2.5 flex-shrink-0">
              {setupReturn && (
                <button
                  type="button"
                  onClick={backToSetup}
                  aria-label={`Back to Set up ${monthNames[viewMonth.getMonth()]}`}
                  className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0 mt-0.5"
                  data-testid="sheet-back"
                >
                  <ChevronLeft className="w-4 h-4 text-gray-500" />
                </button>
              )}
              <div className="flex-1 min-w-0">
                <p style={{ fontFamily: "'DM Serif Display', serif", fontSize: '22px', lineHeight: 1.15, color: '#1A1A1F' }}>
                  {moneySheet === 'balances' ? 'Starting balances' : 'Your income'}
                </p>
                {moneySheet !== 'balances' && (
                  <p className="text-[12.5px] mt-0.5" style={{ color: mutedInk }}>
                    {monthNames[viewMonth.getMonth()]}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={closeSheet}
                aria-label="Close"
                className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <div className="px-5 overflow-y-auto flex-1 min-h-0">
              <MonthlyBudgetSetup
                only={moneySheet}
                theme={theme}
                onClose={closeSheet}
                viewMonthKey={viewMonthKey}
                viewMonth={viewMonth}
                budgetSettings={budgetSettings}
                saveUserData={saveUserData}
                cards={paymentMethods}
                colorMap={colorMap}
                categoriesWithActivity={categoriesWithActivity}
                categoryBudgets={categoryBudgets}
                availableSinkingFunds={availableSinkingFunds}
                availableInvestmentAccounts={availableInvestmentAccounts}
                userDebts={userDebts}
                prevCardBalances={prevCardBalances}
              />

              {/* Only the balances sheet carries a total. "Left to budget" used
                  to sit here too, but that number now lives in the Your Budgets
                  header where the budgets it counts down are - showing it in
                  two places invites the two to disagree. */}
              {moneySheet === 'balances' && (
                <div className="pt-3 mt-3 border-t border-gray-100 flex items-center gap-3">
                  <span className="flex-1 text-[14px] font-semibold" style={{ color: '#1F2937' }}>
                    {monthNames[viewMonth.getMonth()]} starts with
                  </span>
                  <span className="text-[18px] font-bold" style={{ color: accentInk }}>
                    {formatCurrency(budgetSettings.leftover_by_month?.[viewMonthKey] || 0)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Budget another category. Lists only what is missing from Your Budgets,
          then hands straight to the same BudgetEditPopup every other row uses -
          one save path, whichever way a budget gets set. */}
      {pickCategory && createPortal(
        <div className="fixed inset-0 z-[100] flex items-end justify-center" onClick={() => setPickCategory(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full bg-white rounded-t-3xl shadow-2xl flex flex-col wy-plan-sheet"
            style={{ height: '88vh', paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom, 0px))' }}
            onClick={(e) => e.stopPropagation()}
            data-testid="category-picker-sheet"
          >
            <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-1 rounded-full bg-gray-200" />
            </div>
            <div className="px-5 pt-2 pb-3 flex items-start gap-3 flex-shrink-0">
              <div className="flex-1 min-w-0">
                <p style={{ fontFamily: "'DM Serif Display', serif", fontSize: '22px', lineHeight: 1.15, color: '#1A1A1F' }}>
                  {totalCategoryBudgets > 0 ? 'Budget another category' : 'Budget a category'}
                </p>
                <p className="text-[12.5px] mt-0.5" style={{ color: mutedInk }}>
                  Categories with nothing budgeted or spent in {monthNames[viewMonth.getMonth()]}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPickCategory(false)}
                aria-label="Close"
                className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="px-5 overflow-y-auto flex-1 min-h-0">
              {categories
                .filter(c => !(monthlyCategoryBudgets[c] > 0) && !(spendingByCategory[c] > 0))
                .map((cat, i, arr) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => {
                      setPickCategory(false);
                      setBudgetPopup({
                        label: cat,
                        value: '',
                        onSave: (val) => {
                          const v = parseFloat(val) || 0;
                          saveUserData({ category_budgets_by_month: { ...categoryBudgets, [viewMonthKey]: { ...monthlyCategoryBudgets, [cat]: v } } });
                        },
                      });
                    }}
                    className={`w-full flex items-center gap-3 py-3.5 text-left${i === arr.length - 1 ? '' : ' border-b border-gray-50'}`}
                  >
                    <span className="flex-1 min-w-0 text-[15px] font-medium text-gray-800 truncate">{cat}</span>
                    <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#C9C2CC' }} />
                  </button>
                ))}
            </div>
          </div>
        </div>,
        document.body
      )}

      {budgetPopup && (
        <BudgetEditPopup
          label={budgetPopup.label}
          hint={budgetPopup.hint}
          initialValue={budgetPopup.value}
          accent={accentColors?.primary}
          currency={getCurrentCurrency()}
          allowNegative={budgetPopup.allowNegative}
          isWarm={theme === 'warm'}
          isPink={theme === 'pink'}
          theme={theme}
          onSave={(val) => { budgetPopup.onSave(val); setBudgetPopup(null); }}
          onCancel={() => setBudgetPopup(null)}
        />
      )}

      {/* Cross-month edit confirmation */}
      {pendingMonthMoveEdit && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4"
          onClick={() => setPendingMonthMoveEdit(null)}
        >
          <div
            className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-base font-semibold text-gray-800 mb-2">Move to a different month?</p>
            <p className="text-sm text-gray-600 leading-relaxed mb-4">
              {(() => {
                const formatMonth = (mk) => {
                  if (!mk) return 'another month';
                  const [y, m] = mk.split('-').map(Number);
                  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
                };
                return `This transaction is currently in ${formatMonth(pendingMonthMoveEdit.oldMonthKey)}. Saving will move it to ${formatMonth(pendingMonthMoveEdit.newMonthKey)} and update both months' data.`;
              })()}
            </p>
            <div className="flex gap-2">
              <Button
                onClick={() => setPendingMonthMoveEdit(null)}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold"
              >
                Cancel
              </Button>
              <Button
                onClick={() => commitEditTransaction(pendingMonthMoveEdit.updatedList)}
                className="flex-1 theme-bg-500 hover:theme-bg-600 text-white font-semibold"
              >
                Move it
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Transaction Bottom Sheet — replaces inline edit */}
      {editSheetTxId && (
        <div
          className="fixed inset-0 bg-black/40 z-40 flex items-end justify-center"
          onClick={cancelEditTransaction}
        >
          <div
            className="w-full bg-white rounded-t-3xl shadow-2xl flex flex-col animate-in slide-in-from-bottom duration-500 wy-plan-addsheet"
            style={{ position: 'relative', height: 'calc(100vh - env(safe-area-inset-top, 0px) - 12px)', marginTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 44px)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex justify-center pt-3 pb-3 flex-shrink-0 cursor-grab"
              onTouchStart={(e) => { e.currentTarget._startY = e.touches[0].clientY; }}
              onTouchMove={(e) => {
                const dy = e.touches[0].clientY - e.currentTarget._startY;
                if (dy > 0) e.currentTarget.parentElement.style.transform = `translateY(${dy}px)`;
              }}
              onTouchEnd={(e) => {
                const dy = e.changedTouches[0].clientY - e.currentTarget._startY;
                const el = e.currentTarget.parentElement;
                if (dy > 50) { el.style.transform = ''; cancelEditTransaction(); }
                else { el.style.transition = 'transform 0.2s'; el.style.transform = ''; setTimeout(() => { el.style.transition = ''; }, 200); }
              }}
            ><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
            <button
              type="button"
              aria-label="Close sheet"
              onClick={() => { cancelEditTransaction(); }}
              className="absolute w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
              style={{ top: 10, right: 14, zIndex: 5 }}
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>

            <div className="flex-1 overflow-y-auto overscroll-contain px-5 pb-4">
              <div className="flex items-center justify-between mb-4 px-1 pt-2">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-lg flex-shrink-0 ${txTypeChip(editTypeKey).className}`} style={txTypeChip(editTypeKey).style}>
                    {editTransactionData.transactionType === 'interest' ? '🌱' : txTypeMeta[editTypeKey].emoji}
                  </div>
                  <h3 className="m-0" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '21px', fontWeight: 400, color: '#1F2937' }}>Edit Transaction</h3>
                </div>
                <span className="text-xs font-semibold theme-text-600 theme-bg-50 px-2.5 py-1 rounded-full flex-shrink-0" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  {(() => {
                    const t = editTransactionData.transactionType;
                    if (t === 'expense') return 'Expense';
                    if (t === 'income') return 'Income';
                    if (t === 'interest') return 'Interest';
                    if (t === 'savings') {
                      return editTransactionData.savingsTransactionType === 'withdrawal' ? 'Withdrawal' : 'Savings Deposit';
                    }
                    return 'Transaction';
                  })()}
                </span>
              </div>

              <div className="flex flex-col gap-4">
                {/* Description / Note */}
                <div>
                  <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Note <span className="text-gray-300">(optional)</span></p>
                  <input
                    type="text"
                    value={editTransactionData.description}
                    onChange={(e) => setEditTransactionData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Add a note"
                    className="w-full h-14 bg-white rounded-2xl px-4 shadow-sm outline-none text-[15px] text-gray-800 placeholder:text-gray-300"
                    style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, caretColor: '#1F2937' }}
                  />
                </div>

                {/* Amount hero card */}
                <div className="rounded-3xl px-4 py-5 text-center" style={{ background: txTypeAccentSoft(editTypeKey) }}>
                  <p className="text-[11px] font-bold uppercase tracking-[0.08em] mb-1" style={{ color: txTypeAccent(editTypeKey), opacity: 0.75, fontFamily: "'DM Sans', sans-serif" }}>Amount</p>
                  <div className="flex items-center justify-center gap-1">
                    <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: '24px', color: txTypeAccent(editTypeKey), opacity: 0.65 }}>{getCurrentCurrency().symbol}</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={editTransactionData.amount}
                      onChange={(e) => setEditTransactionData(prev => ({ ...prev, amount: e.target.value }))}
                      placeholder="0"
                      className="bg-transparent outline-none text-center placeholder:opacity-40"
                      style={{ fontFamily: "'DM Serif Display', serif", fontSize: '44px', color: txTypeAccent(editTypeKey), width: '55%', caretColor: txTypeAccent(editTypeKey) }}
                    />
                  </div>
                </div>

                {/* Date */}
                <div>
                  <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Date</p>
                  <label
                    htmlFor="worthly-edit-tx-date"
                    className="flex items-center gap-3 bg-white rounded-2xl px-4 h-14 shadow-sm w-full cursor-pointer"
                    onClick={(e) => {
                      const input = e.currentTarget.querySelector('input[type="date"]');
                      if (input && typeof input.showPicker === 'function') {
                        try { input.showPicker(); } catch (_) { /* fallback to label click */ }
                      }
                    }}
                  >
                    <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">📅</div>
                    <input
                      id="worthly-edit-tx-date"
                      type="date"
                      value={editTransactionData.date}
                      onChange={(e) => setEditTransactionData(prev => ({ ...prev, date: e.target.value }))}
                      className="flex-1 bg-transparent text-[15px] text-gray-800 focus:outline-none cursor-pointer appearance-none"
                      style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, textAlign: 'left', WebkitAppearance: 'none' }}
                    />
                  </label>
                </div>

                {/* Expense-only: Category + Payment method */}
                {editTransactionData.transactionType === 'expense' && (
                  <>
                    <div>
                      <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Category</p>
                      <div className="relative">
                        <div className="flex items-center gap-3 bg-white rounded-2xl px-4 h-14 shadow-sm w-full">
                          <div className="w-9 h-9 rounded-xl flex items-center justify-center text-base flex-shrink-0" style={{ background: txTypeAccentSoft('expense') }}>🏷️</div>
                          <input
                            type="text"
                            value={editTransactionData.category || editCategorySearch}
                            onChange={(e) => { setEditCategorySearch(e.target.value); setEditTransactionData(prev => ({ ...prev, category: '' })); setShowEditCategoryDropdown(true); }}
                            onFocus={() => setShowEditCategoryDropdown(true)}
                            onBlur={() => setTimeout(() => setShowEditCategoryDropdown(false), 200)}
                            placeholder="Choose a category"
                            className="flex-1 min-w-0 bg-transparent outline-none text-[15px] text-gray-800 placeholder:text-gray-300"
                            style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400, caretColor: '#1F2937' }}
                          />
                        </div>
                        {showEditCategoryDropdown && editFilteredCategories.length > 0 && (
                          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-100 rounded-2xl shadow-lg max-h-72 overflow-y-auto">
                            {editFilteredCategories.map((cat, index) => (
                              <button key={index} type="button" onMouseDown={(e) => { e.preventDefault(); selectEditCategory(cat); }} className="w-full text-left px-4 py-2 hover:theme-bg-50 text-sm text-gray-700 flex items-center gap-2">
                                <Tag className="w-3 h-3 text-gray-400" />{cat}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Payment method</p>
                      <Select value={editTransactionData.paymentMethod} onValueChange={(value) => setEditTransactionData(prev => ({ ...prev, paymentMethod: value }))}>
                        <SelectTrigger className="!h-14 bg-white rounded-2xl px-4 shadow-sm w-full">
                          <div className="flex items-center gap-3 min-w-0" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 400, color: '#1F2937' }}>
                            <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">💳</div>
                            <SelectValue placeholder="Select payment method" />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          {paymentMethods.map((card) => {
                            const colors = colorMap[card.color] || colorMap.grey;
                            return (
                              <SelectItem key={card.name} value={card.name}>
                                <div className="flex items-center gap-2">
                                  <div className={`w-3 h-3 rounded-full ${colors.bg}`} />
                                  {card.name}
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    <FeelingPicker
                      value={editTransactionData.feeling || ''}
                      onChange={(v) => setEditTransactionData(prev => ({ ...prev, feeling: v }))}
                      theme={theme}
                    />
                  </>
                )}

                {/* Income: optional to_card */}
                {editTransactionData.transactionType === 'income' && (
                  <div>
                    <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Deposited to <span className="text-gray-300">(optional)</span></p>
                    <Select value={editTransactionData.to_card || '__none__'} onValueChange={(value) => setEditTransactionData(prev => ({ ...prev, to_card: value === '__none__' ? '' : value }))}>
                      <SelectTrigger className="!h-14 bg-white rounded-2xl px-4 shadow-sm w-full">
                        <div className="flex items-center gap-3 min-w-0" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 400, color: '#1F2937' }}>
                          <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">💳</div>
                          <SelectValue placeholder="Select payment method" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {paymentMethods.map((card) => {
                          const colors = colorMap[card.color] || colorMap.grey;
                          return (
                            <SelectItem key={card.name} value={card.name}>
                              <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded-full ${colors.bg}`} />
                                {card.name}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Interest: account it was applied to (sinking fund / investment account) */}
                {editTransactionData.transactionType === 'interest' && (
                  <div>
                    <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Apply interest to</p>
                    <Select
                      value={editTransactionData.accountId ? `${editTransactionData.accountType}:${editTransactionData.accountId}` : ''}
                      onValueChange={(value) => {
                        const [type, id] = value.split(':');
                        setEditTransactionData(prev => ({ ...prev, accountType: type, accountId: id }));
                      }}
                    >
                      <SelectTrigger className="!h-14 bg-white rounded-2xl px-4 shadow-sm w-full">
                        <div className="flex items-center gap-3 min-w-0" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 400, color: '#1F2937' }}>
                          <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">🌱</div>
                          <SelectValue placeholder="Select account" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {availableSinkingFunds.length > 0 && (
                          <>
                            <div className="px-2 py-1 text-xs font-semibold text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Sinking Funds</div>
                            {availableSinkingFunds.map((fund) => (
                              <SelectItem key={`fund:${fund.id}`} value={`fund:${fund.id}`}>
                                {fund.emoji || '💰'} {fund.name}
                              </SelectItem>
                            ))}
                          </>
                        )}
                        {availableInvestmentAccounts.length > 0 && (
                          <>
                            <div className="px-2 py-1 text-xs font-semibold text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Investment Accounts</div>
                            {availableInvestmentAccounts.map((inv) => (
                              <SelectItem key={`investment:${inv.id}`} value={`investment:${inv.id}`}>
                                📊 {inv.name}
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Savings: account (fund/investment) + from_card or to_card depending on direction */}
                {editTransactionData.transactionType === 'savings' && editTransactionData.savingsTransactionType === 'deposit' && (
                  <div>
                    <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Account</p>
                    <Select
                      value={editTransactionData.accountId ? `${editTransactionData.accountType}:${editTransactionData.accountId}` : ''}
                      onValueChange={(value) => {
                        const [type, id] = value.split(':');
                        setEditTransactionData(prev => ({ ...prev, accountType: type, accountId: id }));
                      }}
                    >
                      <SelectTrigger className="!h-14 bg-white rounded-2xl px-4 shadow-sm w-full">
                        <div className="flex items-center gap-3 min-w-0" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 400, color: '#1F2937' }}>
                          <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">🏦</div>
                          <SelectValue placeholder="Select account to deposit to" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {availableSinkingFunds.length > 0 && (
                          <>
                            <div className="px-2 py-1 text-xs font-semibold text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Sinking Funds</div>
                            {availableSinkingFunds.map((fund) => (
                              <SelectItem key={`fund:${fund.id}`} value={`fund:${fund.id}`}>
                                {fund.emoji || '💰'} {fund.name}
                              </SelectItem>
                            ))}
                          </>
                        )}
                        {availableInvestmentAccounts.length > 0 && (
                          <>
                            <div className="px-2 py-1 text-xs font-semibold text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Investment Accounts</div>
                            {availableInvestmentAccounts.map((inv) => (
                              <SelectItem key={`investment:${inv.id}`} value={`investment:${inv.id}`}>
                                📊 {inv.name}
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {editTransactionData.transactionType === 'savings' && editTransactionData.savingsTransactionType === 'deposit' && (
                  <div>
                    <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>From payment method <span className="text-gray-300">(optional)</span></p>
                    <Select value={editTransactionData.from_card || '__none__'} onValueChange={(value) => setEditTransactionData(prev => ({ ...prev, from_card: value === '__none__' ? '' : value }))}>
                      <SelectTrigger className="!h-14 bg-white rounded-2xl px-4 shadow-sm w-full">
                        <div className="flex items-center gap-3 min-w-0" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 400, color: '#1F2937' }}>
                          <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">💳</div>
                          <SelectValue placeholder="Select payment method" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {paymentMethods.map((card) => {
                          const colors = colorMap[card.color] || colorMap.grey;
                          return (
                            <SelectItem key={card.name} value={card.name}>
                              <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded-full ${colors.bg}`} />
                                {card.name}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {editTransactionData.transactionType === 'savings' && editTransactionData.savingsTransactionType === 'withdrawal' && (
                  <div>
                    <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Account</p>
                    <Select
                      value={editTransactionData.accountId ? `${editTransactionData.accountType}:${editTransactionData.accountId}` : ''}
                      onValueChange={(value) => {
                        const [type, id] = value.split(':');
                        setEditTransactionData(prev => ({ ...prev, accountType: type, accountId: id }));
                      }}
                    >
                      <SelectTrigger className="!h-14 bg-white rounded-2xl px-4 shadow-sm w-full">
                        <div className="flex items-center gap-3 min-w-0" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 400, color: '#1F2937' }}>
                          <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">🏦</div>
                          <SelectValue placeholder="Select account to withdraw from" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {availableSinkingFunds.length > 0 && (
                          <>
                            <div className="px-2 py-1 text-xs font-semibold text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Sinking Funds</div>
                            {availableSinkingFunds.map((fund) => (
                              <SelectItem key={`fund:${fund.id}`} value={`fund:${fund.id}`}>
                                {fund.emoji || '💰'} {fund.name}
                              </SelectItem>
                            ))}
                          </>
                        )}
                        {availableInvestmentAccounts.length > 0 && (
                          <>
                            <div className="px-2 py-1 text-xs font-semibold text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Investment Accounts</div>
                            {availableInvestmentAccounts.map((inv) => (
                              <SelectItem key={`investment:${inv.id}`} value={`investment:${inv.id}`}>
                                📊 {inv.name}
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {editTransactionData.transactionType === 'savings' && editTransactionData.savingsTransactionType === 'withdrawal' && (
                  <div>
                    <p className="text-xs font-medium text-gray-400 mb-1.5 ml-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>To payment method <span className="text-gray-300">(optional)</span></p>
                    <Select value={editTransactionData.to_card || '__none__'} onValueChange={(value) => setEditTransactionData(prev => ({ ...prev, to_card: value === '__none__' ? '' : value }))}>
                      <SelectTrigger className="!h-14 bg-white rounded-2xl px-4 shadow-sm w-full">
                        <div className="flex items-center gap-3 min-w-0" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 400, color: '#1F2937' }}>
                          <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-base flex-shrink-0">💳</div>
                          <SelectValue placeholder="Select payment method" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {paymentMethods.map((card) => {
                          const colors = colorMap[card.color] || colorMap.grey;
                          return (
                            <SelectItem key={card.name} value={card.name}>
                              <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded-full ${colors.bg}`} />
                                {card.name}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-2.5 mt-1">
                  <button
                    onClick={cancelEditTransaction}
                    className="flex-1 rounded-full py-3.5 font-semibold text-[15px] border border-gray-200 bg-white text-gray-600"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveEditTransaction}
                    className="flex-1 rounded-full py-3.5 text-white font-semibold text-[15px] shadow-md"
                    style={{ ...txCtaStyle, fontFamily: "'DM Sans', sans-serif" }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default BudgetTab;
